import { z } from 'zod'
import type { AreEquivalent, ItemType } from '../../types'
import type { components } from './openapi-spec'

// #region Base schemas (mapped to OpenAPI, required by utility schemas)

export const HALLinkSchema = z
  .object({
    href: z.string(),
    title: z.string().optional(),
  })
  .describe('HALLink')
export type HALLink = z.infer<typeof HALLinkSchema>
true satisfies AreEquivalent<HALLink, components['schemas']['HALLink']>

export const BranchLinksSchema = z.object({
  'latest-scan': HALLinkSchema,
  self: HALLinkSchema,
})
export type BranchLinks = z.infer<typeof BranchLinksSchema>
true satisfies AreEquivalent<BranchLinks, components['schemas']['BranchLinks']>

export const HALTemplateSchema = z
  .object({
    method: z.string(),
    target: z.string().optional(),
  })
  .describe('HALTemplate')
export type HALTemplate = z.infer<typeof HALTemplateSchema>
true satisfies AreEquivalent<HALTemplate, components['schemas']['HALTemplate']>

export const ProjectTemplatesSchema = z
  .object({
    delete: HALTemplateSchema.optional(),
    replace: HALTemplateSchema.optional(),
  })
  .describe('ProjectTemplates')
export type ProjectTemplates = z.infer<typeof ProjectTemplatesSchema>
true satisfies AreEquivalent<ProjectTemplates, components['schemas']['ProjectTemplates']>

export const ProjectMembershipTemplatesSchema = z
  .object({
    delete: HALTemplateSchema.optional(),
    replace: HALTemplateSchema.optional(),
  })
  .describe('ProjectMembershipTemplates')
export type ProjectMembershipTemplates = z.infer<typeof ProjectMembershipTemplatesSchema>
true satisfies AreEquivalent<ProjectMembershipTemplates, components['schemas']['ProjectMembershipTemplates']>

export const ProjectCollectionTemplatesSchema = z
  .object({
    create: HALTemplateSchema.optional(),
  })
  .describe('ProjectCollectionTemplates')
export type ProjectCollectionTemplates = z.infer<typeof ProjectCollectionTemplatesSchema>
true satisfies AreEquivalent<ProjectCollectionTemplates, components['schemas']['ProjectCollectionTemplates']>

export const ProjectMembershipCollectionTemplatesSchema = z
  .object({
    create: HALTemplateSchema.optional(),
  })
  .describe('ProjectMembershipCollectionTemplates')
export type ProjectMembershipCollectionTemplates = z.infer<typeof ProjectMembershipCollectionTemplatesSchema>
true satisfies AreEquivalent<
  ProjectMembershipCollectionTemplates,
  components['schemas']['ProjectMembershipCollectionTemplates']
>

export const FeedbackSubmissionLinksSchema = z.object({
  project: HALLinkSchema,
})
export type FeedbackSubmissionLinks = z.infer<typeof FeedbackSubmissionLinksSchema>
true satisfies AreEquivalent<FeedbackSubmissionLinks, components['schemas']['FeedbackSubmissionLinks']>

export const ApiLinksSchema = z.object({
  analyses: HALLinkSchema.optional(),
  'feedback-submissions': HALLinkSchema.optional(),
  integrations: HALLinkSchema.optional(),
  labs: HALLinkSchema.optional(),
  organizations: HALLinkSchema.optional(),
  patches: HALLinkSchema.optional(),
  projects: HALLinkSchema.optional(),
  repositories: HALLinkSchema.optional(),
  scales: HALLinkSchema.optional(),
  scans: HALLinkSchema.optional(),
  self: HALLinkSchema,
  'support-bundles': HALLinkSchema.optional(),
  'system-statuses': HALLinkSchema.optional(),
  users: HALLinkSchema.optional(),
  workflows: HALLinkSchema.optional(),
})
export type ApiLinks = z.infer<typeof ApiLinksSchema>
true satisfies AreEquivalent<ApiLinks, components['schemas']['ApiLinks']>

export const ApiResponseSchema = z.object({
  _links: ApiLinksSchema,
})
export type ApiResponse = z.infer<typeof ApiResponseSchema>
true satisfies AreEquivalent<ApiResponse, components['schemas']['ApiResponse']>

export const PatchesPatchFixLinksSchema = z.object({
  'fix-result': HALLinkSchema,
  self: HALLinkSchema,
})
export type PatchesPatchFixLinks = z.infer<typeof PatchesPatchFixLinksSchema>
true satisfies AreEquivalent<PatchesPatchFixLinks, components['schemas']['PatchesPatchFixLinks']>

export const SelfLinkSchema = z
  .object({
    self: HALLinkSchema,
  })
  .describe('SelfLink')
export type SelfLink = z.infer<typeof SelfLinkSchema>
true satisfies AreEquivalent<SelfLink, components['schemas']['SelfLink']>

export const StandardPageNavigationLinksSchema = z
  .object({
    first: HALLinkSchema,
    next: HALLinkSchema.optional(),
    prev: HALLinkSchema.optional(),
    self: HALLinkSchema,
  })
  .describe('StandardPageNavigationLinks')
export type StandardPageNavigationLinks = z.infer<typeof StandardPageNavigationLinksSchema>
true satisfies AreEquivalent<StandardPageNavigationLinks, components['schemas']['StandardPageNavigationLinks']>

export const StandardCursorPageNavigationLinksSchema = z
  .object({
    first: HALLinkSchema,
    next: HALLinkSchema.optional(),
    self: HALLinkSchema,
  })
  .describe('StandardCursorPageNavigationLinks')
export type StandardCursorPageNavigationLinks = z.infer<typeof StandardCursorPageNavigationLinksSchema>
true satisfies AreEquivalent<
  StandardCursorPageNavigationLinks,
  components['schemas']['StandardCursorPageNavigationLinks']
>

const PageResponseSchema = z.object({
  number: z.number().int(),
  size: z.number().int(),
})
type PageResponse = z.infer<typeof PageResponseSchema>
true satisfies AreEquivalent<PageResponse, components['schemas']['PageResponse']>

// Lives in this region rather than beside `ThreatModelPhaseSchema` because the utility region below
// derives from it, and a `const` cannot be referenced before it is evaluated.
// `RefreshRequestResponse.threat_model_document_status` is where analysis-service may report `unknown`,
// when it can't determine the live status.
export const ThreatModelStatusSchema = z
  .enum(['queued', 'processing', 'completed', 'failed', 'abandoned', 'unknown'])
  .describe('ThreatModelStatus')
export type ThreatModelStatus = z.infer<typeof ThreatModelStatusSchema>
true satisfies AreEquivalent<ThreatModelStatus, components['schemas']['ThreatModelStatus']>

// #endregion

// #region Utility schemas (no OpenAPI counterpart)

/**
 * Schema with a permissive input type and a strict output type. It simply filters out items that don't match the strict schema.
 *
 * Accepts any `ZodType<unknown>` so callers can pass either a top-level `z.discriminatedUnion(...)` (the typical case) or a
 * plain `z.object(...)` whose discriminator is nested (e.g. `WorkflowResponseSchema` discriminates on `event.type`). The
 * outer schema is `z.unknown()` because per-item `safeParse` against `itemSchema` is the real narrowing step.
 *
 * Not what `IntegrationPaginatedResponseSchema` uses, and the difference is deliberate: this drops an
 * item that fails for *any* reason, so a malformed payload is indistinguishable from an unrecognized
 * one. `ModelledIntegrationSchema` tolerates only an unmodelled `type` and still hard-fails a `sonar`
 * item missing a required field. Reach for this one where dropping anything unparseable is the
 * intent; reach for that one where only a new tool should be tolerated. It also yields
 * `T[] | undefined`, which `Paginated`'s required `items` cannot take.
 */
function PermissiveDiscriminatedUnionArray<TSchema extends z.ZodType>(itemSchema: TSchema) {
  return (
    z
      .array(z.unknown())
      // As a future improvement, we could update this to support both optional and non-optional arrays.
      .optional()
      .nullable()
      .transform(items =>
        items
          ?.map(item => itemSchema.safeParse(item))
          .filter((result): result is z.ZodSafeParseSuccess<z.infer<TSchema>> => result.success)
          .map(result => result.data)
      )
      .optional()
  )
}

const Paginated = <T extends z.ZodType, TLinks extends z.ZodType>(itemSchema: T, linksSchema: TLinks) =>
  z.object({
    _embedded: z.object({
      items: z.array(itemSchema),
    }),
    _links: linksSchema,
    page: PageResponseSchema,
    total: z.number().int(),
  })

const PaginatedResponse = Paginated(z.unknown(), StandardPageNavigationLinksSchema)
export type PaginatedResponse = z.infer<typeof PaginatedResponse>

/**
 * One cursor-paged page, as every integration picker answers. There is no page number or `total`:
 * a later page is read by requesting `_links.next.href` as given.
 */
const CursorPaginated = <T extends z.ZodType>(itemSchema: T) =>
  z.object({
    _embedded: z.object({
      items: z.array(itemSchema),
    }),
    _links: StandardCursorPageNavigationLinksSchema,
  })

/**
 * The fabricated repository-risk payload — a deliberate throwaway, and the whole point of the
 * ISS-8362 prototype: this schema *is* the written ask for what the analysis service would have to return.
 *
 * It lives in this region rather than beside its route because nothing here asserts `AreEquivalent`: there
 * is no OpenAPI counterpart to assert against. Deleting it is one stroke — this region and the hook in
 * `api-hooks.ts` go together when ENH-570 lands.
 *
 * The dimension ids live here, not with their display names, because a schema in `utils/` cannot
 * import from `routes/` and the ids are payload rather than presentation.
 * `risk-dimensions.ts` keeps the names and glosses and types them against `RiskDimensionId`.
 */
export const RISK_DIMENSION_IDS = [
  'attack-surface',
  'defense-in-depth',
  'blast-radius',
  'authentication',
  'authorization',
  'input-handling',
  'secrets',
  'dependencies',
  'transport',
  'observability',
] as const

export const RiskDimensionIdSchema = z.enum(RISK_DIMENSION_IDS)
export type RiskDimensionId = z.infer<typeof RiskDimensionIdSchema>

const RepositoryRiskFactorSchema = z.object({
  detail: z.string(),
  dimension: RiskDimensionIdSchema,
})

/** One contributing factor behind a repository's score, as the drawer lists them. */
export type RepositoryRiskFactor = z.infer<typeof RepositoryRiskFactorSchema>

export const RepositoryRiskSchema = z.object({
  commitSha: z.string().optional(),
  defaultBranch: z.string(),
  details: z.string(),
  factors: z.array(RepositoryRiskFactorSchema),
  fullName: z.string(),
  generatedAt: z.string().optional(),
  rationale: z.string(),
  repositoryId: z.string(),
  riskScore: z.number(),
  scanId: z.string().optional(),
  threatModelStatus: ThreatModelStatusSchema.exclude(['unknown']),
  truePositiveCount: z.number(),
})

export const RepositoryRiskResponseSchema = z.object({
  items: z.array(RepositoryRiskSchema),
})

export type RepositoryRisk = z.infer<typeof RepositoryRiskSchema>

// #endregion

// #region API schemas

export const FindingSortFieldSchema = z
  .enum(['effective-cvss', 'pixee-intelligence', 'pixee-severity', 'severity'])
  .describe('FindingSortField')
export type FindingSortField = z.infer<typeof FindingSortFieldSchema>
true satisfies AreEquivalent<FindingSortField, components['schemas']['FindingSortField']>

export const SortOrderSchema = z.enum(['asc', 'desc']).describe('SortOrder')
export type SortOrder = z.infer<typeof SortOrderSchema>
true satisfies AreEquivalent<SortOrder, components['schemas']['SortOrder']>

export const UserSchema = z
  .object({
    avatar_url: z.string(),
    email: z.string(),
    login: z.string(),
    name: z.string().nullable(),
  })
  .describe('User')
export type User = z.infer<typeof UserSchema>

export const CurrentUserResponseSchema = z
  .object({
    account_management_url: z.string().optional(),
    avatar_url: z.string().optional(),
    email: z.string().optional(),
    login: z.string().optional(),
    roles: z.array(z.string()).optional(),
    username: z.string().optional(),
  })
  .describe('CurrentUserResponse')
export type CurrentUserResponse = z.infer<typeof CurrentUserResponseSchema>
true satisfies AreEquivalent<CurrentUserResponse, components['schemas']['CurrentUserResponse']>

export const SystemStatusStatusSchema = z.enum(['up', 'down']).describe('SystemStatusStatus')
export type SystemStatusStatus = z.infer<typeof SystemStatusStatusSchema>
true satisfies AreEquivalent<SystemStatusStatus, components['schemas']['SystemStatusValue']>

export const AIModelStatusResponseSchema = z
  .object({
    _links: SelfLinkSchema,
    description: z.string().optional(),
    id: z.string(),
    model: z.string(),
    name: z.string(),
    status: SystemStatusStatusSchema,
    type: z.literal('ai-model'),
  })
  .describe('AIModelStatusResponse')
export type AIModelStatusResponse = z.infer<typeof AIModelStatusResponseSchema>
true satisfies AreEquivalent<AIModelStatusResponse, components['schemas']['AIModelStatusResponse']>

export const LicenseStatusResponseSchema = z
  .object({
    _links: SelfLinkSchema,
    description: z.string().optional(),
    // The backend emits expires_at as an empty string on license-failure / no-expiry paths, so it is
    // not always an ISO-8601 timestamp. Keep it as z.string(); do NOT tighten to .datetime().
    expires_at: z.string().optional(),
    features_enabled: z.boolean().optional(),
    id: z.string(),
    name: z.string(),
    status: SystemStatusStatusSchema,
    type: z.literal('license'),
  })
  .describe('LicenseStatusResponse')
export type LicenseStatusResponse = z.infer<typeof LicenseStatusResponseSchema>
true satisfies AreEquivalent<LicenseStatusResponse, components['schemas']['LicenseStatusResponse']>

export const VersionStatusResponseSchema = z
  .object({
    _links: SelfLinkSchema,
    description: z.string().optional(),
    id: z.string(),
    name: z.string(),
    status: SystemStatusStatusSchema,
    type: z.literal('version'),
    version: z.string(),
  })
  .describe('VersionStatusResponse')
export type VersionStatusResponse = z.infer<typeof VersionStatusResponseSchema>
true satisfies AreEquivalent<VersionStatusResponse, components['schemas']['VersionStatusResponse']>

export const SystemStatusSchema = z
  .discriminatedUnion('type', [AIModelStatusResponseSchema, LicenseStatusResponseSchema, VersionStatusResponseSchema])
  .describe('SystemStatus')
export type SystemStatus = z.infer<typeof SystemStatusSchema>
true satisfies AreEquivalent<SystemStatus, components['schemas']['SystemStatusResponse']>

export const SystemStatusesResponseSchema = z
  .object({
    _embedded: z.object({
      items: PermissiveDiscriminatedUnionArray(SystemStatusSchema),
    }),
    _links: StandardPageNavigationLinksSchema,
    page: PageResponseSchema,
    total: z.number().int(),
  })
  .describe('SystemStatusesResponse')
export type SystemStatusesResponse = z.infer<typeof SystemStatusesResponseSchema>
// @ts-expect-error PermissiveDiscriminatedUnionArray types the embedded items array as optional, but the spec's PageEmbeddedSystemStatusResponse requires it.
true satisfies AreEquivalent<SystemStatusesResponse, components['schemas']['SystemStatusPaginatedResponse']>

export const RepositoryTypeSchema = z.enum(['git', 'azure', 'github', 'gitlab', 'bitbucket']).describe('RepositoryType')
export type RepositoryType = z.infer<typeof RepositoryTypeSchema>
true satisfies AreEquivalent<RepositoryType, components['schemas']['RepositoryResponse']['type']>
export const RepositoryLinksSchema = z
  .object({
    branches: HALLinkSchema.optional(),
    integration: HALLinkSchema.optional(),
    'latest-analysis': HALLinkSchema.optional(),
    'latest-scan': HALLinkSchema.optional(),
    'project-membership': HALLinkSchema,
    'repository-integrations': HALLinkSchema.optional(),
    scans: HALLinkSchema.optional(),
    self: HALLinkSchema,
    workflows: HALLinkSchema.optional(),
  })
  .describe('RepositoryLinks')
export type RepositoryLinks = z.infer<typeof RepositoryLinksSchema>
true satisfies AreEquivalent<RepositoryLinks, components['schemas']['RepositoryLinks']>

export const ProjectLinksSchema = z
  .object({
    memberships: HALLinkSchema,
    repositories: HALLinkSchema,
    self: HALLinkSchema,
  })
  .describe('ProjectLinks')
export type ProjectLinks = z.infer<typeof ProjectLinksSchema>
true satisfies AreEquivalent<ProjectLinks, components['schemas']['ProjectLinks']>

export const DuplicateRepositoryProblemLinksSchema = z
  .object({
    repository: HALLinkSchema,
  })
  .describe('DuplicateRepositoryProblemLinks')
export type DuplicateRepositoryProblemLinks = z.infer<typeof DuplicateRepositoryProblemLinksSchema>
true satisfies AreEquivalent<DuplicateRepositoryProblemLinks, components['schemas']['DuplicateRepositoryProblemLinks']>

export const DuplicateRepositoryProblemSchema = z
  .object({
    _links: DuplicateRepositoryProblemLinksSchema,
    connection_name: z.string().optional(),
    detail: z.string(),
    repository_id: z.string(),
    status: z.number().int(),
    title: z.string(),
  })
  .describe('DuplicateRepositoryProblem')
export type DuplicateRepositoryProblem = z.infer<typeof DuplicateRepositoryProblemSchema>
true satisfies AreEquivalent<DuplicateRepositoryProblem, components['schemas']['DuplicateRepositoryProblem']>

const ConstraintViolationSchema = z
  .object({
    field: z.string().optional(),
    message: z.string(),
  })
  .describe('ConstraintViolation')
type ConstraintViolation = z.infer<typeof ConstraintViolationSchema>
true satisfies AreEquivalent<ConstraintViolation, components['schemas']['ConstraintViolation']>

export const ConstraintViolationProblemSchema = z
  .object({
    detail: z.string(),
    status: z.number().int(),
    title: z.string(),
    violations: z.array(ConstraintViolationSchema),
  })
  .describe('ConstraintViolationProblem')
export type ConstraintViolationProblem = z.infer<typeof ConstraintViolationProblemSchema>
true satisfies AreEquivalent<ConstraintViolationProblem, components['schemas']['ConstraintViolationProblem']>

export const IntegrationReferenceSchema = z
  .object({
    id: z.string(),
    name: z.string(),
  })
  .describe('IntegrationReference')
export type IntegrationReference = z.infer<typeof IntegrationReferenceSchema>
true satisfies AreEquivalent<IntegrationReference, components['schemas']['IntegrationReference']>

const BaseRepositorySchema = z.object({
  // A getter because RepositoryEmbeddedSchema is declared further down and refers back to this schema through
  // ProjectMembership → ProjectMembershipEmbedded → Project → ProjectEmbedded → RepositoryPaginatedResponse.
  // Zod calls it once, on the first read of the object's shape (the first parse, or `.shape`), so reading the
  // shape of this schema or its extensions before RepositoryEmbeddedSchema is declared throws a ReferenceError.
  get _embedded() {
    return RepositoryEmbeddedSchema.optional()
  },
  _links: RepositoryLinksSchema,
  default_branch: z.string(),
  full_name: z.string(),
  id: z.string(),
  name: z.string(),
})
const AzureRepositorySchema = BaseRepositorySchema.extend({
  integration: IntegrationReferenceSchema.optional(),
  organization: z.string(),
  project: z.string(),
  type: z.literal('azure'),
})
type AzureRepository = z.infer<typeof AzureRepositorySchema>
true satisfies AreEquivalent<AzureRepository, components['schemas']['RepositoriesAzureRepositoryResponse']>
const GitRepositorySchema = BaseRepositorySchema.extend({
  type: z.literal('git'),
})
type GitRepository = z.infer<typeof GitRepositorySchema>
true satisfies AreEquivalent<GitRepository, components['schemas']['GitRepositoryResponse']>
const GitHubRepositorySchema = BaseRepositorySchema.extend({
  html_url: z.string(),
  installed: z.boolean(),
  integration: IntegrationReferenceSchema.optional(),
  owner: z.string(),
  type: z.literal('github'),
})
type GitHubRepository = z.infer<typeof GitHubRepositorySchema>
true satisfies AreEquivalent<GitHubRepository, components['schemas']['GitHubAppRepositoryResponse']>
const GitLabRepositorySchema = BaseRepositorySchema.extend({
  integration: IntegrationReferenceSchema.optional(),
  namespace: z.string(),
  type: z.literal('gitlab'),
})
type GitLabRepository = z.infer<typeof GitLabRepositorySchema>
true satisfies AreEquivalent<GitLabRepository, components['schemas']['GitLabRepositoryResponse']>
const BitbucketRepositorySchema = BaseRepositorySchema.extend({
  integration: IntegrationReferenceSchema.optional(),
  slug: z.string(),
  type: z.literal('bitbucket'),
  workspace: z.string(),
})
type BitbucketRepository = z.infer<typeof BitbucketRepositorySchema>
true satisfies AreEquivalent<BitbucketRepository, components['schemas']['RepositoriesBitbucketRepositoryResponse']>
export const RepositorySchema = z
  .discriminatedUnion('type', [
    GitHubRepositorySchema,
    GitLabRepositorySchema,
    AzureRepositorySchema,
    BitbucketRepositorySchema,
    GitRepositorySchema,
  ])
  .describe('Repository')
export type Repository = z.infer<typeof RepositorySchema>
true satisfies AreEquivalent<Repository, components['schemas']['RepositoryResponse']>

/**
 * The project a repository belongs to, or `undefined` when it is ungrouped or the caller did not
 * embed the membership. Reads the `embed=…project-membership.project` structure so callers do not
 * have to walk the two `_embedded` levels themselves, and answers the whole project rather than a
 * narrowed shape — the member count is on it, and a caller that wanted it was walking those levels
 * by hand to get at it.
 */
export const getRepositoryProject = (repository: Pick<Repository, '_embedded'>) =>
  repository._embedded?.['project-membership']?._embedded?.project

export const RepositoryUngroupedProblemSchema = z
  .object({
    detail: z.string(),
    status: z.number().int(),
    title: z.string(),
  })
  .describe('RepositoryUngroupedProblem')
export type RepositoryUngroupedProblem = z.infer<typeof RepositoryUngroupedProblemSchema>
true satisfies AreEquivalent<RepositoryUngroupedProblem, components['schemas']['RepositoryUngroupedProblem']>

export const RepositoryPaginatedResponseSchema = Paginated(
  RepositorySchema,
  StandardPageNavigationLinksSchema
).describe('RepositoryPaginatedResponse')
export type RepositoryPaginatedResponse = z.infer<typeof RepositoryPaginatedResponseSchema>
true satisfies AreEquivalent<RepositoryPaginatedResponse, components['schemas']['RepositoryPaginatedResponse']>

export const ProjectEmbeddedSchema = z
  .object({
    repositories: RepositoryPaginatedResponseSchema,
  })
  .describe('ProjectEmbedded')
export type ProjectEmbedded = z.infer<typeof ProjectEmbeddedSchema>
true satisfies AreEquivalent<ProjectEmbedded, components['schemas']['ProjectEmbedded']>

// Tool represents the lowercase enum from the OpenAPI spec (used in workflow.tool, createScan.metadata.tool)
export const ToolSchema = z
  .enum([
    'aikido_sast',
    'aikido_sca',
    'black_duck_hub_sca',
    'codeql',
    'sonar',
    'semgrep',
    'polaris_sast',
    'polaris_sca',
    'appscan',
    'datadog_sast',
    'arnica_sast',
    'dependabot',
    'contrast',
    'gitlab_sast',
    'gitlab_dependency_scanning',
    'snyk',
    'checkmarx',
    'checkmarx_enterprise',
    'veracode',
    'fortify',
    'gitleaks',
    'zap',
    'pixee_ai',
    'freeform',
  ])
  .describe('Tool')
export type Tool = z.infer<typeof ToolSchema>
// This clause ensures that we aren't missing any enum values.
true satisfies AreEquivalent<Tool, components['schemas']['Tool']>

// Instant is inlined (string) in the regenerated spec; kept as a named schema for readability.
export const InstantSchema = z.string().describe('Instant')
export type Instant = z.infer<typeof InstantSchema>

export const ScanAnalysisStateSchema = z
  .enum(['completed', 'in-progress', 'not-analyzed'])
  .describe('ScanAnalysisState')
export type ScanAnalysisState = z.infer<typeof ScanAnalysisStateSchema>
true satisfies AreEquivalent<ScanAnalysisState, components['schemas']['ScanAnalysisState']>

export const ScanLinksSchema = z
  .object({
    analyses: HALLinkSchema.optional(),
    'analysis-cancellations': HALLinkSchema.optional(),
    findings: HALLinkSchema,
    integration: HALLinkSchema.optional(),
    patches: HALLinkSchema,
    repository: HALLinkSchema,
    scale: HALLinkSchema,
    self: HALLinkSchema,
  })
  .describe('ScanLinks')
export type ScanLinks = z.infer<typeof ScanLinksSchema>
true satisfies AreEquivalent<ScanLinks, components['schemas']['ScanLinks']>

export const SbomToolSchema = z
  .enum(['aikido_sca', 'black_duck_hub_sca', 'dependabot', 'gitlab_dependency_scanning', 'polaris_sca'])
  .describe('SbomTool')
export type SbomTool = z.infer<typeof SbomToolSchema>
true satisfies AreEquivalent<SbomTool, components['schemas']['SbomTool']>

export const SbomLinksSchema = z
  .object({
    repository: HALLinkSchema,
    self: HALLinkSchema,
  })
  .describe('SbomLinks')
export type SbomLinks = z.infer<typeof SbomLinksSchema>
true satisfies AreEquivalent<SbomLinks, components['schemas']['SbomLinks']>

export const SbomResponseSchema = z
  .object({
    _links: SbomLinksSchema,
    branch: z.string().optional(),
    created_at: InstantSchema,
    id: z.string(),
    sha: z.string(),
  })
  .describe('SbomResponse')
export type SbomResponse = z.infer<typeof SbomResponseSchema>
true satisfies AreEquivalent<SbomResponse, components['schemas']['SbomResponse']>

export const ThreatModelPhaseSchema = z
  .enum(['queued', 'fetching_code', 'exploring_codebase', 'modeling_threats', 'rendering', 'unknown'])
  .describe('ThreatModelPhase')
export type ThreatModelPhase = z.infer<typeof ThreatModelPhaseSchema>
true satisfies AreEquivalent<ThreatModelPhase, components['schemas']['ThreatModelPhase']>

// `queued`, `processing` and `unknown` are in-flight generation states: analysis-service reports
// `unknown` when it can't determine the live status, so callers keep streaming/polling until it
// resolves. `queued` (a refresh reserved but not yet dispatched) exists only on `ThreatModelStatus`.
// Every other status is terminal. The render, the stream gate, and the poll gate in `api-hooks.ts`
// all derive from this list so they can't disagree.
export const IN_FLIGHT_GENERATION_STATUSES = [
  'queued',
  'processing',
  'unknown',
] as const satisfies readonly ThreatModelStatus[]
type InFlightGenerationStatus = ItemType<typeof IN_FLIGHT_GENERATION_STATUSES>

export const isInFlightGenerationStatus = <S extends string>(status: S): status is S & InFlightGenerationStatus =>
  IN_FLIGHT_GENERATION_STATUSES.some(inFlight => inFlight === status)

export const ThreatModelLinksSchema = z
  .object({
    content: HALLinkSchema.optional(),
    events: HALLinkSchema.optional(),
    self: HALLinkSchema,
  })
  .describe('ThreatModelLinks')
export type ThreatModelLinks = z.infer<typeof ThreatModelLinksSchema>
true satisfies AreEquivalent<ThreatModelLinks, components['schemas']['ThreatModelLinks']>

export const ThreatModelResponseSchema = z
  .object({
    _links: ThreatModelLinksSchema,
    commitSha: z.string().optional(),
    failureReason: z.string().optional(),
    generatedAt: InstantSchema.optional(),
    phase: ThreatModelPhaseSchema,
    requestedBy: z.string().optional(),
    status: ThreatModelStatusSchema,
  })
  .describe('ThreatModelResponse')
export type ThreatModelResponse = z.infer<typeof ThreatModelResponseSchema>
true satisfies AreEquivalent<ThreatModelResponse, components['schemas']['ThreatModelResponse']>

export const ThreatModelUnavailableProblemSchema = z
  .object({
    detail: z.string(),
    status: z.number().int(),
    title: z.string(),
  })
  .describe('ThreatModelUnavailableProblem')
export type ThreatModelUnavailableProblem = z.infer<typeof ThreatModelUnavailableProblemSchema>
true satisfies AreEquivalent<ThreatModelUnavailableProblem, components['schemas']['ThreatModelUnavailableProblem']>

export const AdminConsoleIntegrationProblemSchema = z
  .object({
    detail: z.string(),
    integration_id: z.string(),
    status: z.number().int(),
    title: z.string(),
  })
  .describe('AdminConsoleIntegrationProblem')
export type AdminConsoleIntegrationProblem = z.infer<typeof AdminConsoleIntegrationProblemSchema>
true satisfies AreEquivalent<AdminConsoleIntegrationProblem, components['schemas']['AdminConsoleIntegrationProblem']>

// The threat-model `/content` endpoint returns the analysis-service `model_json` document. The
// platform forwards that body byte-for-byte (the caller's `Accept: application/json` header selects
// JSON over the legacy markdown rendering), so the wire shape is analysis-service's, expressed in
// snake_case, and this schema matches it verbatim. Like `FindingOverlayResponse` above, there is no
// `components['schemas']` counterpart to assert against — the platform forwards the body opaquely
// rather than re-declaring it in its OpenAPI spec — so no `AreEquivalent` check is added. Categorical
// fields (`trust_level`, `kind`, `relevance`, …) are typed as `z.string()` rather than enums: the
// values are generated per model and the UI degrades gracefully on ones it does not recognise.
//
// `id` and the graph-edge reference fields (`reaches`, `exposes`, `affects`, `applies_to`) are
// `.optional()` on purpose. The generator only began emitting them once its linking pass landed
// (analysis-service#2927); documents generated before that omit them entirely. Because Zod strips unknown keys, these must be declared for the fields to
// survive `.parse()` at all, and optional so a pre-linking document still validates. The Attack
// paths view reads them when present and falls back to the control→weakness view when they are
// absent, so no consumer may assume they exist.
export const ThreatModelActorSchema = z
  .object({
    authenticates_via: z.string(),
    can_reach: z.string(),
    id: z.string().optional(),
    name: z.string(),
    /** Entry-point ids this actor can reach — the actor→entry-point graph edge. */
    reaches: z.string().array().optional(),
    trust_level: z.string(),
  })
  .describe('ThreatModelActor')
export type ThreatModelActor = z.infer<typeof ThreatModelActorSchema>

export const ThreatModelExposureSchema = z
  .object({
    deployment_environment: z.string(),
    internet_facing: z.string(),
    network_position: z.string(),
    notes: z.string(),
  })
  .describe('ThreatModelExposure')
export type ThreatModelExposure = z.infer<typeof ThreatModelExposureSchema>

export const ThreatModelEntryPointSchema = z
  .object({
    authentication: z.string(),
    description: z.string(),
    /** Vulnerability-class-impact ids reachable through this surface — the entry-point→weakness edge. */
    exposes: z.string().array().optional(),
    id: z.string().optional(),
    kind: z.string(),
    location: z.string(),
    name: z.string(),
  })
  .describe('ThreatModelEntryPoint')
export type ThreatModelEntryPoint = z.infer<typeof ThreatModelEntryPointSchema>

export const ThreatModelTrustBoundarySchema = z
  .object({
    crosses: z.string(),
    description: z.string(),
    name: z.string(),
  })
  .describe('ThreatModelTrustBoundary')
export type ThreatModelTrustBoundary = z.infer<typeof ThreatModelTrustBoundarySchema>

export const ThreatModelSensitiveAssetSchema = z
  .object({
    description: z.string(),
    id: z.string().optional(),
    kind: z.string(),
    location: z.string(),
    name: z.string(),
  })
  .describe('ThreatModelSensitiveAsset')
export type ThreatModelSensitiveAsset = z.infer<typeof ThreatModelSensitiveAssetSchema>

export const ThreatModelSecurityControlSchema = z
  .object({
    /** Entry-point ids this control guards — the control→entry-point relationship. */
    applies_to: z.string().array().optional(),
    id: z.string().optional(),
    kind: z.string(),
    location: z.string(),
    mitigates: z.string().array(),
    name: z.string(),
    scope: z.string(),
  })
  .describe('ThreatModelSecurityControl')
export type ThreatModelSecurityControl = z.infer<typeof ThreatModelSecurityControlSchema>

export const ThreatModelDependencyNoteSchema = z
  .object({
    location: z.string(),
    name: z.string(),
    reason: z.string(),
  })
  .describe('ThreatModelDependencyNote')
export type ThreatModelDependencyNote = z.infer<typeof ThreatModelDependencyNoteSchema>

export const ThreatModelVulnerabilityImpactSchema = z
  .object({
    /** Sensitive-asset ids this weakness puts at risk — the weakness→asset graph edge. */
    affects: z.string().array().optional(),
    blast_radius: z.string(),
    id: z.string().optional(),
    mitigating_controls: z.string(),
    reachability: z.string(),
    relevance: z.string(),
    verify: z.string(),
    vuln_class: z.string(),
  })
  .describe('ThreatModelVulnerabilityImpact')
export type ThreatModelVulnerabilityImpact = z.infer<typeof ThreatModelVulnerabilityImpactSchema>

export const ThreatModelContentSchema = z
  .object({
    actors: ThreatModelActorSchema.array(),
    assumptions: z.string().array(),
    attack_surface_summary: z.string(),
    authentication_model: z.string(),
    authorization_model: z.string(),
    data_flow_notes: z.string(),
    dependency_notes: ThreatModelDependencyNoteSchema.array(),
    entry_points: ThreatModelEntryPointSchema.array(),
    exposure: ThreatModelExposureSchema,
    security_controls: ThreatModelSecurityControlSchema.array(),
    sensitive_assets: ThreatModelSensitiveAssetSchema.array(),
    system_overview: z.string(),
    trust_boundaries: ThreatModelTrustBoundarySchema.array(),
    vulnerability_class_impacts: ThreatModelVulnerabilityImpactSchema.array(),
  })
  .describe('ThreatModelContent')
export type ThreatModelContent = z.infer<typeof ThreatModelContentSchema>

// `threat_model_document_status` reuses the standalone `ThreatModelStatus` component (asserted
// above); `state` and `trigger_source` are standalone spec components with their own
// `AreEquivalent` assertions below.
export const RefreshRequestStateResponseSchema = z
  .enum(['pending', 'claimed', 'regenerating', 'completed', 'failed', 'no-revision', 'superseded'])
  .describe('RefreshRequestStateResponse')
export type RefreshRequestStateResponse = z.infer<typeof RefreshRequestStateResponseSchema>
true satisfies AreEquivalent<RefreshRequestStateResponse, components['schemas']['RefreshRequestStateResponse']>

export const RefreshTriggerSourceResponseSchema = z
  .enum(['onboarding', 'direct-push', 'merged-pr'])
  .describe('RefreshTriggerSourceResponse')
export type RefreshTriggerSourceResponse = z.infer<typeof RefreshTriggerSourceResponseSchema>
true satisfies AreEquivalent<RefreshTriggerSourceResponse, components['schemas']['RefreshTriggerSourceResponse']>

export const RefreshRequestLinksSchema = z
  .object({
    commit: HALLinkSchema.optional(),
    'pull-request': HALLinkSchema.optional(),
    repository: HALLinkSchema,
    self: HALLinkSchema,
    'superseded-by': HALLinkSchema.optional(),
    'threat-model': HALLinkSchema,
  })
  .describe('RefreshRequestLinks')
export type RefreshRequestLinks = z.infer<typeof RefreshRequestLinksSchema>
true satisfies AreEquivalent<RefreshRequestLinks, components['schemas']['RefreshRequestLinks']>

export const RefreshRequestResponseSchema = z
  .object({
    _links: RefreshRequestLinksSchema,
    after_sha: z.string().optional(),
    before_sha: z.string().optional(),
    decision_rationale: z.string().optional(),
    id: z.string(),
    pull_request_number: z.number().int().optional(),
    ref: z.string().optional(),
    state: RefreshRequestStateResponseSchema,
    threat_model_document_id: z.string().optional(),
    threat_model_document_status: ThreatModelStatusSchema.optional(),
    trigger_source: RefreshTriggerSourceResponseSchema,
    triggered_at: InstantSchema,
  })
  .describe('RefreshRequestResponse')
export type RefreshRequestResponse = z.infer<typeof RefreshRequestResponseSchema>
true satisfies AreEquivalent<RefreshRequestResponse, components['schemas']['RefreshRequestResponse']>

export const RefreshRequestsLinksSchema = z
  .object({
    repository: HALLinkSchema,
    self: HALLinkSchema,
    'threat-model': HALLinkSchema,
  })
  .describe('RefreshRequestsLinks')
export type RefreshRequestsLinks = z.infer<typeof RefreshRequestsLinksSchema>
true satisfies AreEquivalent<RefreshRequestsLinks, components['schemas']['RefreshRequestsLinks']>

export const RefreshRequestsResponseSchema = z
  .object({
    _links: RefreshRequestsLinksSchema,
    items: z.array(RefreshRequestResponseSchema),
    next_cursor: z.string().optional(),
  })
  .describe('RefreshRequestsResponse')
export type RefreshRequestsResponse = z.infer<typeof RefreshRequestsResponseSchema>
true satisfies AreEquivalent<RefreshRequestsResponse, components['schemas']['RefreshRequestsResponse']>

export const ExploitChainsStatusSchema = z
  .enum(['processing', 'completed', 'failed', 'unknown'])
  .describe('ExploitChainsStatus')
export type ExploitChainsStatus = z.infer<typeof ExploitChainsStatusSchema>
true satisfies AreEquivalent<ExploitChainsStatus, components['schemas']['ExploitChainsStatus']>

export const ExploitChainsPhaseSchema = z
  .enum(['queued', 'fetching_code', 'loading_inputs', 'correlating', 'rendering', 'unknown'])
  .describe('ExploitChainsPhase')
export type ExploitChainsPhase = z.infer<typeof ExploitChainsPhaseSchema>
true satisfies AreEquivalent<ExploitChainsPhase, components['schemas']['ExploitChainsPhase']>

export const ExploitChainsLinksSchema = z
  .object({
    content: HALLinkSchema.optional(),
    events: HALLinkSchema.optional(),
    self: HALLinkSchema,
  })
  .describe('ExploitChainsLinks')
export type ExploitChainsLinks = z.infer<typeof ExploitChainsLinksSchema>
true satisfies AreEquivalent<ExploitChainsLinks, components['schemas']['ExploitChainsLinks']>

export const FindingBackreferenceLinksSchema = z
  .object({
    finding: HALLinkSchema,
    self: HALLinkSchema,
  })
  .describe('FindingBackreferenceLinks')
export type FindingBackreferenceLinks = z.infer<typeof FindingBackreferenceLinksSchema>
true satisfies AreEquivalent<FindingBackreferenceLinks, components['schemas']['FindingBackreferenceLinks']>

export const LinkedFindingSchema = z
  .object({
    _links: FindingBackreferenceLinksSchema.optional(),
    findingId: z.string(),
    fixAvailable: z.boolean().optional(),
    kind: z.string().optional(),
    path: z.string().optional(),
    rule: z.string().optional(),
    severity: z.string().optional(),
    title: z.string().optional(),
    tool: z.string().optional(),
  })
  .describe('LinkedFinding')
export type LinkedFinding = z.infer<typeof LinkedFindingSchema>
true satisfies AreEquivalent<LinkedFinding, components['schemas']['LinkedFinding']>

export const ChainSummarySchema = z
  .object({
    chainId: z.string(),
    detailMarkdown: z.string().optional(),
    linkedFindings: LinkedFindingSchema.array().optional(),
    severity: z.string().optional(),
    stepCount: z.number().int().optional(),
    title: z.string(),
  })
  .describe('ChainSummary')
export type ChainSummary = z.infer<typeof ChainSummarySchema>
true satisfies AreEquivalent<ChainSummary, components['schemas']['ChainSummary']>

export const CorrelationToolCountSchema = z
  .object({
    count: z.number().int(),
    findingIds: z.string().array(),
    kind: z.string(),
    tool: z.string(),
  })
  .describe('CorrelationToolCount')
export type CorrelationToolCount = z.infer<typeof CorrelationToolCountSchema>
true satisfies AreEquivalent<CorrelationToolCount, components['schemas']['CorrelationToolCount']>

export const CorrelationFindingsSchema = z
  .object({
    byTool: CorrelationToolCountSchema.array(),
    dispatched: z.number().int(),
    droppedForBudget: z.number().int(),
    indexed: z.number().int(),
  })
  .describe('CorrelationFindings')
export type CorrelationFindings = z.infer<typeof CorrelationFindingsSchema>
true satisfies AreEquivalent<CorrelationFindings, components['schemas']['CorrelationFindings']>

export const CorrelationLensSchema = z
  .object({
    failed: z.boolean(),
    lens: z.string(),
    proposed: z.number().int(),
  })
  .describe('CorrelationLens')
export type CorrelationLens = z.infer<typeof CorrelationLensSchema>
true satisfies AreEquivalent<CorrelationLens, components['schemas']['CorrelationLens']>

export const CorrelationCandidatesSchema = z
  .object({
    confirmed: z.number().int(),
    errored: z.number().int(),
    merged: z.number().int(),
    raw: z.number().int(),
    rejected: z.number().int(),
    unique: z.number().int(),
  })
  .describe('CorrelationCandidates')
export type CorrelationCandidates = z.infer<typeof CorrelationCandidatesSchema>
true satisfies AreEquivalent<CorrelationCandidates, components['schemas']['CorrelationCandidates']>

export const CorrelationVerdictFindingSchema = z
  .object({
    findingId: z.string(),
    path: z.string(),
    rule: z.string(),
    title: z.string().optional(),
    tool: z.string().optional(),
  })
  .describe('CorrelationVerdictFinding')
export type CorrelationVerdictFinding = z.infer<typeof CorrelationVerdictFindingSchema>
true satisfies AreEquivalent<CorrelationVerdictFinding, components['schemas']['CorrelationVerdictFinding']>

// `verdict` is a plain string on the wire (`confirmed` / `rejected` / `errored` today) so a value
// added upstream never fails the whole response; `exploit-chains-run.ts` narrows it.
export const CorrelationVerdictSchema = z
  .object({
    findingIds: z.string().array(),
    findings: CorrelationVerdictFindingSchema.array(),
    hypothesis: z.string(),
    id: z.string(),
    lenses: z.string().array(),
    reason: z.string().optional(),
    severity: z.string().optional(),
    title: z.string().optional(),
    verdict: z.string(),
  })
  .describe('CorrelationVerdict')
export type CorrelationVerdict = z.infer<typeof CorrelationVerdictSchema>
true satisfies AreEquivalent<CorrelationVerdict, components['schemas']['CorrelationVerdict']>

export const CorrelationGroundingSchema = z
  .object({
    chainsIn: z.number().int(),
    chainsOut: z.number().int(),
    referencesDropped: z.number().int(),
    referencesRepaired: z.number().int(),
  })
  .describe('CorrelationGrounding')
export type CorrelationGrounding = z.infer<typeof CorrelationGroundingSchema>
true satisfies AreEquivalent<CorrelationGrounding, components['schemas']['CorrelationGrounding']>

export const CorrelationPhaseDurationSchema = z
  .object({
    phase: z.string(),
    seconds: z.number(),
  })
  .describe('CorrelationPhaseDuration')
export type CorrelationPhaseDuration = z.infer<typeof CorrelationPhaseDurationSchema>
true satisfies AreEquivalent<CorrelationPhaseDuration, components['schemas']['CorrelationPhaseDuration']>

export const CorrelationSummarySchema = z
  .object({
    candidates: CorrelationCandidatesSchema,
    findings: CorrelationFindingsSchema,
    grounding: CorrelationGroundingSchema.optional(),
    lenses: CorrelationLensSchema.array(),
    phaseDurations: CorrelationPhaseDurationSchema.array(),
    strategy: z.string(),
    threatModelGeneratedAt: InstantSchema.optional(),
    verdicts: CorrelationVerdictSchema.array(),
  })
  .describe('CorrelationSummary')
export type CorrelationSummary = z.infer<typeof CorrelationSummarySchema>
true satisfies AreEquivalent<CorrelationSummary, components['schemas']['CorrelationSummary']>

export const ExploitChainsResponseSchema = z
  .object({
    _links: ExploitChainsLinksSchema,
    chains: ChainSummarySchema.array().optional(),
    commitSha: z.string().optional(),
    correlation: CorrelationSummarySchema.optional(),
    failureReason: z.string().optional(),
    generatedAt: InstantSchema.optional(),
    phase: ExploitChainsPhaseSchema,
    requestedBy: z.string().optional(),
    status: ExploitChainsStatusSchema,
  })
  .describe('ExploitChainsResponse')
export type ExploitChainsResponse = z.infer<typeof ExploitChainsResponseSchema>
true satisfies AreEquivalent<ExploitChainsResponse, components['schemas']['ExploitChainsResponse']>

export const ExploitChainsUnavailableProblemSchema = z
  .object({
    detail: z.string(),
    status: z.number().int(),
    title: z.string(),
  })
  .describe('ExploitChainsUnavailableProblem')
export type ExploitChainsUnavailableProblem = z.infer<typeof ExploitChainsUnavailableProblemSchema>
true satisfies AreEquivalent<ExploitChainsUnavailableProblem, components['schemas']['ExploitChainsUnavailableProblem']>

// #region Finding Overlays

// Every categorical field carries `unknown`, exactly as the spec declares it: analysis-service
// reports it when it can't determine the value, so a schema without it rejects a legal payload and
// takes the whole tab down with it. Same reasoning as `ThreatModelStatus` above, and
// `isInFlightGenerationStatus` treats `unknown` as still-running for the poll and stream gates.
export const FindingOverlayStatusSchema = z
  .enum(['processing', 'completed', 'failed', 'unknown'])
  .describe('FindingOverlayStatus')
export type FindingOverlayStatus = z.infer<typeof FindingOverlayStatusSchema>
true satisfies AreEquivalent<FindingOverlayStatus, components['schemas']['FindingOverlayStatus']>

export const FindingOverlayPhaseSchema = z.enum(['queued', 'analyzing', 'unknown']).describe('FindingOverlayPhase')
export type FindingOverlayPhase = z.infer<typeof FindingOverlayPhaseSchema>
true satisfies AreEquivalent<FindingOverlayPhase, components['schemas']['FindingOverlayPhase']>

export const FindingOverlayTierSchema = z
  .enum(['critical', 'high', 'medium', 'low', 'unknown'])
  .describe('FindingOverlayTier')
export type FindingOverlayTier = z.infer<typeof FindingOverlayTierSchema>
true satisfies AreEquivalent<FindingOverlayTier, components['schemas']['FindingOverlayTier']>

export const FindingOverlayDimensionSchema = z
  .enum(['entry_point', 'actor', 'trust_boundary', 'sensitive_asset', 'vulnerability_class', 'unknown'])
  .describe('FindingOverlayDimension')
export type FindingOverlayDimension = z.infer<typeof FindingOverlayDimensionSchema>
true satisfies AreEquivalent<FindingOverlayDimension, components['schemas']['FindingOverlayDimension']>

export const FindingOverlaysLinksSchema = z
  .object({
    content: HALLinkSchema.optional(),
    events: HALLinkSchema.optional(),
    self: HALLinkSchema,
  })
  .describe('FindingOverlaysLinks')
export type FindingOverlaysLinks = z.infer<typeof FindingOverlaysLinksSchema>
true satisfies AreEquivalent<FindingOverlaysLinks, components['schemas']['FindingOverlaysLinks']>

export const FindingOverlaySummarySchema = z
  .object({
    clusterCount: z.number().int(),
    highestTier: FindingOverlayTierSchema.optional(),
    priorityCount: z.number().int(),
  })
  .describe('FindingOverlaySummary')
export type FindingOverlaySummary = z.infer<typeof FindingOverlaySummarySchema>
true satisfies AreEquivalent<FindingOverlaySummary, components['schemas']['FindingOverlaySummary']>

export const FindingOverlaysResponseSchema = z
  .object({
    _links: FindingOverlaysLinksSchema,
    failureReason: z.string().optional(),
    generatedAt: InstantSchema.optional(),
    phase: FindingOverlayPhaseSchema,
    requestedBy: z.string().optional(),
    status: FindingOverlayStatusSchema,
    summary: FindingOverlaySummarySchema.optional(),
  })
  .describe('FindingOverlaysResponse')
export type FindingOverlaysResponse = z.infer<typeof FindingOverlaysResponseSchema>
true satisfies AreEquivalent<FindingOverlaysResponse, components['schemas']['FindingOverlaysResponse']>

/**
 * A finding reference embedded in overlay content, sharing `FindingBackreferenceLinks` with the
 * exploit-chain `LinkedFinding` above rather than re-declaring HAL links per feature.
 *
 * The whole `_links` object is absent — not just `finding` inside it — when analysis-service's
 * backreference could not be resolved, and the enrichment fields (`rule/path/tool/severity`) go
 * with it. Consumers render an unlinked `findingId` label in that case rather than failing the row.
 */
export const FindingOverlayClusterFindingSchema = z
  .object({
    _links: FindingBackreferenceLinksSchema.optional(),
    findingId: z.string(),
    path: z.string().optional(),
    rule: z.string().optional(),
    severity: z.string().optional(),
    tool: z.string().optional(),
  })
  .describe('FindingOverlayClusterFinding')
export type FindingOverlayClusterFinding = z.infer<typeof FindingOverlayClusterFindingSchema>
true satisfies AreEquivalent<FindingOverlayClusterFinding, components['schemas']['FindingOverlayClusterFinding']>

// `rationale` is optional on both the priority and the cluster: the agent cites the threat-model
// element that moved the ranking when it has one to cite, and omits the field otherwise.
export const FindingOverlayPrioritySchema = FindingOverlayClusterFindingSchema.extend({
  rationale: z.string().optional(),
  score: z.number().int().min(0).max(100),
  tier: FindingOverlayTierSchema,
}).describe('FindingOverlayPriority')
export type FindingOverlayPriority = z.infer<typeof FindingOverlayPrioritySchema>
true satisfies AreEquivalent<FindingOverlayPriority, components['schemas']['FindingOverlayPriority']>

export const FindingOverlayClusterSchema = z
  .object({
    dimension: FindingOverlayDimensionSchema,
    findings: FindingOverlayClusterFindingSchema.array(),
    name: z.string(),
    rationale: z.string().optional(),
    subjectName: z.string(),
  })
  .describe('FindingOverlayCluster')
export type FindingOverlayCluster = z.infer<typeof FindingOverlayClusterSchema>
true satisfies AreEquivalent<FindingOverlayCluster, components['schemas']['FindingOverlayCluster']>

export const FindingOverlayResponseSchema = z
  .object({
    assumptions: z.string().array(),
    clusters: FindingOverlayClusterSchema.array(),
    narrative: z.string(),
    priorities: FindingOverlayPrioritySchema.array(),
  })
  .describe('FindingOverlayResponse')
export type FindingOverlayResponse = z.infer<typeof FindingOverlayResponseSchema>
true satisfies AreEquivalent<FindingOverlayResponse, components['schemas']['FindingOverlayResponse']>

export const NoThreatModelProblemSchema = z
  .object({
    detail: z.string(),
    status: z.number().int(),
    title: z.string(),
  })
  .describe('NoThreatModelProblem')
export type NoThreatModelProblem = z.infer<typeof NoThreatModelProblemSchema>
true satisfies AreEquivalent<NoThreatModelProblem, components['schemas']['NoThreatModelProblem']>

export const FindingOverlaysUnavailableProblemSchema = z
  .object({
    detail: z.string(),
    status: z.number().int(),
    title: z.string(),
  })
  .describe('FindingOverlaysUnavailableProblem')
export type FindingOverlaysUnavailableProblem = z.infer<typeof FindingOverlaysUnavailableProblemSchema>
true satisfies AreEquivalent<
  FindingOverlaysUnavailableProblem,
  components['schemas']['FindingOverlaysUnavailableProblem']
>

// #endregion Finding Overlays

// #region Scorecards

export const ScorecardsStatusSchema = z.enum(['processing', 'completed', 'failed']).describe('ScorecardsStatus')
export type ScorecardsStatus = z.infer<typeof ScorecardsStatusSchema>

export const ScorecardsPhaseSchema = z
  .enum(['queued', 'fetching_code', 'loading_threat_model', 'scoring_dimensions', 'finalizing'])
  .describe('ScorecardsPhase')
export type ScorecardsPhase = z.infer<typeof ScorecardsPhaseSchema>

export const ScorecardsLinksSchema = z
  .object({
    events: HALLinkSchema.optional(),
    self: HALLinkSchema,
  })
  .describe('ScorecardsLinks')
export type ScorecardsLinks = z.infer<typeof ScorecardsLinksSchema>
true satisfies AreEquivalent<ScorecardsLinks, components['schemas']['ScorecardsLinks']>

export const DimensionAnswerEvidenceSchema = z
  .object({
    excerpt: z.string().optional(),
    location: z.string(),
    note: z.string().optional(),
  })
  .describe('DimensionAnswerEvidence')
export type DimensionAnswerEvidence = z.infer<typeof DimensionAnswerEvidenceSchema>
true satisfies AreEquivalent<DimensionAnswerEvidence, components['schemas']['DimensionAnswerEvidence']>

export const DimensionAnswerSchema = z
  .object({
    answer: z.enum(['yes', 'no', 'na']),
    evidence: DimensionAnswerEvidenceSchema.array(),
    id: z.string(),
    question: z.string(),
    rationale: z.string().optional(),
  })
  .describe('DimensionAnswer')
export type DimensionAnswer = z.infer<typeof DimensionAnswerSchema>
true satisfies AreEquivalent<DimensionAnswer, components['schemas']['DimensionAnswer']>

export const DimensionScoreSchema = z
  .object({
    answers: DimensionAnswerSchema.array(),
    confidence: z.number(),
    description: z.string(),
    dimension: z.string(),
    displayName: z.string(),
    generatedAt: InstantSchema,
    modelUsed: z.string(),
    rationale: z.string(),
    rubricVersion: z.string(),
    score: z.number(),
    status: z.enum(['failed', 'ok']),
  })
  .describe('DimensionScore')
export type DimensionScore = z.infer<typeof DimensionScoreSchema>
true satisfies AreEquivalent<DimensionScore, components['schemas']['DimensionScore']>

export const ScorecardPayloadSchema = z
  .object({
    assumptionsOverhang: z.number(),
    commitSha: z.string(),
    dimensions: DimensionScoreSchema.array(),
    generatedAt: InstantSchema,
    systemOverview: z.string(),
    threatModelCommitSha: z.string(),
    threatModelDocId: z.string(),
  })
  .describe('ScorecardPayload')
export type ScorecardPayload = z.infer<typeof ScorecardPayloadSchema>
true satisfies AreEquivalent<ScorecardPayload, components['schemas']['ScorecardPayload']>

export const ScorecardsResponseSchema = z
  .object({
    _links: ScorecardsLinksSchema,
    commitSha: z.string().optional(),
    failureReason: z.string().optional(),
    generatedAt: InstantSchema.optional(),
    phase: ScorecardsPhaseSchema,
    requestedBy: z.string().optional(),
    scorecard: ScorecardPayloadSchema.optional(),
    status: ScorecardsStatusSchema,
  })
  .describe('ScorecardsResponse')
export type ScorecardsResponse = z.infer<typeof ScorecardsResponseSchema>
true satisfies AreEquivalent<ScorecardsResponse, components['schemas']['ScorecardsResponse']>

export const ScorecardsNoThreatModelProblemSchema = z
  .object({
    detail: z.string(),
    status: z.number().int(),
    title: z.string(),
  })
  .describe('ScorecardsNoThreatModelProblem')
export type ScorecardsNoThreatModelProblem = z.infer<typeof ScorecardsNoThreatModelProblemSchema>
true satisfies AreEquivalent<ScorecardsNoThreatModelProblem, components['schemas']['ScorecardsNoThreatModelProblem']>

export const ScorecardsUnavailableProblemSchema = z
  .object({
    detail: z.string(),
    status: z.number().int(),
    title: z.string(),
  })
  .describe('ScorecardsUnavailableProblem')
export type ScorecardsUnavailableProblem = z.infer<typeof ScorecardsUnavailableProblemSchema>
true satisfies AreEquivalent<ScorecardsUnavailableProblem, components['schemas']['ScorecardsUnavailableProblem']>

// #endregion

// #region OpenGrep Rules hand-authored types

// Only the three schemas below lack a spec counterpart, so they stay hand-authored while
// `OpenGrepRulesResponse`, `-Status` and `-Links` sit with the generated schemas under their
// `AreEquivalent` gates. The contract types `phase` as a bare string, and it never describes the
// generated rules at all -- those arrive as the body behind the response's `content` link.
export const OpenGrepRulesPhaseSchema = z.enum(['queued', 'generating', 'unknown']).describe('OpenGrepRulesPhase')
export type OpenGrepRulesPhase = z.infer<typeof OpenGrepRulesPhaseSchema>

export const OpenGrepRuleSchema = z
  .object({
    bad_code_example: z.string(),
    good_code_example: z.string(),
    language: z.string(),
    name: z.string(),
    noise_level: z.enum(['high', 'medium', 'low']),
    opengrep_yaml: z.string(),
    rationale: z.string(),
    rule_id: z.string(),
  })
  .describe('OpenGrepRule')
export type OpenGrepRule = z.infer<typeof OpenGrepRuleSchema>

export const OpenGrepRuleSetSchema = z
  .object({
    rules: OpenGrepRuleSchema.array(),
  })
  .describe('OpenGrepRuleSet')
export type OpenGrepRuleSet = z.infer<typeof OpenGrepRuleSetSchema>

// #endregion

export const PrerequisitesNotMetProblemSchema = z
  .object({
    detail: z.string(),
    needs_default_branch_scan: z.boolean(),
    needs_threat_model: z.boolean(),
    status: z.number().int(),
    title: z.string(),
  })
  .describe('PrerequisitesNotMetProblem')
export type PrerequisitesNotMetProblem = z.infer<typeof PrerequisitesNotMetProblemSchema>
true satisfies AreEquivalent<PrerequisitesNotMetProblem, components['schemas']['PrerequisitesNotMetProblem']>

export const ProjectFullProblemSchema = z
  .object({
    capacity: z.number().int(),
    detail: z.string(),
    remaining: z.number().int(),
    status: z.number().int(),
    title: z.string(),
  })
  .describe('ProjectFullProblem')
export type ProjectFullProblem = z.infer<typeof ProjectFullProblemSchema>
true satisfies AreEquivalent<ProjectFullProblem, components['schemas']['ProjectFullProblem']>

export const DuplicateProjectNameProblemSchema = z
  .object({
    detail: z.string(),
    status: z.number().int(),
    title: z.string(),
  })
  .describe('DuplicateProjectNameProblem')
export type DuplicateProjectNameProblem = z.infer<typeof DuplicateProjectNameProblemSchema>
true satisfies AreEquivalent<DuplicateProjectNameProblem, components['schemas']['DuplicateProjectNameProblem']>

export const RatingResponseSchema = z
  .object({
    description: z.string(),
    score: z.number(),
  })
  .describe('RatingResponse')
export type RatingResponse = z.infer<typeof RatingResponseSchema>
true satisfies AreEquivalent<RatingResponse, components['schemas']['RatingResponse']>

export const QualityResponseSchema = z
  .object({
    cleanliness: RatingResponseSchema,
    effectiveness: RatingResponseSchema,
    merge_readiness: RatingResponseSchema,
    safety: RatingResponseSchema,
  })
  .describe('QualityResponse')
export type QualityResponse = z.infer<typeof QualityResponseSchema>
true satisfies AreEquivalent<QualityResponse, components['schemas']['QualityResponse']>

export const ChangesetSchema = z
  .object({
    path: z.string(),
    preview: z.string(),
  })
  .describe('Changeset')
export type Changeset = z.infer<typeof ChangesetSchema>
true satisfies AreEquivalent<Changeset, components['schemas']['ChangesetResponse']>

export const ChangesetPaginatedResponseSchema = Paginated(ChangesetSchema, StandardPageNavigationLinksSchema).describe(
  'ChangesetPaginatedResponse'
)
export type ChangesetPaginatedResponse = z.infer<typeof ChangesetPaginatedResponseSchema>
true satisfies AreEquivalent<ChangesetPaginatedResponse, components['schemas']['ChangesetPaginatedResponse']>

export const AnalysisStateResponseSchema = z
  .enum(['queued', 'in-progress', 'skipped', 'completed', 'failed', 'cancelled'])
  .describe('AnalysisStateResponse')
export type AnalysisStateResponse = z.infer<typeof AnalysisStateResponseSchema>
true satisfies AreEquivalent<AnalysisStateResponse, components['schemas']['AnalysisStateResponse']>
const AnalysisStateLogResponseSchema = z
  .object({
    state: AnalysisStateResponseSchema,
    timestamp: z.string(),
  })
  .describe('AnalysisStateLogResponse')
export type AnalysisStateLogResponse = z.infer<typeof AnalysisStateLogResponseSchema>
true satisfies AreEquivalent<AnalysisStateLogResponse, components['schemas']['AnalysisStateLogResponse']>

export const AnalysisLinksSchema = z
  .object({
    cancellation: HALLinkSchema.optional(),
    preferences: HALLinkSchema.optional(),
    scan: HALLinkSchema,
    self: HALLinkSchema,
  })
  .describe('AnalysisLinks')
export type AnalysisLinks = z.infer<typeof AnalysisLinksSchema>
true satisfies AreEquivalent<AnalysisLinks, components['schemas']['AnalysisLinks']>

export const AnalysisCancellationLinksSchema = z
  .object({
    analyses: HALLinkSchema,
    scan: HALLinkSchema,
    self: HALLinkSchema,
  })
  .describe('AnalysisCancellationLinks')
export type AnalysisCancellationLinks = z.infer<typeof AnalysisCancellationLinksSchema>
true satisfies AreEquivalent<AnalysisCancellationLinks, components['schemas']['AnalysisCancellationLinks']>

export const AnalysisCancellationResponseSchema = z
  .object({
    _links: AnalysisCancellationLinksSchema,
    created_at: z.string(),
    created_by: z.string(),
    id: z.string(),
  })
  .describe('AnalysisCancellationResponse')
export type AnalysisCancellationResponse = z.infer<typeof AnalysisCancellationResponseSchema>
true satisfies AreEquivalent<AnalysisCancellationResponse, components['schemas']['AnalysisCancellationResponse']>

export const CancellationConflictProblemLinksSchema = z
  .object({
    cancellation: HALLinkSchema,
  })
  .describe('CancellationConflictProblemLinks')
export type CancellationConflictProblemLinks = z.infer<typeof CancellationConflictProblemLinksSchema>
true satisfies AreEquivalent<
  CancellationConflictProblemLinks,
  components['schemas']['CancellationConflictProblemLinks']
>

export const CancellationConflictProblemSchema = z
  .object({
    _links: CancellationConflictProblemLinksSchema,
    cancellation_id: z.string(),
    detail: z.string(),
    status: z.number().int(),
    title: z.string(),
  })
  .describe('CancellationConflictProblem')
export type CancellationConflictProblem = z.infer<typeof CancellationConflictProblemSchema>
true satisfies AreEquivalent<CancellationConflictProblem, components['schemas']['CancellationConflictProblem']>

export const FindingsSelectionSchema = z
  .object({
    finding_ids: z.array(z.string()),
    scan_id: z.string(),
  })
  .describe('FindingsSelection')
export type FindingsSelection = z.infer<typeof FindingsSelectionSchema>
true satisfies AreEquivalent<FindingsSelection, components['schemas']['FindingsSelection']>

export const FindingsFilterSchema = z.object({
  include_severity_labels: z.array(z.string()).optional(),
  maximum_severity_score: z.number().int().optional(),
  minimum_severity_score: z.number().int().optional(),
})
export type FindingsFilter = z.infer<typeof FindingsFilterSchema>
true satisfies AreEquivalent<FindingsFilter, components['schemas']['FindingsFilter']>

export const AzureOrganizationSchema = z
  .object({
    name: z.string(),
  })
  .describe('AzureOrganization')
export type AzureOrganization = z.infer<typeof AzureOrganizationSchema>
true satisfies AreEquivalent<AzureOrganization, components['schemas']['AzureOrganizationResponse']>
export const AzureProjectSchema = z
  .object({
    id: z.string(),
    name: z.string(),
  })
  .describe('AzureProject')
export type AzureProject = z.infer<typeof AzureProjectSchema>
true satisfies AreEquivalent<AzureProject, components['schemas']['AzureProjectResponse']>
export const AzureIntegrationRepositorySchema = z
  .object({
    id: z.string(),
    name: z.string(),
    pixee_id: z.string().optional(),
    project: AzureProjectSchema,
  })
  .describe('AzureIntegrationRepository')
export type AzureIntegrationRepository = z.infer<typeof AzureIntegrationRepositorySchema>
true satisfies AreEquivalent<AzureIntegrationRepository, components['schemas']['IntegrationsAzureRepositoryResponse']>

export const AzureOrganizationPaginatedResponseSchema = CursorPaginated(AzureOrganizationSchema).describe(
  'AzureOrganizationPaginatedResponse'
)
export type AzureOrganizationPaginatedResponse = z.infer<typeof AzureOrganizationPaginatedResponseSchema>
true satisfies AreEquivalent<
  AzureOrganizationPaginatedResponse,
  components['schemas']['AzureOrganizationPaginatedResponse']
>

export const AzureProjectPaginatedResponseSchema = CursorPaginated(AzureProjectSchema).describe(
  'AzureProjectPaginatedResponse'
)
export type AzureProjectPaginatedResponse = z.infer<typeof AzureProjectPaginatedResponseSchema>
true satisfies AreEquivalent<AzureProjectPaginatedResponse, components['schemas']['AzureProjectPaginatedResponse']>

export const AzureRepositoryPaginatedResponseSchema = CursorPaginated(AzureIntegrationRepositorySchema).describe(
  'AzureRepositoryPaginatedResponse'
)
export type AzureRepositoryPaginatedResponse = z.infer<typeof AzureRepositoryPaginatedResponseSchema>
true satisfies AreEquivalent<
  AzureRepositoryPaginatedResponse,
  components['schemas']['AzureRepositoryPaginatedResponse']
>

export const AzureAuthenticationProblemSchema = z
  .object({
    detail: z.string(),
    status: z.number().int(),
    title: z.string(),
  })
  .describe('AzureAuthenticationProblem')
export type AzureAuthenticationProblem = z.infer<typeof AzureAuthenticationProblemSchema>
true satisfies AreEquivalent<AzureAuthenticationProblem, components['schemas']['AzureAuthenticationProblem']>

export const ProjectChangedProblemSchema = z
  .object({
    detail: z.string(),
    project_id: z.string(),
    status: z.number().int(),
    title: z.string(),
  })
  .describe('ProjectChangedProblem')
export type ProjectChangedProblem = z.infer<typeof ProjectChangedProblemSchema>
true satisfies AreEquivalent<ProjectChangedProblem, components['schemas']['ProjectChangedProblem']>

export const FindingCountLimitSchema = z.object({
  max: z.number().int().min(1),
  type: z.literal('finding-count'),
})
export type FindingCountLimit = z.infer<typeof FindingCountLimitSchema>
true satisfies AreEquivalent<FindingCountLimit, components['schemas']['FindingCountLimit']>

// Older pixee-platform builds serialize WorkflowActionLimit via Jackson's
// WRAPPER_ARRAY mode (e.g. `["none", "INSTANCE"]`), a fallback used before
// NoneWorkflowActionLimit was modeled as an object so Jackson could write the
// `type` discriminator inline (pixee-platform#6384). Unwrap the [typeId,
// payload] envelope back to the spec-documented payload so the rest of the
// schema parses successfully. Keeps compatibility with a user-platform image
// pinned newer than the platform image it talks to.
const unwrapWorkflowActionLimit = (value: unknown): unknown => {
  if (Array.isArray(value) && value.length === 2 && typeof value[0] === 'string') {
    return value[1]
  }
  return value
}

export const WorkflowActionSchema = z
  .discriminatedUnion('type', [
    z.object({
      findings: z
        .object({
          include_severity_labels: z.array(z.string()).optional(),
          maximum_severity_score: z.number().optional(),
          minimum_severity_score: z.number().optional(),
        })
        .optional(),
      fixes: z
        .object({
          max_count: z.number().optional(),
          minimum_fix_confidence: z.enum(['high', 'medium', 'low', 'no-rating']),
        })
        .optional(),
      limit: z
        .preprocess(
          unwrapWorkflowActionLimit,
          z.union([
            z.object({
              max: z.number(),
              type: z.literal('finding-count'),
            }),
            z.object({
              type: z.literal('none'),
            }),
            z.literal('INSTANCE'),
          ])
        )
        .optional(),
      type: z.literal('create-patch'),
    }),
    z.object({
      type: z.literal('none'),
    }),
  ])
  .describe('WorkflowAction')
export type WorkflowAction = z.infer<typeof WorkflowActionSchema>
// @ts-expect-error Spec uses oneOf without an explicit type discriminator; Zod uses a discriminatedUnion with one.
true satisfies AreEquivalent<WorkflowAction, components['schemas']['WorkflowAction']>

export const NoneWorkflowActionLimitSchema = z
  .object({
    type: z.literal('none'),
  })
  .describe('NoneWorkflowActionLimit')
export type NoneWorkflowActionLimit = z.infer<typeof NoneWorkflowActionLimitSchema>
true satisfies AreEquivalent<NoneWorkflowActionLimit, components['schemas']['NoneWorkflowActionLimit']>

export const WorkflowActionLimitSchema = z
  .discriminatedUnion('type', [FindingCountLimitSchema, NoneWorkflowActionLimitSchema])
  .describe('WorkflowActionLimit')
export type WorkflowActionLimit = z.infer<typeof WorkflowActionLimitSchema>
true satisfies AreEquivalent<WorkflowActionLimit, components['schemas']['WorkflowActionLimit']>

export const CreateWorkflowResponseSchema = z
  .object({
    _links: z.object({
      repository: HALLinkSchema,
      self: HALLinkSchema,
    }),
    id: z.string(),
    name: z.string(),
    type: z.string(),
  })
  .describe('CreateWorkflowResponse')
export type CreateWorkflowResponse = z.infer<typeof CreateWorkflowResponseSchema>
// @ts-expect-error Not yet in sync with spec (structural differences).
true satisfies AreEquivalent<CreateWorkflowResponse, components['schemas']['WorkflowResponse']>

export const GitLabProjectResponseSchema = z
  .object({
    name_with_namespace: z.string(),
    namespace_full_path: z.string(),
    path: z.string(),
    pixee_id: z.string().optional(),
  })
  .describe('GitLabProjectResponse')
export type GitLabProjectResponse = z.infer<typeof GitLabProjectResponseSchema>
true satisfies AreEquivalent<GitLabProjectResponse, components['schemas']['GitLabProjectResponse']>

export const GitLabProjectPaginatedResponseSchema = CursorPaginated(GitLabProjectResponseSchema).describe(
  'GitLabProjectPaginatedResponse'
)
export type GitLabProjectPaginatedResponse = z.infer<typeof GitLabProjectPaginatedResponseSchema>
true satisfies AreEquivalent<GitLabProjectPaginatedResponse, components['schemas']['GitLabProjectPaginatedResponse']>

export const SonarProjectResponseSchema = z
  .object({
    key: z.string(),
    name: z.string(),
  })
  .describe('SonarProjectResponse')
export type SonarProjectResponse = z.infer<typeof SonarProjectResponseSchema>
true satisfies AreEquivalent<SonarProjectResponse, components['schemas']['SonarProjectResponse']>

export const SonarProjectPaginatedResponseSchema = CursorPaginated(SonarProjectResponseSchema).describe(
  'SonarProjectPaginatedResponse'
)
export type SonarProjectPaginatedResponse = z.infer<typeof SonarProjectPaginatedResponseSchema>
true satisfies AreEquivalent<SonarProjectPaginatedResponse, components['schemas']['SonarProjectPaginatedResponse']>

export const ScheduleWorkflowCadenceSchema = z.enum(['daily', 'weekly']).describe('ScheduleWorkflowCadence')
export type ScheduleWorkflowCadence = z.infer<typeof ScheduleWorkflowCadenceSchema>
true satisfies AreEquivalent<ScheduleWorkflowCadence, components['schemas']['ScheduleWorkflowCadence']>

export const ScheduleWorkflowEventSchema = z
  .object({
    branch: z.string().optional(),
    cadence: ScheduleWorkflowCadenceSchema,
    start: z.string().optional(),
    type: z.literal('schedule'),
  })
  .describe('ScheduleWorkflowEvent')
export type ScheduleWorkflowEvent = z.infer<typeof ScheduleWorkflowEventSchema>
true satisfies AreEquivalent<ScheduleWorkflowEvent, components['schemas']['ScheduleWorkflowEvent']>

export const NewScanWorkflowEventRequestSchema = z
  .object({
    branch: z.string().optional(),
    type: z.literal('new-scan'),
  })
  .describe('NewScanWorkflowEventRequest')
export type NewScanWorkflowEventRequest = z.infer<typeof NewScanWorkflowEventRequestSchema>
true satisfies AreEquivalent<NewScanWorkflowEventRequest, components['schemas']['NewScanWorkflowEventRequest']>

export const PullRequestScanWorkflowEventRequestSchema = z
  .object({
    source_branch: z.string().optional(),
    target_branch: z.string().optional(),
    type: z.literal('pull-request-scan'),
  })
  .describe('PullRequestScanWorkflowEventRequest')
export type PullRequestScanWorkflowEventRequest = z.infer<typeof PullRequestScanWorkflowEventRequestSchema>
true satisfies AreEquivalent<
  PullRequestScanWorkflowEventRequest,
  components['schemas']['PullRequestScanWorkflowEventRequest']
>

export const WorkflowEventSchema = z
  .discriminatedUnion('type', [
    ScheduleWorkflowEventSchema,
    NewScanWorkflowEventRequestSchema,
    PullRequestScanWorkflowEventRequestSchema,
  ])
  .describe('WorkflowEvent')
export type WorkflowEvent = z.infer<typeof WorkflowEventSchema>
true satisfies AreEquivalent<WorkflowEvent, components['schemas']['WorkflowEvent']>

export const WorkflowLinksSchema = z
  .object({
    repository: HALLinkSchema,
    runs: HALLinkSchema.optional(),
    scale: HALLinkSchema,
    self: HALLinkSchema,
  })
  .describe('WorkflowLinks')
export type WorkflowLinks = z.infer<typeof WorkflowLinksSchema>
true satisfies AreEquivalent<WorkflowLinks, components['schemas']['WorkflowLinks']>

export const WorkflowResponseSchema = z
  .object({
    _links: WorkflowLinksSchema,
    action: WorkflowActionSchema,
    created_at: z.string().optional(),
    enabled: z.boolean(),
    event: WorkflowEventSchema,
    id: z.string(),
    name: z.string(),
    tool: ToolSchema,
  })
  .describe('WorkflowResponse')
export type WorkflowResponse = z.infer<typeof WorkflowResponseSchema>
// @ts-expect-error WorkflowAction Spec uses oneOf without an explicit type discriminator; Zod uses a discriminatedUnion with one.
true satisfies AreEquivalent<WorkflowResponse, components['schemas']['WorkflowResponse']>

export const WorkflowPaginatedResponseSchema = z
  .object({
    _embedded: z.object({
      items: PermissiveDiscriminatedUnionArray(WorkflowResponseSchema),
    }),
    _links: StandardPageNavigationLinksSchema,
    page: PageResponseSchema,
    total: z.number().int(),
  })
  .describe('WorkflowPaginatedResponse')
export type WorkflowPaginatedResponse = z.infer<typeof WorkflowPaginatedResponseSchema>
// @ts-expect-error Transitive: WorkflowResponseSchema diverges from spec (repository/runs); items is permissive.
true satisfies AreEquivalent<WorkflowPaginatedResponse, components['schemas']['WorkflowPaginatedResponse']>

const CapabilitySchema = z.enum(['triage-push']).describe('Capability')
type Capability = z.infer<typeof CapabilitySchema>
true satisfies AreEquivalent<Capability, components['schemas']['Capability']>

const ToolIntegrationLinksSchema = z
  .object({
    scale: HALLinkSchema.optional(),
    self: HALLinkSchema,
  })
  .describe('ToolIntegrationLinks')
type ToolIntegrationLinks = z.infer<typeof ToolIntegrationLinksSchema>
true satisfies AreEquivalent<ToolIntegrationLinks, components['schemas']['ToolIntegrationLinks']>

const AzureIntegrationLinksSchema = z
  .object({
    organizations: HALLinkSchema.optional(),
    self: HALLinkSchema,
  })
  .describe('AzureIntegrationLinks')
type AzureIntegrationLinks = z.infer<typeof AzureIntegrationLinksSchema>
true satisfies AreEquivalent<AzureIntegrationLinks, components['schemas']['AzureIntegrationLinks']>

const BitbucketIntegrationLinksSchema = z
  .object({
    self: HALLinkSchema,
    workspaces: HALLinkSchema.optional(),
  })
  .describe('BitbucketIntegrationLinks')
type BitbucketIntegrationLinks = z.infer<typeof BitbucketIntegrationLinksSchema>
true satisfies AreEquivalent<BitbucketIntegrationLinks, components['schemas']['BitbucketIntegrationLinks']>

const CheckmarxIntegrationLinksSchema = z
  .object({
    scale: HALLinkSchema.optional(),
    self: HALLinkSchema,
  })
  .describe('CheckmarxIntegrationLinks')
type CheckmarxIntegrationLinks = z.infer<typeof CheckmarxIntegrationLinksSchema>
true satisfies AreEquivalent<CheckmarxIntegrationLinks, components['schemas']['CheckmarxIntegrationLinks']>

const GitLabIntegrationLinksSchema = z
  .object({
    projects: HALLinkSchema.optional(),
    self: HALLinkSchema,
  })
  .describe('GitLabIntegrationLinks')
type GitLabIntegrationLinks = z.infer<typeof GitLabIntegrationLinksSchema>
true satisfies AreEquivalent<GitLabIntegrationLinks, components['schemas']['GitLabIntegrationLinks']>

const SonarIntegrationLinksSchema = z
  .object({
    scale: HALLinkSchema,
    self: HALLinkSchema,
    webhooks: HALLinkSchema,
  })
  .describe('SonarIntegrationLinks')
type SonarIntegrationLinks = z.infer<typeof SonarIntegrationLinksSchema>
true satisfies AreEquivalent<SonarIntegrationLinks, components['schemas']['SonarIntegrationLinks']>

const VeracodeIntegrationLinksSchema = z
  .object({
    applications: HALLinkSchema.optional(),
    scale: HALLinkSchema.optional(),
    self: HALLinkSchema,
  })
  .describe('VeracodeIntegrationLinks')
type VeracodeIntegrationLinks = z.infer<typeof VeracodeIntegrationLinksSchema>
true satisfies AreEquivalent<VeracodeIntegrationLinks, components['schemas']['VeracodeIntegrationLinks']>

const AppScanIntegrationLinksSchema = z
  .object({
    scale: HALLinkSchema,
    self: HALLinkSchema,
    webhooks: HALLinkSchema,
  })
  .describe('AppScanIntegrationLinks')
type AppScanIntegrationLinks = z.infer<typeof AppScanIntegrationLinksSchema>
true satisfies AreEquivalent<AppScanIntegrationLinks, components['schemas']['AppScanIntegrationLinks']>

const IntegrationTypeSchema = z.enum([
  'sonar',
  'github',
  'gitlab',
  'azure',
  'bitbucket',
  'veracode',
  'appscan',
  'checkmarx',
  'arnica',
  'datadog',
  'polaris',
  'black-duck-hub',
  'checkmarx-enterprise',
])
export type IntegrationType = z.infer<typeof IntegrationTypeSchema>
true satisfies AreEquivalent<IntegrationType, components['schemas']['IntegrationType']>
// Use a set for O(1) lookup, and type it as `unknown` to avoid the need to cast `item.type`.
const INTEGRATION_TYPE_SET = new Set<unknown>(IntegrationTypeSchema.options)

export const AzureManagedIdentityResponseSchema = z
  .object({
    client_id: z.string(),
  })
  .describe('AzureManagedIdentityResponse')
export type AzureManagedIdentityResponse = z.infer<typeof AzureManagedIdentityResponseSchema>
true satisfies AreEquivalent<AzureManagedIdentityResponse, components['schemas']['AzureManagedIdentityResponse']>

export const AzureManagedIdentitiesResponseSchema = z
  .object({
    _links: SelfLinkSchema,
    identities: z.array(AzureManagedIdentityResponseSchema),
  })
  .describe('AzureManagedIdentitiesResponse')
export type AzureManagedIdentitiesResponse = z.infer<typeof AzureManagedIdentitiesResponseSchema>
true satisfies AreEquivalent<AzureManagedIdentitiesResponse, components['schemas']['AzureManagedIdentitiesResponse']>

/**
 * The `Allow` header, split into methods. Internal to the client — the spec documents no OPTIONS
 * operation on the collection — so there is no `AreEquivalent` counterpart to check against.
 * A server that answers the probe with no `Allow` yields no methods, which reads as read-only.
 */
export const AllowedMethodsSchema = z
  .string()
  .nullable()
  .transform(header =>
    (header ?? '')
      .split(',')
      .map(method => method.trim().toUpperCase())
      .filter(Boolean)
  )
  .describe('AllowedMethods')

export const IntegrationHealthStatusResponseSchema = z
  .enum(['healthy', 'needs-attention', 'unknown'])
  .describe('IntegrationHealthStatusResponse')
export type IntegrationHealthStatusResponse = z.infer<typeof IntegrationHealthStatusResponseSchema>
true satisfies AreEquivalent<IntegrationHealthStatusResponse, components['schemas']['IntegrationHealthStatusResponse']>

export const IntegrationSourceResponseSchema = z.enum(['managed', 'system']).describe('IntegrationSourceResponse')
export type IntegrationSourceResponse = z.infer<typeof IntegrationSourceResponseSchema>
true satisfies AreEquivalent<IntegrationSourceResponse, components['schemas']['IntegrationSourceResponse']>

export const IntegrationHealthResponseSchema = z
  .object({
    detail: z.string().optional(),
    last_checked_at: InstantSchema.optional(),
    status: IntegrationHealthStatusResponseSchema,
  })
  .describe('IntegrationHealthResponse')
export type IntegrationHealthResponse = z.infer<typeof IntegrationHealthResponseSchema>
true satisfies AreEquivalent<IntegrationHealthResponse, components['schemas']['IntegrationHealthResponse']>

export const CheckmarxRegionSchema = z.enum(['ANZ', 'DEU', 'EU', 'EU2', 'IND', 'MEA', 'SNG', 'US', 'US2'])
export type CheckmarxRegion = z.infer<typeof CheckmarxRegionSchema>
true satisfies AreEquivalent<CheckmarxRegion, components['schemas']['CheckmarxRegion']>

export const AppScanWebhookAuthMethodSchema = z
  .enum(['basic-auth', 'shared-secret'])
  .describe('AppScanWebhookAuthMethod')
export type AppScanWebhookAuthMethod = z.infer<typeof AppScanWebhookAuthMethodSchema>
true satisfies AreEquivalent<AppScanWebhookAuthMethod, components['schemas']['AppScanWebhookAuthMethod']>

const BaseIntegrationSchema = z.object({
  capabilities: z.array(CapabilitySchema).optional(),
  health: IntegrationHealthResponseSchema.optional(),
  // Required upstream: every instance in the merged collection has an id, whether it is a stored
  // row's UUID or a config-derived slug like `gitlab-default`.
  id: z.string(),
  name: z.string(),
  repository_count: z.number().int().optional(),
  source: IntegrationSourceResponseSchema,
})
const SonarIntegrationSchema = BaseIntegrationSchema.extend({
  _links: SonarIntegrationLinksSchema,
  base_uri: z.url().optional(),
  github_app_id: z.number().int().optional(),
  github_app_name: z.string().optional(),
  organization: z.string().optional(),
  token_configured: z.boolean().optional(),
  type: z.literal('sonar'),
  webhook_secret_configured: z.boolean().optional(),
})
export type SonarIntegration = z.infer<typeof SonarIntegrationSchema>
true satisfies AreEquivalent<SonarIntegration, components['schemas']['SonarIntegrationResponse']>
const GitHubIntegrationSchema = BaseIntegrationSchema.extend({
  _links: SelfLinkSchema,
  app_id: z.string().optional(),
  app_name: z.string().optional(),
  base_uri: z.url().optional(),
  type: z.literal('github'),
})
export type GitHubIntegration = z.infer<typeof GitHubIntegrationSchema>
true satisfies AreEquivalent<GitHubIntegration, components['schemas']['GitHubIntegrationResponse']>
const GitLabIntegrationSchema = BaseIntegrationSchema.extend({
  _links: GitLabIntegrationLinksSchema,
  base_uri: z.url().optional(),
  type: z.literal('gitlab'),
})
export type GitLabIntegration = z.infer<typeof GitLabIntegrationSchema>
true satisfies AreEquivalent<GitLabIntegration, components['schemas']['GitLabIntegrationResponse']>
const AzureIntegrationSchema = BaseIntegrationSchema.extend({
  _links: AzureIntegrationLinksSchema,
  base_uri: z.url().optional(),
  commit_message_prefix: z.string().optional(),
  organization: z.string().optional(),
  type: z.literal('azure'),
  webhook_configured: z.boolean().optional(),
  webhook_user: z.string().optional(),
  work_item_id: z.number().int().optional(),
})
export type AzureIntegration = z.infer<typeof AzureIntegrationSchema>
true satisfies AreEquivalent<AzureIntegration, components['schemas']['AzureIntegrationResponse']>
const BitbucketIntegrationSchema = BaseIntegrationSchema.extend({
  _links: BitbucketIntegrationLinksSchema,
  email_address: z.string().optional(),
  type: z.literal('bitbucket'),
  username: z.string().optional(),
})
export type BitbucketIntegration = z.infer<typeof BitbucketIntegrationSchema>
true satisfies AreEquivalent<BitbucketIntegration, components['schemas']['BitbucketIntegrationResponse']>
export const VeracodeIntegrationSchema = BaseIntegrationSchema.extend({
  _links: VeracodeIntegrationLinksSchema,
  api_key_configured: z.boolean().optional(),
  base_uri: z.url().optional(),
  signing_target: z.string().optional(),
  type: z.literal('veracode'),
}).describe('VeracodeIntegration')
export type VeracodeIntegration = z.infer<typeof VeracodeIntegrationSchema>
true satisfies AreEquivalent<VeracodeIntegration, components['schemas']['VeracodeIntegrationResponse']>
const AppScanIntegrationSchema = BaseIntegrationSchema.extend({
  _links: AppScanIntegrationLinksSchema,
  api_key_configured: z.boolean().optional(),
  base_uri: z.url().optional(),
  type: z.literal('appscan'),
  webhook_auth_method: AppScanWebhookAuthMethodSchema.optional(),
  webhook_user: z.string().optional(),
})
export type AppScanIntegration = z.infer<typeof AppScanIntegrationSchema>
true satisfies AreEquivalent<AppScanIntegration, components['schemas']['AppScanIntegrationResponse']>
export const CheckmarxIntegrationSchema = BaseIntegrationSchema.extend({
  _links: CheckmarxIntegrationLinksSchema,
  api_key_configured: z.boolean().optional(),
  base_auth_uri: z.string().optional(),
  base_uri: z.url().optional(),
  region: CheckmarxRegionSchema.optional(),
  tenant: z.string().optional(),
  type: z.literal('checkmarx'),
}).describe('CheckmarxIntegration')
export type CheckmarxIntegration = z.infer<typeof CheckmarxIntegrationSchema>
true satisfies AreEquivalent<CheckmarxIntegration, components['schemas']['CheckmarxIntegrationResponse']>
export const ArnicaIntegrationProvenanceSchema = z
  .enum(['pattern-matching', 'user-supplied'])
  .describe('ArnicaIntegrationProvenance')
export type ArnicaIntegrationProvenance = z.infer<typeof ArnicaIntegrationProvenanceSchema>
true satisfies AreEquivalent<ArnicaIntegrationProvenance, components['schemas']['ArnicaIntegrationProvenance']>

export const SonarIntegrationProvenanceSchema = z
  .enum(['GITHUB_CHECK_RUN', 'SONAR_WEBHOOK', 'PATTERN_MATCHING', 'USER_PROVIDED'])
  .describe('SonarIntegrationProvenance')
export type SonarIntegrationProvenance = z.infer<typeof SonarIntegrationProvenanceSchema>
true satisfies AreEquivalent<SonarIntegrationProvenance, components['schemas']['SonarIntegrationProvenance']>

const ArnicaIntegrationSchema = BaseIntegrationSchema.extend({
  _links: ToolIntegrationLinksSchema,
  api_key_configured: z.boolean().optional(),
  base_uri: z.url().optional(),
  type: z.literal('arnica'),
})
export type ArnicaIntegration = z.infer<typeof ArnicaIntegrationSchema>
true satisfies AreEquivalent<ArnicaIntegration, components['schemas']['ArnicaIntegrationResponse']>

const DatadogIntegrationSchema = BaseIntegrationSchema.extend({
  _links: ToolIntegrationLinksSchema,
  base_uri: z.url().optional(),
  type: z.literal('datadog'),
})
export type DatadogIntegration = z.infer<typeof DatadogIntegrationSchema>
true satisfies AreEquivalent<DatadogIntegration, components['schemas']['DatadogIntegrationResponse']>

const PolarisIntegrationSchema = BaseIntegrationSchema.extend({
  _links: ToolIntegrationLinksSchema,
  api_token_configured: z.boolean().optional(),
  base_uri: z.url().optional(),
  type: z.literal('polaris'),
})
export type PolarisIntegration = z.infer<typeof PolarisIntegrationSchema>
true satisfies AreEquivalent<PolarisIntegration, components['schemas']['PolarisIntegrationResponse']>

/**
 * `base_uri` is `z.string()` where every other tool's is `z.url()`, because the spec gives this
 * response no `format: uri` — though the Hub create and update requests for the same field have it.
 * Tracked upstream as ISS-9020; this becomes `z.url().optional()` on the sync after that lands.
 */
const BlackDuckHubIntegrationSchema = BaseIntegrationSchema.extend({
  _links: ToolIntegrationLinksSchema,
  api_token_configured: z.boolean().optional(),
  base_uri: z.string().optional(),
  type: z.literal('black-duck-hub'),
})
export type BlackDuckHubIntegration = z.infer<typeof BlackDuckHubIntegrationSchema>
true satisfies AreEquivalent<BlackDuckHubIntegration, components['schemas']['BlackDuckHubIntegrationResponse']>

/** `base_uri` is `z.string()` for the same reason as Black Duck Hub's: the response gives it no `format: uri`. Tracked upstream as ISS-9192. */
const CheckmarxEnterpriseIntegrationSchema = BaseIntegrationSchema.extend({
  _links: ToolIntegrationLinksSchema,
  base_uri: z.string().optional(),
  credentials_configured: z.boolean().optional(),
  type: z.literal('checkmarx-enterprise'),
})
export type CheckmarxEnterpriseIntegration = z.infer<typeof CheckmarxEnterpriseIntegrationSchema>
true satisfies AreEquivalent<
  CheckmarxEnterpriseIntegration,
  components['schemas']['CheckmarxEnterpriseIntegrationResponse']
>

/**
 * Why the tool refused the connection a create or update described. The drawers branch on this to
 * choose which field to anchor the error to, so the user is pointed at the field they can act on and
 * is never told to re-enter a credential the tool never rejected.
 */
export const IntegrationValidationFailureResponseSchema = z
  .enum([
    'authentication-rejected',
    'endpoint-not-allowed',
    'host-unreachable',
    'insufficient-permissions',
    'unexpected-response',
  ])
  .describe('IntegrationValidationFailureResponse')
export type IntegrationValidationFailureResponse = z.infer<typeof IntegrationValidationFailureResponseSchema>
true satisfies AreEquivalent<
  IntegrationValidationFailureResponse,
  components['schemas']['IntegrationValidationFailureResponse']
>

export const IntegrationSchema = z
  .discriminatedUnion('type', [
    SonarIntegrationSchema,
    GitHubIntegrationSchema,
    GitLabIntegrationSchema,
    AzureIntegrationSchema,
    BitbucketIntegrationSchema,
    VeracodeIntegrationSchema,
    AppScanIntegrationSchema,
    CheckmarxIntegrationSchema,
    ArnicaIntegrationSchema,
    DatadogIntegrationSchema,
    PolarisIntegrationSchema,
    BlackDuckHubIntegrationSchema,
    CheckmarxEnterpriseIntegrationSchema,
  ])
  .describe('Integration')
export type Integration = z.infer<typeof IntegrationSchema>
true satisfies AreEquivalent<Integration, components['schemas']['IntegrationResponseObject']>

/**
 * One integration, or `null` for a tool this client does not model.
 *
 * Narrower than `PermissiveDiscriminatedUnionArray` on purpose: only an unrecognized `type` is
 * tolerated, and a `sonar` item missing a required field still throws rather than vanishing from the
 * page. A collection where any unparseable item should be dropped wants that helper instead.
 *
 * The single rule for tolerating an unmodelled tool, read by both surfaces that serve this resource:
 * the collection filters the `null`s out of `_embedded.items`, and the single-item read that follows
 * an integration's `self` href returns it, so a caller with a fallback for "no instance to name" —
 * the read-only Instance field in the add-findings drawer — shows that instead of erroring. `null`
 * rather than `undefined` because react-query rejects `undefined` from a `queryFn`.
 *
 * No `AreEquivalent` counterpart: the `null` is this client's own, not something the platform sends.
 */
export const ModelledIntegrationSchema = z
  .object({ type: z.unknown().optional() })
  .loose()
  .transform(integration => (INTEGRATION_TYPE_SET.has(integration.type) ? IntegrationSchema.parse(integration) : null))
  .describe('ModelledIntegration')

/**
 * Items whose `type` this client does not model are dropped rather than failing the parse, so a
 * platform that gains a tool ahead of the front end still serves a usable page.
 *
 * `total` is the platform's, describing the whole filtered collection rather than the page — so a
 * page that dropped an unmodelled item reports more in `total` than it lists in `items`. That is the
 * honest reading: the collection has instances this build cannot render.
 */
export const IntegrationPaginatedResponseSchema = Paginated(IntegrationSchema, StandardPageNavigationLinksSchema)
  .extend({
    _embedded: z.object({
      items: z.array(ModelledIntegrationSchema).transform(items => items.filter(item => item !== null)),
    }),
  })
  .describe('IntegrationPaginatedResponse')
export type IntegrationPaginatedResponse = z.infer<typeof IntegrationPaginatedResponseSchema>
true satisfies AreEquivalent<IntegrationPaginatedResponse, components['schemas']['IntegrationPaginatedResponse']>

const IntegrationTypeCountSchema = z
  .object({
    count: z.number().int(),
    type: IntegrationTypeSchema,
  })
  .describe('IntegrationTypeCount')
type IntegrationTypeCount = z.infer<typeof IntegrationTypeCountSchema>
true satisfies AreEquivalent<IntegrationTypeCount, components['schemas']['IntegrationTypeCount']>

/**
 * Only tools that have at least one instance appear, and the counts ignore the `search` and `type`
 * parameters `GET /api/v1/integrations` accepts. That is what makes this usable for the tool chooser:
 * counts narrowed by `type` would leave it offering only the tool already selected.
 */
export const IntegrationTypeCountsResponseSchema = z
  .object({
    _links: SelfLinkSchema,
    type_counts: z.array(IntegrationTypeCountSchema),
  })
  .describe('IntegrationTypeCountsResponse')
export type IntegrationTypeCountsResponse = z.infer<typeof IntegrationTypeCountsResponseSchema>
true satisfies AreEquivalent<IntegrationTypeCountsResponse, components['schemas']['IntegrationTypeCountsResponse']>

const CreateCheckmarxIntegrationRequestSchema = z
  .object({
    api_key: z.string().min(1),
    name: z.string().min(1),
    region: CheckmarxRegionSchema,
    tenant: z.string().min(1),
    type: z.literal('checkmarx'),
  })
  .describe('CreateCheckmarxIntegrationRequest')
type CreateCheckmarxIntegrationRequest = z.infer<typeof CreateCheckmarxIntegrationRequestSchema>
true satisfies AreEquivalent<
  CreateCheckmarxIntegrationRequest,
  components['schemas']['CreateCheckmarxIntegrationRequest']
>

const CreateAppScanIntegrationRequestSchema = z
  .object({
    api_key_id: z.string().min(1),
    api_key_secret: z.string().min(1),
    base_uri: z.url(),
    name: z.string().min(1),
    type: z.literal('appscan'),
    webhook_auth_method: AppScanWebhookAuthMethodSchema.optional(),
    webhook_password: z.string().optional(),
    webhook_secret: z.string().optional(),
    webhook_user: z.string().optional(),
  })
  .describe('CreateAppScanIntegrationRequest')
type CreateAppScanIntegrationRequest = z.infer<typeof CreateAppScanIntegrationRequestSchema>
true satisfies AreEquivalent<CreateAppScanIntegrationRequest, components['schemas']['CreateAppScanIntegrationRequest']>

const CreateArnicaIntegrationRequestSchema = z
  .object({
    api_key: z.string().min(1),
    base_uri: z.url(),
    name: z.string().min(1),
    type: z.literal('arnica'),
  })
  .describe('CreateArnicaIntegrationRequest')
type CreateArnicaIntegrationRequest = z.infer<typeof CreateArnicaIntegrationRequestSchema>
true satisfies AreEquivalent<CreateArnicaIntegrationRequest, components['schemas']['CreateArnicaIntegrationRequest']>

const CreateDatadogIntegrationRequestSchema = z
  .object({
    api_key: z.string().min(1),
    application_key: z.string().min(1),
    base_uri: z.url(),
    name: z.string().min(1),
    type: z.literal('datadog'),
  })
  .describe('CreateDatadogIntegrationRequest')
type CreateDatadogIntegrationRequest = z.infer<typeof CreateDatadogIntegrationRequestSchema>
true satisfies AreEquivalent<CreateDatadogIntegrationRequest, components['schemas']['CreateDatadogIntegrationRequest']>

const CreateBitbucketIntegrationRequestSchema = z
  .object({
    api_token: z.string().min(1),
    email_address: z.string().min(1),
    name: z.string().min(1),
    type: z.literal('bitbucket'),
    username: z.string().min(1),
  })
  .describe('CreateBitbucketIntegrationRequest')
type CreateBitbucketIntegrationRequest = z.infer<typeof CreateBitbucketIntegrationRequestSchema>
true satisfies AreEquivalent<
  CreateBitbucketIntegrationRequest,
  components['schemas']['CreateBitbucketIntegrationRequest']
>

const CreateAzureIntegrationRequestSchema = z
  .object({
    base_uri: z.url(),
    client_id: z.string().optional(),
    client_secret: z.string().optional(),
    commit_message_prefix: z.string().optional(),
    managed_identity_client_id: z.string().optional(),
    name: z.string().min(1),
    organization: z.string().min(1),
    tenant_id: z.string().optional(),
    token: z.string().optional(),
    type: z.literal('azure'),
    webhook_password: z.string().optional(),
    work_item_id: z.number().int().optional(),
  })
  .describe('CreateAzureIntegrationRequest')
type CreateAzureIntegrationRequest = z.infer<typeof CreateAzureIntegrationRequestSchema>
true satisfies AreEquivalent<CreateAzureIntegrationRequest, components['schemas']['CreateAzureIntegrationRequest']>

const CreateGitHubIntegrationRequestSchema = z
  .object({
    app_id: z.string().min(1),
    app_name: z.string().min(1),
    base_uri: z.url(),
    name: z.string().min(1),
    private_key: z.string().min(1),
    type: z.literal('github'),
    webhook_secret: z.string().min(1),
  })
  .describe('CreateGitHubIntegrationRequest')
type CreateGitHubIntegrationRequest = z.infer<typeof CreateGitHubIntegrationRequestSchema>
true satisfies AreEquivalent<CreateGitHubIntegrationRequest, components['schemas']['CreateGitHubIntegrationRequest']>

const CreateGitLabIntegrationRequestSchema = z
  .object({
    base_uri: z.url(),
    name: z.string().min(1),
    token: z.string().min(1),
    type: z.literal('gitlab'),
    webhook_secret: z.string().optional(),
  })
  .describe('CreateGitLabIntegrationRequest')
type CreateGitLabIntegrationRequest = z.infer<typeof CreateGitLabIntegrationRequestSchema>
true satisfies AreEquivalent<CreateGitLabIntegrationRequest, components['schemas']['CreateGitLabIntegrationRequest']>

const CreatePolarisIntegrationRequestSchema = z
  .object({
    api_token: z.string().min(1),
    base_uri: z.url(),
    name: z.string().min(1),
    type: z.literal('polaris'),
  })
  .describe('CreatePolarisIntegrationRequest')
type CreatePolarisIntegrationRequest = z.infer<typeof CreatePolarisIntegrationRequestSchema>
true satisfies AreEquivalent<CreatePolarisIntegrationRequest, components['schemas']['CreatePolarisIntegrationRequest']>

const CreateSonarIntegrationRequestSchema = z
  .object({
    base_uri: z.url(),
    github_app_id: z.number().int().min(1).optional(),
    github_app_name: z.string().regex(/\S/).optional(),
    name: z.string().min(1),
    organization: z.string().regex(/\S/).optional(),
    token: z.string().min(1),
    type: z.literal('sonar'),
    webhook_secret: z.string().regex(/\S/).optional(),
  })
  .describe('CreateSonarIntegrationRequest')
type CreateSonarIntegrationRequest = z.infer<typeof CreateSonarIntegrationRequestSchema>
true satisfies AreEquivalent<CreateSonarIntegrationRequest, components['schemas']['CreateSonarIntegrationRequest']>

const CreateVeracodeIntegrationRequestSchema = z
  .object({
    api_key_id: z.string().min(1),
    api_key_secret: z.string().min(1),
    base_uri: z.url(),
    name: z.string().min(1),
    signing_target: z.url().optional(),
    type: z.literal('veracode'),
  })
  .describe('CreateVeracodeIntegrationRequest')
type CreateVeracodeIntegrationRequest = z.infer<typeof CreateVeracodeIntegrationRequestSchema>
true satisfies AreEquivalent<
  CreateVeracodeIntegrationRequest,
  components['schemas']['CreateVeracodeIntegrationRequest']
>

const CreateBlackDuckHubIntegrationRequestSchema = z
  .object({
    api_token: z.string().min(1),
    base_uri: z.url(),
    name: z.string().min(1),
    type: z.literal('black-duck-hub'),
  })
  .describe('CreateBlackDuckHubIntegrationRequest')
type CreateBlackDuckHubIntegrationRequest = z.infer<typeof CreateBlackDuckHubIntegrationRequestSchema>
true satisfies AreEquivalent<
  CreateBlackDuckHubIntegrationRequest,
  components['schemas']['CreateBlackDuckHubIntegrationRequest']
>

const CreateCheckmarxEnterpriseIntegrationRequestSchema = z
  .object({
    base_uri: z.url(),
    client_id: z.string().min(1),
    client_secret: z.string().min(1),
    name: z.string().min(1),
    password: z.string().min(1),
    type: z.literal('checkmarx-enterprise'),
    username: z.string().min(1),
  })
  .describe('CreateCheckmarxEnterpriseIntegrationRequest')
type CreateCheckmarxEnterpriseIntegrationRequest = z.infer<typeof CreateCheckmarxEnterpriseIntegrationRequestSchema>
true satisfies AreEquivalent<
  CreateCheckmarxEnterpriseIntegrationRequest,
  components['schemas']['CreateCheckmarxEnterpriseIntegrationRequest']
>

export const CreateIntegrationRequestSchema = z
  .discriminatedUnion('type', [
    CreateAppScanIntegrationRequestSchema,
    CreateArnicaIntegrationRequestSchema,
    CreateAzureIntegrationRequestSchema,
    CreateBitbucketIntegrationRequestSchema,
    CreateBlackDuckHubIntegrationRequestSchema,
    CreateCheckmarxIntegrationRequestSchema,
    CreateCheckmarxEnterpriseIntegrationRequestSchema,
    CreateDatadogIntegrationRequestSchema,
    CreateGitHubIntegrationRequestSchema,
    CreateGitLabIntegrationRequestSchema,
    CreatePolarisIntegrationRequestSchema,
    CreateSonarIntegrationRequestSchema,
    CreateVeracodeIntegrationRequestSchema,
  ])
  .describe('CreateIntegrationRequest')
export type CreateIntegrationRequest = z.infer<typeof CreateIntegrationRequestSchema>
true satisfies AreEquivalent<CreateIntegrationRequest, components['schemas']['CreateIntegrationRequest']>

/**
 * `region` changes both hosts at once, so the backend only accepts it alongside a resupplied
 * `api_key` — the frontend does not enforce this pairing and simply forwards what's given.
 */
const UpdateCheckmarxIntegrationRequestSchema = z
  .object({
    api_key: z.string().optional(),
    name: z.string().optional(),
    region: CheckmarxRegionSchema.optional(),
    tenant: z.string().optional(),
    type: z.literal('checkmarx'),
  })
  .describe('UpdateCheckmarxIntegrationRequest')
type UpdateCheckmarxIntegrationRequest = z.infer<typeof UpdateCheckmarxIntegrationRequestSchema>
true satisfies AreEquivalent<
  UpdateCheckmarxIntegrationRequest,
  components['schemas']['UpdateCheckmarxIntegrationRequest']
>

/**
 * The API key pair is replace-or-keep: omit both `api_key_id` and `api_key_secret` to keep the
 * stored pair, or supply both to rotate them. `webhook_password` and `webhook_secret` may each be
 * replaced independently of the API key pair, without needing to resend it. Send
 * `webhook_auth_method: null` to clear it along with the credential it selects.
 */
const UpdateAppScanIntegrationRequestSchema = z
  .object({
    api_key_id: z.string().optional(),
    api_key_secret: z.string().optional(),
    base_uri: z.url().optional(),
    name: z.string().optional(),
    type: z.literal('appscan'),
    webhook_auth_method: AppScanWebhookAuthMethodSchema.nullable().optional(),
    webhook_password: z.string().optional(),
    webhook_secret: z.string().optional(),
    webhook_user: z.string().optional(),
  })
  .describe('UpdateAppScanIntegrationRequest')
type UpdateAppScanIntegrationRequest = z.infer<typeof UpdateAppScanIntegrationRequestSchema>
true satisfies AreEquivalent<UpdateAppScanIntegrationRequest, components['schemas']['UpdateAppScanIntegrationRequest']>

/**
 * The credential pair is replace-or-keep: omit both keys to keep the stored credentials, or supply
 * both to rotate them. The backend rejects one without the other, and rejects blank values.
 */
const UpdateDatadogIntegrationRequestSchema = z
  .object({
    api_key: z.string().optional(),
    application_key: z.string().optional(),
    base_uri: z.url().optional(),
    name: z.string().optional(),
    type: z.literal('datadog'),
  })
  .describe('UpdateDatadogIntegrationRequest')
type UpdateDatadogIntegrationRequest = z.infer<typeof UpdateDatadogIntegrationRequestSchema>
true satisfies AreEquivalent<UpdateDatadogIntegrationRequest, components['schemas']['UpdateDatadogIntegrationRequest']>

const UpdateBitbucketIntegrationRequestSchema = z
  .object({
    api_token: z.string().optional(),
    email_address: z.string().optional(),
    name: z.string().optional(),
    type: z.literal('bitbucket'),
    username: z.string().optional(),
  })
  .describe('UpdateBitbucketIntegrationRequest')
type UpdateBitbucketIntegrationRequest = z.infer<typeof UpdateBitbucketIntegrationRequestSchema>
true satisfies AreEquivalent<
  UpdateBitbucketIntegrationRequest,
  components['schemas']['UpdateBitbucketIntegrationRequest']
>

const UpdateAzureIntegrationRequestSchema = z
  .object({
    base_uri: z.url().optional(),
    commit_message_prefix: z.string().nullable().optional(),
    name: z.string().optional(),
    organization: z.string().optional(),
    token: z.string().optional(),
    type: z.literal('azure'),
    webhook_password: z.string().nullable().optional(),
    work_item_id: z.number().int().nullable().optional(),
  })
  .describe('UpdateAzureIntegrationRequest')
type UpdateAzureIntegrationRequest = z.infer<typeof UpdateAzureIntegrationRequestSchema>
true satisfies AreEquivalent<UpdateAzureIntegrationRequest, components['schemas']['UpdateAzureIntegrationRequest']>

const UpdateGitHubIntegrationRequestSchema = z
  .object({
    app_id: z.string().optional(),
    app_name: z.string().optional(),
    base_uri: z.url().optional(),
    name: z.string().optional(),
    private_key: z.string().optional(),
    type: z.literal('github'),
    webhook_secret: z.string().optional(),
  })
  .describe('UpdateGitHubIntegrationRequest')
type UpdateGitHubIntegrationRequest = z.infer<typeof UpdateGitHubIntegrationRequestSchema>
true satisfies AreEquivalent<UpdateGitHubIntegrationRequest, components['schemas']['UpdateGitHubIntegrationRequest']>

const UpdateGitLabIntegrationRequestSchema = z
  .object({
    base_uri: z.url().optional(),
    name: z.string().optional(),
    token: z.string().optional(),
    type: z.literal('gitlab'),
    webhook_secret: z.string().nullish(),
  })
  .describe('UpdateGitLabIntegrationRequest')
type UpdateGitLabIntegrationRequest = z.infer<typeof UpdateGitLabIntegrationRequestSchema>
true satisfies AreEquivalent<UpdateGitLabIntegrationRequest, components['schemas']['UpdateGitLabIntegrationRequest']>

const UpdatePolarisIntegrationRequestSchema = z
  .object({
    api_token: z.string().regex(/\S/).optional(),
    base_uri: z.url().optional(),
    name: z.string().optional(),
    type: z.literal('polaris'),
  })
  .describe('UpdatePolarisIntegrationRequest')
type UpdatePolarisIntegrationRequest = z.infer<typeof UpdatePolarisIntegrationRequestSchema>
true satisfies AreEquivalent<UpdatePolarisIntegrationRequest, components['schemas']['UpdatePolarisIntegrationRequest']>

const UpdateSonarIntegrationRequestSchema = z
  .object({
    base_uri: z.url().optional(),
    github_app_id: z.number().int().min(1).optional(),
    github_app_name: z.string().nullable().optional(),
    name: z.string().optional(),
    organization: z.string().nullable().optional(),
    token: z.string().regex(/\S/).optional(),
    type: z.literal('sonar'),
    webhook_secret: z.string().regex(/\S/).optional(),
  })
  .describe('UpdateSonarIntegrationRequest')
type UpdateSonarIntegrationRequest = z.infer<typeof UpdateSonarIntegrationRequestSchema>
true satisfies AreEquivalent<UpdateSonarIntegrationRequest, components['schemas']['UpdateSonarIntegrationRequest']>

const UpdateVeracodeIntegrationRequestSchema = z
  .object({
    api_key_id: z.string().regex(/\S/).optional(),
    api_key_secret: z.string().regex(/\S/).optional(),
    base_uri: z.url().optional(),
    name: z.string().optional(),
    signing_target: z.url().nullable().optional(),
    type: z.literal('veracode'),
  })
  .describe('UpdateVeracodeIntegrationRequest')
type UpdateVeracodeIntegrationRequest = z.infer<typeof UpdateVeracodeIntegrationRequestSchema>
true satisfies AreEquivalent<
  UpdateVeracodeIntegrationRequest,
  components['schemas']['UpdateVeracodeIntegrationRequest']
>

const UpdateArnicaIntegrationRequestSchema = z
  .object({
    api_key: z.string().optional(),
    base_uri: z.url().optional(),
    name: z.string().optional(),
    type: z.literal('arnica'),
  })
  .describe('UpdateArnicaIntegrationRequest')
type UpdateArnicaIntegrationRequest = z.infer<typeof UpdateArnicaIntegrationRequestSchema>
true satisfies AreEquivalent<UpdateArnicaIntegrationRequest, components['schemas']['UpdateArnicaIntegrationRequest']>

const UpdateBlackDuckHubIntegrationRequestSchema = z
  .object({
    api_token: z.string().optional(),
    base_uri: z.url().optional(),
    name: z.string().optional(),
    type: z.literal('black-duck-hub'),
  })
  .describe('UpdateBlackDuckHubIntegrationRequest')
type UpdateBlackDuckHubIntegrationRequest = z.infer<typeof UpdateBlackDuckHubIntegrationRequestSchema>
true satisfies AreEquivalent<
  UpdateBlackDuckHubIntegrationRequest,
  components['schemas']['UpdateBlackDuckHubIntegrationRequest']
>

const UpdateCheckmarxEnterpriseIntegrationRequestSchema = z
  .object({
    base_uri: z.url().optional(),
    client_id: z.string().regex(/\S/).optional(),
    client_secret: z.string().regex(/\S/).optional(),
    name: z.string().optional(),
    password: z.string().regex(/\S/).optional(),
    type: z.literal('checkmarx-enterprise'),
    username: z.string().regex(/\S/).optional(),
  })
  .describe('UpdateCheckmarxEnterpriseIntegrationRequest')
type UpdateCheckmarxEnterpriseIntegrationRequest = z.infer<typeof UpdateCheckmarxEnterpriseIntegrationRequestSchema>
true satisfies AreEquivalent<
  UpdateCheckmarxEnterpriseIntegrationRequest,
  components['schemas']['UpdateCheckmarxEnterpriseIntegrationRequest']
>

export const UpdateIntegrationRequestSchema = z
  .discriminatedUnion('type', [
    UpdateAppScanIntegrationRequestSchema,
    UpdateArnicaIntegrationRequestSchema,
    UpdateAzureIntegrationRequestSchema,
    UpdateBitbucketIntegrationRequestSchema,
    UpdateBlackDuckHubIntegrationRequestSchema,
    UpdateCheckmarxIntegrationRequestSchema,
    UpdateCheckmarxEnterpriseIntegrationRequestSchema,
    UpdateDatadogIntegrationRequestSchema,
    UpdateGitHubIntegrationRequestSchema,
    UpdateGitLabIntegrationRequestSchema,
    UpdatePolarisIntegrationRequestSchema,
    UpdateSonarIntegrationRequestSchema,
    UpdateVeracodeIntegrationRequestSchema,
  ])
  .describe('UpdateIntegrationRequest')
export type UpdateIntegrationRequest = z.infer<typeof UpdateIntegrationRequestSchema>
true satisfies AreEquivalent<UpdateIntegrationRequest, components['schemas']['UpdateIntegrationRequest']>

/**
 * The integration types the spec accepts a create/update body for, derived from the discriminated
 * union so that a variant shipping upstream widens the client without a second list to maintain.
 */
export type WritableIntegrationType = CreateIntegrationRequest['type']

// `getIsIntegrationTypeSupportedByApi` also gates the `UpdateIntegrationRequestSchema.parse` in
// `usePutIntegration`, which is only sound while the two unions carry the same types. A spec that
// ships a create-only variant should break the build here, not throw inside a mutation.
true satisfies AreEquivalent<CreateIntegrationRequest['type'], UpdateIntegrationRequest['type']>

const WRITABLE_INTEGRATION_TYPE_SET = new Set<string>(
  CreateIntegrationRequestSchema.options.map(option => option.shape.type.value)
)

/**
 * Whether the spec has a write contract for this type. Distinct from `getHasIntegrationSetupForm`
 * in `integration-instance-utils.ts`, which answers whether the *product* has built a setup form.
 */
export const getIsIntegrationTypeSupportedByApi = (type: string): type is WritableIntegrationType =>
  WRITABLE_INTEGRATION_TYPE_SET.has(type)

export const ScanOriginKindSchema = z.enum(['ci_run']).describe('ScanOriginKind')
export type ScanOriginKind = z.infer<typeof ScanOriginKindSchema>
true satisfies AreEquivalent<ScanOriginKind, components['schemas']['ScanOriginKind']>

// Scan/Analysis responses live here, below IntegrationSchema, because `_embedded.integration` references it.
export const ScanResponseSchema = z
  .object({
    _embedded: z
      .object({ integration: IntegrationSchema.optional(), repository: RepositorySchema.optional() })
      .optional(),
    _links: ScanLinksSchema,
    branch: z.string().optional(),
    detector: ToolSchema,
    expires_at: InstantSchema.optional(),
    html_url: z.string().optional(),
    id: z.string(),
    imported_at: InstantSchema,
    origin: ScanOriginKindSchema.optional(),
    origin_url: z.string().optional(),
    scanned_at: InstantSchema.optional(),
    sha: z.string(),
  })
  .describe('ScanResponse')
export type ScanResponse = z.infer<typeof ScanResponseSchema>
true satisfies AreEquivalent<ScanResponse, components['schemas']['ScanResponse']>

export const PageEmbeddedScanResponseSchema = z.object({
  items: z.array(ScanResponseSchema),
})
export type PageEmbeddedScanResponse = z.infer<typeof PageEmbeddedScanResponseSchema>
true satisfies AreEquivalent<PageEmbeddedScanResponse, components['schemas']['PageEmbeddedScanResponse']>

export const BranchEmbeddedSchema = z.object({
  'latest-scan': ScanResponseSchema.optional(),
})
export type BranchEmbedded = z.infer<typeof BranchEmbeddedSchema>
true satisfies AreEquivalent<BranchEmbedded, components['schemas']['BranchEmbedded']>

export const ScanAggregateResponseSchema = Paginated(ScanResponseSchema, StandardPageNavigationLinksSchema)
  .extend({
    // Deprecated in favor of `GET /api/v1/repositories/{id}/branches`, and removed once nothing reads
    // it (ENH-493). Optional so responses without it still parse.
    distinct_branches: z.array(z.string()).optional(),
  })
  .describe('ScanAggregateResponse')
export type ScanAggregateResponse = z.infer<typeof ScanAggregateResponseSchema>
// @ts-expect-error `distinct_branches` is optional here ahead of its removal from the spec (ENH-493).
true satisfies AreEquivalent<ScanAggregateResponse, components['schemas']['ScanAggregateResponse']>

export const AnalysisResponseSchema = z
  .object({
    _embedded: z
      .object({
        scan: ScanResponseSchema.optional(),
      })
      .optional(),
    _links: AnalysisLinksSchema,
    current_state: AnalysisStateLogResponseSchema,
    findings_count: z.number().int().optional(),
    id: z.string(),
    log: z.array(AnalysisStateLogResponseSchema),
  })
  .describe('AnalysisResponse')
export type AnalysisResponse = z.infer<typeof AnalysisResponseSchema>
true satisfies AreEquivalent<AnalysisResponse, components['schemas']['AnalysisResponse']>

export const AnalysisPaginatedResponseSchema = Paginated(
  AnalysisResponseSchema,
  StandardPageNavigationLinksSchema
).describe('AnalysisPaginatedResponse')
export type AnalysisPaginatedResponse = z.infer<typeof AnalysisPaginatedResponseSchema>
true satisfies AreEquivalent<AnalysisPaginatedResponse, components['schemas']['AnalysisPaginatedResponse']>

export const CreateAnalysisRequestSchema = z
  .object({
    finding_ids: z.array(z.string()).optional(),
  })
  .describe('CreateAnalysisRequest')
export type CreateAnalysisRequest = z.infer<typeof CreateAnalysisRequestSchema>
true satisfies AreEquivalent<CreateAnalysisRequest, components['schemas']['CreateAnalysisRequest']>

const ScaleLinksSchema = z.object({
  self: HALLinkSchema,
})

export const SeveritySchema = z
  .object({
    label: z.string().optional(),
    rank: z.number(),
  })
  .describe('Severity')
export type Severity = z.infer<typeof SeveritySchema>
true satisfies AreEquivalent<Severity, components['schemas']['SeverityResponse']>

export const RankedSetResponseSchema = z
  .object({
    _links: ScaleLinksSchema,
    id: z.string(),
    severities: z.array(SeveritySchema),
    type: z.literal('ranked-set'),
  })
  .describe('RankedSetResponse')
export type RankedSetResponse = z.infer<typeof RankedSetResponseSchema>
true satisfies AreEquivalent<RankedSetResponse, components['schemas']['RankedSetResponse']>

const ScoreResponseSchema = z
  .object({
    _links: ScaleLinksSchema,
    id: z.string().optional(),
    max: z.number().optional(),
    min: z.number().optional(),
    type: z.literal('score'),
  })
  .describe('ScoreResponse')
type ScoreResponse = z.infer<typeof ScoreResponseSchema>
true satisfies AreEquivalent<ScoreResponse, components['schemas']['ScoreResponse']>

export const ScaleSchema = z
  .discriminatedUnion('type', [RankedSetResponseSchema, ScoreResponseSchema])
  .describe('Scale')
export type Scale = z.infer<typeof ScaleSchema>
true satisfies AreEquivalent<Scale, components['schemas']['ScaleResponse']>

export const ScalePaginatedResponseSchema = Paginated(ScaleSchema, StandardPageNavigationLinksSchema).describe(
  'ScalePaginatedResponse'
)
export type ScalePaginatedResponse = z.infer<typeof ScalePaginatedResponseSchema>
true satisfies AreEquivalent<ScalePaginatedResponse, components['schemas']['ScalePaginatedResponse']>

export const MergeStateResponseSchema = z
  .enum(['open', 'closed_merged', 'closed_not_merged'])
  .describe('MergeStateResponse')
export type MergeStateResponse = z.infer<typeof MergeStateResponseSchema>
true satisfies AreEquivalent<MergeStateResponse, components['schemas']['MergeStateResponse']>

export const PatchStateSchema = z
  .enum(['in-progress', 'completed-success', 'completed-failed', 'completed-fixes-overcome-by-events'])
  .describe('PatchState')
export type PatchState = z.infer<typeof PatchStateSchema>
true satisfies AreEquivalent<PatchState, components['schemas']['PatchStateResponse']>
const BasePatchSchema = z.object({
  _links: z.object({
    self: HALLinkSchema,
  }),
  id: z.string(),
  state: PatchStateSchema,
})

export const GitPatchResponseSchema = BasePatchSchema.extend({
  branch_name: z.string().optional(),
  type: z.literal('git'),
}).describe('GitPatchResponse')
export type GitPatchResponse = z.infer<typeof GitPatchResponseSchema>
true satisfies AreEquivalent<GitPatchResponse, components['schemas']['GitPatchResponse']>

const PullRequestPatchBaseSchema = BasePatchSchema.extend({
  html_url: z.string().optional(),
  merge_state: MergeStateResponseSchema.optional(),
  pr_number: z.number().optional(),
})

export const GitHubPatchResponseSchema = PullRequestPatchBaseSchema.extend({
  type: z.literal('github'),
}).describe('GitHubPatchResponse')
export type GitHubPatchResponse = z.infer<typeof GitHubPatchResponseSchema>
true satisfies AreEquivalent<GitHubPatchResponse, components['schemas']['GitHubPatchResponse']>

export const GitLabPatchResponseSchema = PullRequestPatchBaseSchema.extend({
  type: z.literal('gitlab'),
}).describe('GitLabPatchResponse')
export type GitLabPatchResponse = z.infer<typeof GitLabPatchResponseSchema>
true satisfies AreEquivalent<GitLabPatchResponse, components['schemas']['GitLabPatchResponse']>

export const AzurePatchResponseSchema = PullRequestPatchBaseSchema.extend({
  type: z.literal('azure'),
}).describe('AzurePatchResponse')
export type AzurePatchResponse = z.infer<typeof AzurePatchResponseSchema>
true satisfies AreEquivalent<AzurePatchResponse, components['schemas']['AzurePatchResponse']>

export const BitbucketPatchResponseSchema = PullRequestPatchBaseSchema.extend({
  type: z.literal('bitbucket'),
}).describe('BitbucketPatchResponse')
export type BitbucketPatchResponse = z.infer<typeof BitbucketPatchResponseSchema>
true satisfies AreEquivalent<BitbucketPatchResponse, components['schemas']['BitbucketPatchResponse']>

export const PatchSchema = z
  .discriminatedUnion('type', [
    GitPatchResponseSchema,
    GitHubPatchResponseSchema,
    GitLabPatchResponseSchema,
    AzurePatchResponseSchema,
    BitbucketPatchResponseSchema,
  ])
  .describe('Patch')
export type Patch = z.infer<typeof PatchSchema>
true satisfies AreEquivalent<Patch, components['schemas']['PatchResponse']>

export const TriageAnalyzerStrategySchema = z
  .enum(['static', 'auto_mapped', 'linter', 'dynamic'])
  .describe('TriageAnalyzerStrategy')
export type TriageAnalyzerStrategy = z.infer<typeof TriageAnalyzerStrategySchema>
true satisfies AreEquivalent<
  TriageAnalyzerStrategy,
  components['schemas']['TriageAnalyzerMetadataResponse']['strategy']
>

export const TriageAnalyzerMetadataResponseSchema = z
  .object({
    strategy: TriageAnalyzerStrategySchema,
  })
  .describe('TriageAnalyzerMetadataResponse')
export type TriageAnalyzerMetadataResponse = z.infer<typeof TriageAnalyzerMetadataResponseSchema>
true satisfies AreEquivalent<TriageAnalyzerMetadataResponse, components['schemas']['TriageAnalyzerMetadataResponse']>

export const ScaClassificationResponseSchema = z
  .enum(['exploitable', 'not-exploitable', 'inconclusive', 'potentially-exploitable'])
  .describe('ScaClassificationResponse')
export type ScaClassificationResponse = z.infer<typeof ScaClassificationResponseSchema>
true satisfies AreEquivalent<ScaClassificationResponse, components['schemas']['ScaClassificationResponse']>

export const DuplicateRepositoryIntegrationProblemResponseSchema = z
  .object({
    detail: z.string(),
    status: z.number(),
    title: z.string(),
  })
  .describe('DuplicateRepositoryIntegrationProblem')
export type DuplicateRepositoryIntegrationProblemResponse = z.infer<
  typeof DuplicateRepositoryIntegrationProblemResponseSchema
>
true satisfies AreEquivalent<
  DuplicateRepositoryIntegrationProblemResponse,
  components['schemas']['DuplicateRepositoryIntegrationProblem']
>

export const DuplicateIntegrationNameProblemSchema = z
  .object({
    detail: z.string(),
    name: z.string(),
    status: z.number().int(),
    title: z.string(),
  })
  .describe('DuplicateIntegrationNameProblem')
export type DuplicateIntegrationNameProblem = z.infer<typeof DuplicateIntegrationNameProblemSchema>
true satisfies AreEquivalent<DuplicateIntegrationNameProblem, components['schemas']['DuplicateIntegrationNameProblem']>

export const UnknownIntegrationProblemResponseSchema = z
  .object({
    detail: z.string(),
    integration_id: z.string(),
    status: z.number().int(),
    title: z.string(),
  })
  .describe('UnknownIntegrationProblem')
export type UnknownIntegrationProblemResponse = z.infer<typeof UnknownIntegrationProblemResponseSchema>
true satisfies AreEquivalent<UnknownIntegrationProblemResponse, components['schemas']['UnknownIntegrationProblem']>

export const UnknownRepositoriesProblemSchema = z
  .object({
    detail: z.string(),
    repository_ids: z.array(z.string()),
    status: z.number().int(),
    title: z.string(),
  })
  .describe('UnknownRepositoriesProblem')
export type UnknownRepositoriesProblem = z.infer<typeof UnknownRepositoriesProblemSchema>
true satisfies AreEquivalent<UnknownRepositoriesProblem, components['schemas']['UnknownRepositoriesProblem']>

export const IntegrationTypeMismatchProblemResponseSchema = z
  .object({
    detail: z.string(),
    integration_id: z.string(),
    status: z.number().int(),
    title: z.string(),
  })
  .describe('IntegrationTypeMismatchProblem')
export type IntegrationTypeMismatchProblemResponse = z.infer<typeof IntegrationTypeMismatchProblemResponseSchema>
true satisfies AreEquivalent<
  IntegrationTypeMismatchProblemResponse,
  components['schemas']['IntegrationTypeMismatchProblem']
>

/**
 * The `409` refusing to delete an instance that repositories still reference. `repositories` is
 * capped at ten by the platform however many there are, so `repository_count` — the true total — is
 * what sizes the job, and may exceed the number named.
 */
export const IntegrationInUseProblemSchema = z
  .object({
    detail: z.string(),
    repositories: z.array(z.string()),
    repository_count: z.number().int(),
    status: z.number().int(),
    title: z.string(),
  })
  .describe('IntegrationInUseProblem')
export type IntegrationInUseProblem = z.infer<typeof IntegrationInUseProblemSchema>
true satisfies AreEquivalent<IntegrationInUseProblem, components['schemas']['IntegrationInUseProblem']>

/**
 * The `409` refusing to change a connection's server address once repositories have been onboarded
 * through it.
 *
 * `repositories` is a *count* here, not a list: the platform carries it as a typed field so a client
 * can say how many repositories are in the way without parsing `detail`, which names the connection
 * by its id and so is not fit to show. Its sibling `IntegrationInUseProblemSchema` spells the same
 * key as the repository names themselves — the two shapes are not interchangeable.
 *
 * De-anchored from the spec: its `PUT` still documents only the concurrency and duplicate conflicts,
 * so there is no `AreEquivalent` counterpart to check against. Listed under "Ahead of the spec" in
 * `unmapped-schemas.md`, which is where the swap to an `AreEquivalent` line is tracked.
 */
export const IntegrationEndpointFixedProblemSchema = z
  .object({
    detail: z.string(),
    integration_id: z.string(),
    repositories: z.number().int(),
    status: z.number().int(),
    title: z.string(),
  })
  .describe('IntegrationEndpointFixedProblem')
export type IntegrationEndpointFixedProblem = z.infer<typeof IntegrationEndpointFixedProblemSchema>

export const IntegrationCredentialsUnreadableProblemSchema = z
  .object({
    detail: z.string(),
    integration_id: z.string(),
    status: z.number().int(),
    title: z.string(),
  })
  .describe('IntegrationCredentialsUnreadableProblem')
export type IntegrationCredentialsUnreadableProblem = z.infer<typeof IntegrationCredentialsUnreadableProblemSchema>
true satisfies AreEquivalent<
  IntegrationCredentialsUnreadableProblem,
  components['schemas']['IntegrationCredentialsUnreadableProblem']
>

export const IntegrationEndpointMovedWithoutCredentialsProblemSchema = z
  .object({
    detail: z.string(),
    integration_id: z.string(),
    missing_credentials: z.array(z.string()).optional(),
    status: z.number().int(),
    title: z.string(),
  })
  .describe('IntegrationEndpointMovedWithoutCredentialsProblem')
export type IntegrationEndpointMovedWithoutCredentialsProblem = z.infer<
  typeof IntegrationEndpointMovedWithoutCredentialsProblemSchema
>
true satisfies AreEquivalent<
  IntegrationEndpointMovedWithoutCredentialsProblem,
  components['schemas']['IntegrationEndpointMovedWithoutCredentialsProblem']
>

export const DuplicateGitHubAppProblemSchema = z
  .object({
    app_id: z.string(),
    base_uri: z.string(),
    detail: z.string(),
    status: z.number().int(),
    title: z.string(),
  })
  .describe('DuplicateGitHubAppProblem')
export type DuplicateGitHubAppProblem = z.infer<typeof DuplicateGitHubAppProblemSchema>
true satisfies AreEquivalent<DuplicateGitHubAppProblem, components['schemas']['DuplicateGitHubAppProblem']>

export const ScanNotAnalyzableProblemResponseSchema = z
  .object({
    detail: z.string(),
    status: z.number(),
    title: z.string(),
    type: z.literal('pixee:problems/api/analysis/scan-not-analyzable'),
  })
  .describe('ScanNotAnalyzableProblem')
export type ScanNotAnalyzableProblemResponse = z.infer<typeof ScanNotAnalyzableProblemResponseSchema>
true satisfies AreEquivalent<ScanNotAnalyzableProblemResponse, components['schemas']['ScanNotAnalyzableProblem']>

export const FindingsNotFoundProblemResponseSchema = z
  .object({
    detail: z.string(),
    missing_finding_ids: z.array(z.string()),
    status: z.number(),
    title: z.string(),
    type: z.literal('pixee:problems/api/analysis/findings-not-found'),
  })
  .describe('FindingsNotFoundProblem')
export type FindingsNotFoundProblemResponse = z.infer<typeof FindingsNotFoundProblemResponseSchema>
true satisfies AreEquivalent<FindingsNotFoundProblemResponse, components['schemas']['FindingsNotFoundProblem']>

export const AnalyzeScanProblemResponseSchema = z
  .union([FindingsNotFoundProblemResponseSchema, ScanNotAnalyzableProblemResponseSchema])
  .describe('AnalyzeScanProblem')
export type AnalyzeScanProblemResponse = z.infer<typeof AnalyzeScanProblemResponseSchema>
true satisfies AreEquivalent<AnalyzeScanProblemResponse, components['schemas']['AnalyzeScanProblem']>

export const TriageSuggestedStatusResponseSchema = z
  .enum([
    'wont_fix',
    'false_positive',
    'true_positive',
    'suspicious',
    'inconclusive',
    'not_triaged',
    'blocked',
    'excluded',
    'error',
  ])
  .describe('TriageSuggestedStatusResponse')
export type TriageSuggestedStatusResponse = z.infer<typeof TriageSuggestedStatusResponseSchema>
true satisfies AreEquivalent<TriageSuggestedStatusResponse, components['schemas']['TriageSuggestedStatusResponse']>

export const SeverityUpdateFilterSchema = z
  .enum(['increased', 'decreased', 'no_update'])
  .describe('SeverityUpdateFilter')
export type SeverityUpdateFilter = z.infer<typeof SeverityUpdateFilterSchema>
true satisfies AreEquivalent<SeverityUpdateFilter, components['schemas']['SeverityUpdateFilter']>

export const LocationSchema = z
  .object({
    file: z.string(),
    line: z.number(),
    snippet: z.string().optional(),
  })
  .describe('Location')
export type Location = z.infer<typeof LocationSchema>
true satisfies AreEquivalent<Location, components['schemas']['Location']>

export const EvidenceSchema = z
  .object({
    description: z.string(),
    locations: z.array(LocationSchema).optional(),
    type: z.string(),
  })
  .describe('Evidence')
export type Evidence = z.infer<typeof EvidenceSchema>
true satisfies AreEquivalent<Evidence, components['schemas']['Evidence']>

export const StrategyResponseSchema = z
  .enum(['provisional_ai', 'ai', 'deterministic', 'hybrid'])
  .describe('StrategyResponse')
export type StrategyResponse = z.infer<typeof StrategyResponseSchema>
true satisfies AreEquivalent<StrategyResponse, components['schemas']['StrategyResponse']>

export const ConfidenceSchema = z.enum(['high', 'medium', 'low', 'no-rating']).describe('Confidence')
export type Confidence = z.infer<typeof ConfidenceSchema>
true satisfies AreEquivalent<Confidence, components['schemas']['Confidence']>

export const CompletedTriageOutcomeSchema = z
  .object({
    analyzer_metadata: TriageAnalyzerMetadataResponseSchema.optional(),
    evidence: z.array(EvidenceSchema).optional(),
    suggested_severity: SeveritySchema.optional(),
    suggested_status: TriageSuggestedStatusResponseSchema.optional(),
    summary: z.string(),
    type: z.literal('completed-triage'),
  })
  .describe('CompletedTriageOutcome')
export type CompletedTriageOutcome = z.infer<typeof CompletedTriageOutcomeSchema>
true satisfies AreEquivalent<CompletedTriageOutcome, components['schemas']['CompletedTriageOutcome']>

export const FailedTriageOutcomeSchema = z
  .object({
    problem: z.object({
      code: z.string(),
      message: z.string(),
    }),
    type: z.literal('failed-triage'),
  })
  .describe('FailedTriageOutcome')
export type FailedTriageOutcome = z.infer<typeof FailedTriageOutcomeSchema>
true satisfies AreEquivalent<FailedTriageOutcome, components['schemas']['FailedTriageOutcome']>

export const NoRecommendationsTriageOutcomeSchema = z
  .object({
    type: z.literal('no-recommendations-triage'),
  })
  .describe('NoRecommendationsTriageOutcome')
export type NoRecommendationsTriageOutcome = z.infer<typeof NoRecommendationsTriageOutcomeSchema>
true satisfies AreEquivalent<NoRecommendationsTriageOutcome, components['schemas']['NoRecommendationsTriageOutcome']>

export const BlockedTriageOutcomeSchema = z
  .object({
    details: z.string().optional(),
    reason: z.string().optional(),
    type: z.literal('blocked-triage'),
  })
  .describe('BlockedTriageOutcome')
export type BlockedTriageOutcome = z.infer<typeof BlockedTriageOutcomeSchema>
true satisfies AreEquivalent<BlockedTriageOutcome, components['schemas']['BlockedTriageOutcome']>

export const ExcludedTriageOutcomeSchema = z
  .object({
    details: z.string().optional(),
    reason: z.string().optional(),
    type: z.literal('excluded-triage'),
  })
  .describe('ExcludedTriageOutcome')
export type ExcludedTriageOutcome = z.infer<typeof ExcludedTriageOutcomeSchema>
true satisfies AreEquivalent<ExcludedTriageOutcome, components['schemas']['ExcludedTriageOutcome']>

export const CancelledTriageOutcomeSchema = z
  .object({
    type: z.literal('cancelled-triage'),
  })
  .describe('CancelledTriageOutcome')
export type CancelledTriageOutcome = z.infer<typeof CancelledTriageOutcomeSchema>
true satisfies AreEquivalent<CancelledTriageOutcome, components['schemas']['CancelledTriageOutcome']>

export const TriageOutcomeSchema = z
  .discriminatedUnion('type', [
    CompletedTriageOutcomeSchema,
    FailedTriageOutcomeSchema,
    NoRecommendationsTriageOutcomeSchema,
    BlockedTriageOutcomeSchema,
    ExcludedTriageOutcomeSchema,
    CancelledTriageOutcomeSchema,
  ])
  .describe('TriageOutcome')
export type TriageOutcome = z.infer<typeof TriageOutcomeSchema>
true satisfies AreEquivalent<TriageOutcome, components['schemas']['TriageOutcome']>

export const CompletedFixOutcomeSchema = z
  .object({
    confidence: ConfidenceSchema.optional(),
    description: z.string().optional(),
    quality: QualityResponseSchema.optional(),
    strategy: StrategyResponseSchema,
    summary: z.string().optional(),
    type: z.literal('completed-fix'),
  })
  .describe('CompletedFixOutcome')
export type CompletedFixOutcome = z.infer<typeof CompletedFixOutcomeSchema>
true satisfies AreEquivalent<CompletedFixOutcome, components['schemas']['CompletedFixOutcome']>

export const FailedFixOutcomeSchema = z
  .object({
    problem: z.object({
      code: z.string(),
      message: z.string(),
    }),
    type: z.literal('failed-fix'),
  })
  .describe('FailedFixOutcome')
export type FailedFixOutcome = z.infer<typeof FailedFixOutcomeSchema>
true satisfies AreEquivalent<FailedFixOutcome, components['schemas']['FailedFixOutcome']>

export const NoRecommendationsFixOutcomeSchema = z
  .object({
    type: z.literal('no-recommendations-fix'),
  })
  .describe('NoRecommendationsFixOutcome')
export type NoRecommendationsFixOutcome = z.infer<typeof NoRecommendationsFixOutcomeSchema>
true satisfies AreEquivalent<NoRecommendationsFixOutcome, components['schemas']['NoRecommendationsFixOutcome']>

export const BlockedFixOutcomeSchema = z
  .object({
    details: z.string().optional(),
    reason: z.string().optional(),
    type: z.literal('blocked-fix'),
  })
  .describe('BlockedFixOutcome')
export type BlockedFixOutcome = z.infer<typeof BlockedFixOutcomeSchema>
true satisfies AreEquivalent<BlockedFixOutcome, components['schemas']['BlockedFixOutcome']>

// out-of-scope-fix was removed from the OpenAPI spec (use excluded-fix going forward),
// but kept here for backward compatibility with existing backend data
export const OutOfScopeFixOutcomeSchema = z
  .object({
    details: z.string().optional(),
    reason: z.string().optional(),
    type: z.literal('out-of-scope-fix'),
  })
  .describe('OutOfScopeFixOutcome')
export type OutOfScopeFixOutcome = z.infer<typeof OutOfScopeFixOutcomeSchema>

export const ExcludedFixOutcomeSchema = z
  .object({
    details: z.string().optional(),
    reason: z.string().optional(),
    type: z.literal('excluded-fix'),
  })
  .describe('ExcludedFixOutcome')
export type ExcludedFixOutcome = z.infer<typeof ExcludedFixOutcomeSchema>
true satisfies AreEquivalent<ExcludedFixOutcome, components['schemas']['ExcludedFixOutcome']>

export const CancelledFixOutcomeSchema = z
  .object({
    type: z.literal('cancelled-fix'),
  })
  .describe('CancelledFixOutcome')
export type CancelledFixOutcome = z.infer<typeof CancelledFixOutcomeSchema>
true satisfies AreEquivalent<CancelledFixOutcome, components['schemas']['CancelledFixOutcome']>

export const FixOutcomeSchema = z
  .discriminatedUnion('type', [
    CompletedFixOutcomeSchema,
    FailedFixOutcomeSchema,
    NoRecommendationsFixOutcomeSchema,
    BlockedFixOutcomeSchema,
    ExcludedFixOutcomeSchema,
    CancelledFixOutcomeSchema,
  ])
  .describe('FixOutcome')
export type FixOutcome = z.infer<typeof FixOutcomeSchema>
true satisfies AreEquivalent<FixOutcome, components['schemas']['FixOutcome']>

export const FindingResultLinksSchema = z
  .object({
    analysis: HALLinkSchema,
    finding: HALLinkSchema,
    self: HALLinkSchema,
  })
  .describe('FindingResultLinks')
export type FindingResultLinks = z.infer<typeof FindingResultLinksSchema>
true satisfies AreEquivalent<FindingResultLinks, components['schemas']['FindingResultLinks']>

const FindingResultResponseBaseSchema = z.object({
  _links: FindingResultLinksSchema,
  completed: InstantSchema.optional(),
  type: z.enum(['fix', 'triage', 'sca']),
})

export const TriageResultLinksSchema = z
  .object({
    analysis: HALLinkSchema,
    article: HALLinkSchema.optional(),
    'feedback-submissions': HALLinkSchema.optional(),
    finding: HALLinkSchema,
    report: HALLinkSchema.optional(),
    self: HALLinkSchema,
    verification: HALLinkSchema.optional(),
  })
  .describe('TriageResultLinks')
export type TriageResultLinks = z.infer<typeof TriageResultLinksSchema>
true satisfies AreEquivalent<TriageResultLinks, components['schemas']['TriageResultLinks']>

// #region Push schemas

export const PushStatusSchema = z.enum(['IN_PROGRESS', 'SUCCESS', 'FAILED'])
export type PushStatus = z.infer<typeof PushStatusSchema>
true satisfies AreEquivalent<PushStatus, components['schemas']['Status']>

export const PushResponseSchema = z.object({
  created_at: InstantSchema,
  error_reason: z.string().optional(),
  id: z.string(),
  status: PushStatusSchema,
  triage_result_ids: z.array(z.string()),
  updated_at: InstantSchema,
})
export type PushResponse = z.infer<typeof PushResponseSchema>
true satisfies AreEquivalent<PushResponse, components['schemas']['PushesPushResponse']>

/**
 * Named `ReasonCode` in the spec; renamed here so it reads as the reason a `SkippedEntry` carries.
 *
 * Declaration order is user-visible: `polaris-push-confirmation-modal.tsx` orders its paragraphs by
 * `.options`, and the spec declares these in the reverse order, so an OpenAPI re-sync that regenerates
 * this enum in spec order would silently reorder the dialog. Keep this order, or give the dialog an
 * explicit display order.
 */
export const SkippedEntryReasonSchema = z
  .enum(['scan-disconnected', 'no-correlation-id', 'no-completed-outcome'])
  .describe('SkippedEntryReason')
export type SkippedEntryReason = z.infer<typeof SkippedEntryReasonSchema>
true satisfies AreEquivalent<SkippedEntryReason, components['schemas']['ReasonCode']>

const SkippedEntrySchema = z.object({
  reason: SkippedEntryReasonSchema,
  triage_result_id: z.string(),
})
export type SkippedEntry = z.infer<typeof SkippedEntrySchema>
true satisfies AreEquivalent<SkippedEntry, components['schemas']['SkippedEntryResponse']>

/** Declaration order is user-visible in the confirmation dialog. @see SkippedEntryReasonSchema */
export const PolarisStatusStateSchema = z
  .enum(['match', 'conflict', 'not-found', 'lookup-failed', 'not-applicable'])
  .describe('PolarisStatusState')
export type PolarisStatusState = z.infer<typeof PolarisStatusStateSchema>
true satisfies AreEquivalent<PolarisStatusState, components['schemas']['PushesPolarisStatusState']>

export const PolarisStatusResponseSchema = z
  .object({
    acknowledged_at: InstantSchema.optional(),
    acknowledged_by: z.string().optional(),
    comment: z.string().optional(),
    current_status: z.string().optional(),
    error_reason: z.string().optional(),
    state: PolarisStatusStateSchema,
    triage_result_id: z.string(),
  })
  .describe('PolarisStatusResponse')
export type PolarisStatusResponse = z.infer<typeof PolarisStatusResponseSchema>
true satisfies AreEquivalent<PolarisStatusResponse, components['schemas']['PushesPolarisStatusResponse']>

export const PolarisStatusLookupResponseSchema = z
  .object({
    skipped: z.array(SkippedEntrySchema),
    statuses: z.array(PolarisStatusResponseSchema),
  })
  .describe('PolarisStatusLookupResponse')
export type PolarisStatusLookupResponse = z.infer<typeof PolarisStatusLookupResponseSchema>
true satisfies AreEquivalent<PolarisStatusLookupResponse, components['schemas']['PushesPolarisStatusLookupResponse']>

export const CreatePushRequestSchema = z.object({
  scan_id: z.string().optional(),
  triage_result_ids: z.array(z.string()).optional(),
})
export type CreatePushRequest = z.infer<typeof CreatePushRequestSchema>
true satisfies AreEquivalent<CreatePushRequest, components['schemas']['CreatePushRequest']>

export const CreatePushResponseSchema = z.object({
  pushes: z.array(PushResponseSchema),
  skipped: z.array(SkippedEntrySchema),
})
export type CreatePushResponse = z.infer<typeof CreatePushResponseSchema>
true satisfies AreEquivalent<CreatePushResponse, components['schemas']['PushesCreatePushResponse']>

export const CreateProjectRequestSchema = z.object({
  description: z.string().min(0).max(500).optional(),
  name: z.string().min(0).max(100),
})
export type CreateProjectRequest = z.infer<typeof CreateProjectRequestSchema>
true satisfies AreEquivalent<CreateProjectRequest, components['schemas']['CreateProjectRequest']>

export const GetPushesResponseSchema = z.object({
  pushes: z.array(PushResponseSchema),
})
export type GetPushesResponse = z.infer<typeof GetPushesResponseSchema>
true satisfies AreEquivalent<GetPushesResponse, components['schemas']['PushesGetPushesResponse']>

// #endregion

export const ResultsTriageResultResponseSchema = FindingResultResponseBaseSchema.extend({
  _links: TriageResultLinksSchema,
  id: z.string(),
  latest_push_state: PushResponseSchema.optional(),
  outcome: TriageOutcomeSchema.optional(),
  type: z.literal('triage'),
}).describe('ResultsTriageResultResponse')
export type ResultsTriageResultResponse = z.infer<typeof ResultsTriageResultResponseSchema>
true satisfies AreEquivalent<ResultsTriageResultResponse, components['schemas']['ResultsTriageResultResponse']>

export const FixResultLinksSchema = z
  .object({
    analysis: HALLinkSchema,
    changesets: HALLinkSchema,
    'feedback-submissions': HALLinkSchema.optional(),
    finding: HALLinkSchema,
    'latest-patch': HALLinkSchema,
    patches: HALLinkSchema,
    self: HALLinkSchema,
  })
  .describe('FixResultLinks')
export type FixResultLinks = z.infer<typeof FixResultLinksSchema>
true satisfies AreEquivalent<FixResultLinks, components['schemas']['FixResultLinks']>

export const PatchPaginatedResponseSchema = Paginated(PatchSchema, StandardPageNavigationLinksSchema).describe(
  'PatchPaginatedResponse'
)
export type PatchPaginatedResponse = z.infer<typeof PatchPaginatedResponseSchema>
true satisfies AreEquivalent<PatchPaginatedResponse, components['schemas']['PatchPaginatedResponse']>

export const FixResultEmbeddedSchema = z
  .object({
    'latest-patch': PatchPaginatedResponseSchema.optional(),
  })
  .describe('FixResultEmbedded')
export type FixResultEmbedded = z.infer<typeof FixResultEmbeddedSchema>
true satisfies AreEquivalent<FixResultEmbedded, components['schemas']['FixResultEmbedded']>

export const FixResultResponseSchema = FindingResultResponseBaseSchema.extend({
  _embedded: FixResultEmbeddedSchema.optional(),
  _links: FixResultLinksSchema,
  id: z.string(),
  outcome: FixOutcomeSchema.optional(),
  type: z.literal('fix'),
}).describe('FixResultResponse')
export type FixResultResponse = z.infer<typeof FixResultResponseSchema>
true satisfies AreEquivalent<FixResultResponse, components['schemas']['FixResultResponse']>

export const ScaResultLinksSchema = z
  .object({
    analysis: HALLinkSchema,
    article: HALLinkSchema.optional(),
    'feedback-submissions': HALLinkSchema.optional(),
    finding: HALLinkSchema,
    report: HALLinkSchema.optional(),
    self: HALLinkSchema,
  })
  .describe('ScaResultLinks')
export type ScaResultLinks = z.infer<typeof ScaResultLinksSchema>
true satisfies AreEquivalent<ScaResultLinks, components['schemas']['ScaResultLinks']>

const ScaDerivedMetricEvidenceResponseSchema = z
  .object({
    description: z.string(),
    file_path: z.string().optional(),
    line_end: z.number().int().optional(),
    line_start: z.number().int().optional(),
    source_quote: z.string().optional(),
    source_url: z.string().optional(),
    symbols: z.array(z.string()).optional(),
    type: z.string().optional(),
  })
  .describe('ScaDerivedMetricEvidenceResponse')
type ScaDerivedMetricEvidenceResponse = z.infer<typeof ScaDerivedMetricEvidenceResponseSchema>
true satisfies AreEquivalent<
  ScaDerivedMetricEvidenceResponse,
  components['schemas']['ScaDerivedMetricEvidenceResponse']
>

const ScaDerivedMetricResponseSchema = z
  .object({
    confidence: z.number().optional(),
    evidence: z.array(ScaDerivedMetricEvidenceResponseSchema).optional(),
    metric: z.string(),
    rationale: z.string().optional(),
    value: z.string(),
  })
  .describe('ScaDerivedMetricResponse')
type ScaDerivedMetricResponse = z.infer<typeof ScaDerivedMetricResponseSchema>
true satisfies AreEquivalent<ScaDerivedMetricResponse, components['schemas']['ScaDerivedMetricResponse']>

export const CompletedScaOutcomeSchema = z
  .object({
    base_cvss_vector: z.string().optional(),
    classification: ScaClassificationResponseSchema,
    contextual_cvss_rationale: z.string().optional(),
    contextual_cvss_vector: z.string().optional(),
    derived_metrics: z.array(ScaDerivedMetricResponseSchema).optional(),
    summary: z.string().optional(),
    type: z.literal('completed-sca'),
  })
  .describe('CompletedScaOutcome')
export type CompletedScaOutcome = z.infer<typeof CompletedScaOutcomeSchema>
true satisfies AreEquivalent<CompletedScaOutcome, components['schemas']['CompletedScaOutcome']>

export const FailedScaProblemDetailsSchema = z
  .object({
    detail: z.string(),
    title: z.string(),
  })
  .describe('FailedScaProblemDetails')
export type FailedScaProblemDetails = z.infer<typeof FailedScaProblemDetailsSchema>
true satisfies AreEquivalent<FailedScaProblemDetails, components['schemas']['FailedScaProblemDetails']>

export const FailedScaOutcomeSchema = z
  .object({
    problem: FailedScaProblemDetailsSchema,
    type: z.literal('failed-sca'),
  })
  .describe('FailedScaOutcome')
export type FailedScaOutcome = z.infer<typeof FailedScaOutcomeSchema>
true satisfies AreEquivalent<FailedScaOutcome, components['schemas']['FailedScaOutcome']>

export const BlockedScaOutcomeSchema = z
  .object({
    details: z.string().optional(),
    reason: z.string().optional(),
    type: z.literal('blocked-sca'),
  })
  .describe('BlockedScaOutcome')
export type BlockedScaOutcome = z.infer<typeof BlockedScaOutcomeSchema>
true satisfies AreEquivalent<BlockedScaOutcome, components['schemas']['BlockedScaOutcome']>

export const ExcludedScaOutcomeSchema = z
  .object({
    details: z.string().optional(),
    reason: z.string().optional(),
    type: z.literal('excluded-sca'),
  })
  .describe('ExcludedScaOutcome')
export type ExcludedScaOutcome = z.infer<typeof ExcludedScaOutcomeSchema>
true satisfies AreEquivalent<ExcludedScaOutcome, components['schemas']['ExcludedScaOutcome']>

export const CancelledScaOutcomeSchema = z
  .object({
    type: z.literal('cancelled-sca'),
  })
  .describe('CancelledScaOutcome')
export type CancelledScaOutcome = z.infer<typeof CancelledScaOutcomeSchema>
true satisfies AreEquivalent<CancelledScaOutcome, components['schemas']['CancelledScaOutcome']>

export const ScaOutcomeSchema = z
  .discriminatedUnion('type', [
    CompletedScaOutcomeSchema,
    FailedScaOutcomeSchema,
    BlockedScaOutcomeSchema,
    ExcludedScaOutcomeSchema,
    CancelledScaOutcomeSchema,
  ])
  .describe('ScaOutcome')
export type ScaOutcome = z.infer<typeof ScaOutcomeSchema>
true satisfies AreEquivalent<ScaOutcome, components['schemas']['ScaOutcome']>

export const ResultsScaResultResponseSchema = FindingResultResponseBaseSchema.extend({
  _links: ScaResultLinksSchema,
  id: z.string(),
  outcome: ScaOutcomeSchema.optional(),
  type: z.literal('sca'),
}).describe('ResultsScaResultResponse')
export type ResultsScaResultResponse = z.infer<typeof ResultsScaResultResponseSchema>
true satisfies AreEquivalent<ResultsScaResultResponse, components['schemas']['ResultsScaResultResponse']>

export const FindingResultResponseSchema = z
  .discriminatedUnion('type', [
    FixResultResponseSchema,
    ResultsTriageResultResponseSchema,
    ResultsScaResultResponseSchema,
  ])
  .describe('FindingResultResponse')
export type FindingResultResponse = z.infer<typeof FindingResultResponseSchema>
true satisfies AreEquivalent<FindingResultResponse, components['schemas']['FindingResultResponse']>

export const TriageStatusCountsAggregateSchema = z
  .object({
    completed: z.number().int(),
    failed: z.number().int(),
    in_progress: z.number().int(),
    no_recommendations: z.number().int(),
  })
  .describe('TriageStatusCountsAggregate')
export type TriageStatusCountsAggregate = z.infer<typeof TriageStatusCountsAggregateSchema>
true satisfies AreEquivalent<TriageStatusCountsAggregate, components['schemas']['ScansTriageStatusCounts']>

export const TriageOutcomeCountsSchema = z
  .object({
    blocked: z.number().int(),
    excluded: z.number().int(),
    false_positive: z.number().int(),
    inconclusive: z.number().int(),
    likely_true_positive: z.number().int(),
    not_triaged: z.number().int(),
    true_positive: z.number().int(),
    wont_fix: z.number().int(),
  })
  .describe('TriageOutcomeCounts')
export type TriageOutcomeCounts = z.infer<typeof TriageOutcomeCountsSchema>
true satisfies AreEquivalent<TriageOutcomeCounts, components['schemas']['TriageOutcomeCounts']>

export const TriageCountsSchema = z
  .object({
    outcome: TriageOutcomeCountsSchema,
    status: TriageStatusCountsAggregateSchema,
  })
  .describe('TriageCounts')
export type TriageCounts = z.infer<typeof TriageCountsSchema>
true satisfies AreEquivalent<TriageCounts, components['schemas']['TriageCounts']>

export const FixStatusCountsSchema = z
  .object({
    completed: z.number().int(),
    failed: z.number().int(),
    in_progress: z.number().int(),
    no_recommendations: z.number().int(),
  })
  .describe('FixStatusCounts')
export type FixStatusCounts = z.infer<typeof FixStatusCountsSchema>
true satisfies AreEquivalent<FixStatusCounts, components['schemas']['FixStatusCounts']>

export const FixOutcomeCountsSchema = z
  .object({
    blocked: z.number().int(),
    excluded: z.number().int(),
    has_fix: z.number().int(),
  })
  .describe('FixOutcomeCounts')
export type FixOutcomeCounts = z.infer<typeof FixOutcomeCountsSchema>
true satisfies AreEquivalent<FixOutcomeCounts, components['schemas']['FixOutcomeCounts']>

export const FixConfidenceCountsAggregateSchema = z
  .object({
    high: z.number().int(),
    low: z.number().int(),
    medium: z.number().int(),
    no_rating: z.number().int(),
  })
  .describe('FixConfidenceCountsAggregate')
export type FixConfidenceCountsAggregate = z.infer<typeof FixConfidenceCountsAggregateSchema>
true satisfies AreEquivalent<FixConfidenceCountsAggregate, components['schemas']['ScansFixConfidenceCounts']>

export const FixCountsSchema = z
  .object({
    confidence: FixConfidenceCountsAggregateSchema,
    outcome: FixOutcomeCountsSchema,
    status: FixStatusCountsSchema,
  })
  .describe('FixCounts')
export type FixCounts = z.infer<typeof FixCountsSchema>
true satisfies AreEquivalent<FixCounts, components['schemas']['FixCounts']>

export const FixesFilterSchema = z
  .object({
    minimum_fix_confidence: ConfidenceSchema.optional(),
  })
  .describe('FixesFilter')
export type FixesFilter = z.infer<typeof FixesFilterSchema>
true satisfies AreEquivalent<FixesFilter, components['schemas']['FixesFilter']>

export const CreatePatchWorkflowActionSchema = z.object({
  findings: FindingsFilterSchema.optional(),
  fixes: FixesFilterSchema.optional(),
  limit: WorkflowActionLimitSchema.optional(),
  type: z.literal('create-patch'),
})
export type CreatePatchWorkflowAction = z.infer<typeof CreatePatchWorkflowActionSchema>
true satisfies AreEquivalent<CreatePatchWorkflowAction, components['schemas']['CreatePatchWorkflowAction']>

export const ScaStatusCountsSchema = z
  .object({
    blocked: z.number().int(),
    completed: z.number().int(),
    excluded: z.number().int(),
    failed: z.number().int(),
    in_progress: z.number().int(),
  })
  .describe('ScaStatusCounts')
export type ScaStatusCounts = z.infer<typeof ScaStatusCountsSchema>
true satisfies AreEquivalent<ScaStatusCounts, components['schemas']['ScaStatusCounts']>

export const ScaStatusResponseSchema = z
  .enum(['completed', 'failed', 'blocked', 'excluded', 'not-analyzed'])
  .describe('ScaStatusResponse')
export type ScaStatusResponse = z.infer<typeof ScaStatusResponseSchema>
true satisfies AreEquivalent<ScaStatusResponse, components['schemas']['ScaStatusResponse']>

export const FindingEmbedParameterSchema = z
  .enum(['items.results.latest-patch', 'items.representative-results.latest-patch'])
  .describe('FindingEmbedParameter')
export type FindingEmbedParameter = z.infer<typeof FindingEmbedParameterSchema>
true satisfies AreEquivalent<FindingEmbedParameter, components['schemas']['FindingEmbedParameter']>

export const RepositoryEmbedParameterSchema = z
  .enum(['project-membership', 'project-membership.project'])
  .describe('RepositoryEmbedParameter')
export type RepositoryEmbedParameter = z.infer<typeof RepositoryEmbedParameterSchema>
true satisfies AreEquivalent<RepositoryEmbedParameter, components['schemas']['RepositoryEmbedParameter']>

export const ScanEmbedParameterSchema = z.enum(['repository.project-membership.project']).describe('ScanEmbedParameter')
export type ScanEmbedParameter = z.infer<typeof ScanEmbedParameterSchema>
true satisfies AreEquivalent<ScanEmbedParameter, components['schemas']['ScanEmbedParameter']>

export const ProjectsEmbedParameterSchema = z.enum(['items.repositories']).describe('ProjectsEmbedParameter')
export type ProjectsEmbedParameter = z.infer<typeof ProjectsEmbedParameterSchema>
true satisfies AreEquivalent<ProjectsEmbedParameter, components['schemas']['ProjectsEmbedParameter']>

export const ScaClassificationCountsAggregateSchema = z
  .object({
    exploitable: z.number().int(),
    inconclusive: z.number().int(),
    not_exploitable: z.number().int(),
  })
  .describe('ScaClassificationCountsAggregate')
export type ScaClassificationCountsAggregate = z.infer<typeof ScaClassificationCountsAggregateSchema>
true satisfies AreEquivalent<ScaClassificationCountsAggregate, components['schemas']['ScansScaClassificationCounts']>

export const ScaCountsSchema = z
  .object({
    classification: ScaClassificationCountsAggregateSchema,
    status: ScaStatusCountsSchema,
  })
  .describe('ScaCounts')
export type ScaCounts = z.infer<typeof ScaCountsSchema>
true satisfies AreEquivalent<ScaCounts, components['schemas']['ScaCounts']>

export const CompositeCountsSchema = z
  .object({
    no_fix_necessary: z.number().int(),
    ready_to_fix: z.number().int(),
  })
  .describe('CompositeCounts')
export type CompositeCounts = z.infer<typeof CompositeCountsSchema>
true satisfies AreEquivalent<CompositeCounts, components['schemas']['CompositeCounts']>

export const ScansFindingResultsCollectionLinksSchema = z
  .object({
    finding: HALLinkSchema,
    first: HALLinkSchema,
    next: HALLinkSchema.optional(),
    prev: HALLinkSchema.optional(),
    self: HALLinkSchema,
  })
  .describe('ScansFindingResultsCollectionLinks')
export type ScansFindingResultsCollectionLinks = z.infer<typeof ScansFindingResultsCollectionLinksSchema>
true satisfies AreEquivalent<
  ScansFindingResultsCollectionLinks,
  components['schemas']['ScansFindingResultsCollectionLinks']
>

export const FindingResultPaginatedResponseSchema = Paginated(
  FindingResultResponseSchema,
  ScansFindingResultsCollectionLinksSchema
).describe('FindingResultPaginatedResponse')
export type FindingResultPaginatedResponse = z.infer<typeof FindingResultPaginatedResponseSchema>
true satisfies AreEquivalent<FindingResultPaginatedResponse, components['schemas']['FindingResultPaginatedResponse']>

export const FindingEmbeddedSchema = z
  .object({
    'representative-results': FindingResultPaginatedResponseSchema.optional(),
    results: FindingResultPaginatedResponseSchema.optional(),
  })
  .describe('FindingEmbedded')
export type FindingEmbedded = z.infer<typeof FindingEmbeddedSchema>
true satisfies AreEquivalent<FindingEmbedded, components['schemas']['FindingEmbedded']>

export const FindingLinksSchema = z
  .object({
    'representative-results': HALLinkSchema,
    results: HALLinkSchema,
    self: HALLinkSchema,
  })
  .describe('FindingLinks')
export type FindingLinks = z.infer<typeof FindingLinksSchema>
true satisfies AreEquivalent<FindingLinks, components['schemas']['FindingLinks']>

export const FindingResponseSchema = z
  .object({
    _embedded: FindingEmbeddedSchema.optional(),
    _links: FindingLinksSchema,
    base_cvss: SeveritySchema.optional(),
    effective_cvss: SeveritySchema.optional(),
    html_url: z.string().optional(),
    id: z.string(),
    push_state: PushResponseSchema.nullish(),
    rule: z.string(),
    severity: SeveritySchema.optional(),
    title: z.string().optional(),
  })
  .describe('FindingResponse')
export type FindingResponse = z.infer<typeof FindingResponseSchema>
// @ts-expect-error OpenAPI YAML marks `push_state` nullable (`type: 'null'` alongside the $ref), but openapi-typescript's generated .d.ts drops the null variant for $ref'd fields, so `components['schemas']['FindingResponse']['push_state']` only shows the non-null type.
true satisfies AreEquivalent<FindingResponse, components['schemas']['FindingResponse']>

// TODO(ISS-5664): Remove once the API spec has a dedicated schema without _embedded https://app.devrev.ai/pixee/works/ISS-5664
export const FindingResponseWithoutEmbedSchema = FindingResponseSchema.omit({ _embedded: true }).describe(
  'FindingResponseWithoutEmbed'
)
export type FindingResponseWithoutEmbed = z.infer<typeof FindingResponseWithoutEmbedSchema>

export const FindingAggregateResponseSchema = Paginated(FindingResponseSchema, StandardPageNavigationLinksSchema)
  .extend({
    completed_analysis: z.number().int(),
    composite: CompositeCountsSchema,
    fix: FixCountsSchema,
    in_progress_analysis: z.number().int(),
    sca: ScaCountsSchema,
    total: z.number().int(),
    triage: TriageCountsSchema,
  })
  .describe('FindingAggregateResponse')
export type FindingAggregateResponse = z.infer<typeof FindingAggregateResponseSchema>
// @ts-expect-error Not yet in sync with spec (camelCase vs snake_case fields).
true satisfies AreEquivalent<FindingAggregateResponse, components['schemas']['FindingAggregateResponse']>

export const FindingAggregateResponseStatisticsSchema = FindingAggregateResponseSchema.pick({
  completed_analysis: true,
  composite: true,
  fix: true,
  in_progress_analysis: true,
  sca: true,
  total: true,
  triage: true,
}).describe('FindingAggregateResponseStatistics')
export type FindingAggregateResponseStatistics = z.infer<typeof FindingAggregateResponseStatisticsSchema>

export const RepositoryIntegrationsLinkSchema = z
  .object({
    integration: HALLinkSchema.optional(),
    self: HALLinkSchema,
  })
  .describe('RepositoryIntegrationsLink')
export type RepositoryIntegrationsLink = z.infer<typeof RepositoryIntegrationsLinkSchema>
true satisfies AreEquivalent<RepositoryIntegrationsLink, components['schemas']['RepositoryIntegrationsLink']>

const RepositoryIntegrationResponseBaseSchema = z.object({
  _links: RepositoryIntegrationsLinkSchema,
  id: z.string(),
})

export const CheckmarxRepositoryIntegrationSchema = RepositoryIntegrationResponseBaseSchema.extend({
  html_url: z.url(),
  project_id: z.string(),
  type: z.literal('checkmarx'),
}).describe('CheckmarxRepositoryIntegration')
export type CheckmarxRepositoryIntegration = z.infer<typeof CheckmarxRepositoryIntegrationSchema>
true satisfies AreEquivalent<
  CheckmarxRepositoryIntegration,
  components['schemas']['CheckmarxRepositoryIntegrationResponse']
>

export const SonarRepositoryIntegrationSchema = RepositoryIntegrationResponseBaseSchema.extend({
  created_at: z.string().optional(),
  html_url: z.url(),
  project_key: z.string(),
  provenance: SonarIntegrationProvenanceSchema.optional(),
  type: z.literal('sonar'),
  updated_at: z.string().optional(),
}).describe('SonarRepositoryIntegration')
export type SonarRepositoryIntegration = z.infer<typeof SonarRepositoryIntegrationSchema>
true satisfies AreEquivalent<SonarRepositoryIntegration, components['schemas']['SonarRepositoryIntegrationResponse']>

export const VeracodeRepositoryIntegrationSchema = RepositoryIntegrationResponseBaseSchema.extend({
  application_guid: z.string(),
  sandbox_guid: z.string().optional(),
  type: z.literal('veracode'),
}).describe('VeracodeRepositoryIntegration')
export type VeracodeRepositoryIntegration = z.infer<typeof VeracodeRepositoryIntegrationSchema>
true satisfies AreEquivalent<
  VeracodeRepositoryIntegration,
  components['schemas']['VeracodeRepositoryIntegrationResponse']
>

export const AppScanRepositoryIntegrationSchema = RepositoryIntegrationResponseBaseSchema.extend({
  application_id: z.string(),
  html_url: z.url(),
  type: z.literal('appscan'),
}).describe('AppScanRepositoryIntegration')
export type AppScanRepositoryIntegration = z.infer<typeof AppScanRepositoryIntegrationSchema>
true satisfies AreEquivalent<
  AppScanRepositoryIntegration,
  components['schemas']['AppScanRepositoryIntegrationResponse']
>

export const ArnicaRepositoryIntegrationSchema = RepositoryIntegrationResponseBaseSchema.extend({
  asset_key: z.string(),
  created_at: z.string(),
  provenance: ArnicaIntegrationProvenanceSchema,
  type: z.literal('arnica'),
  updated_at: z.string(),
}).describe('ArnicaRepositoryIntegration')
export type ArnicaRepositoryIntegration = z.infer<typeof ArnicaRepositoryIntegrationSchema>
true satisfies AreEquivalent<ArnicaRepositoryIntegration, components['schemas']['ArnicaRepositoryIntegrationResponse']>

export const DatadogRepositoryIntegrationSchema = RepositoryIntegrationResponseBaseSchema.extend({
  repository_id: z.string(),
  type: z.literal('datadog'),
}).describe('DatadogRepositoryIntegration')
export type DatadogRepositoryIntegration = z.infer<typeof DatadogRepositoryIntegrationSchema>
true satisfies AreEquivalent<
  DatadogRepositoryIntegration,
  components['schemas']['DatadogRepositoryIntegrationResponse']
>

const ProvenanceSchema = z.enum(['SARIF', 'USER_SUPPLIED']).describe('Provenance')
type Provenance = z.infer<typeof ProvenanceSchema>
true satisfies AreEquivalent<Provenance, components['schemas']['Provenance']>

export const PolarisRepositoryIntegrationSchema = RepositoryIntegrationResponseBaseSchema.extend({
  create_scans_on_ci_run: z.boolean(),
  created_at: InstantSchema,
  polaris_project_id: z.string(),
  provenance: ProvenanceSchema,
  type: z.literal('polaris'),
}).describe('PolarisRepositoryIntegration')
export type PolarisRepositoryIntegration = z.infer<typeof PolarisRepositoryIntegrationSchema>
true satisfies AreEquivalent<
  PolarisRepositoryIntegration,
  components['schemas']['PolarisRepositoryIntegrationResponse']
>

export const BlackDuckHubRepositoryIntegrationSchema = RepositoryIntegrationResponseBaseSchema.extend({
  created_at: InstantSchema,
  project_id: z.string(),
  type: z.literal('black-duck-hub'),
  version_id: z.string(),
}).describe('BlackDuckHubRepositoryIntegration')
export type BlackDuckHubRepositoryIntegration = z.infer<typeof BlackDuckHubRepositoryIntegrationSchema>
true satisfies AreEquivalent<
  BlackDuckHubRepositoryIntegration,
  components['schemas']['BlackDuckHubRepositoryIntegrationResponse']
>

const CheckmarxEnterpriseRepositoryIntegrationSchema = RepositoryIntegrationResponseBaseSchema.extend({
  created_at: InstantSchema,
  project_id: z.number().int(),
  type: z.literal('checkmarx-enterprise'),
}).describe('CheckmarxEnterpriseRepositoryIntegration')
export type CheckmarxEnterpriseRepositoryIntegration = z.infer<typeof CheckmarxEnterpriseRepositoryIntegrationSchema>
true satisfies AreEquivalent<
  CheckmarxEnterpriseRepositoryIntegration,
  components['schemas']['CheckmarxEnterpriseRepositoryIntegrationResponse']
>

export const RepositoryIntegrationSchema = z
  .discriminatedUnion('type', [
    SonarRepositoryIntegrationSchema,
    VeracodeRepositoryIntegrationSchema,
    CheckmarxRepositoryIntegrationSchema,
    AppScanRepositoryIntegrationSchema,
    ArnicaRepositoryIntegrationSchema,
    BlackDuckHubRepositoryIntegrationSchema,
    CheckmarxEnterpriseRepositoryIntegrationSchema,
    DatadogRepositoryIntegrationSchema,
    PolarisRepositoryIntegrationSchema,
  ])
  .describe('RepositoryIntegration')
export type RepositoryIntegration = z.infer<typeof RepositoryIntegrationSchema>
true satisfies AreEquivalent<RepositoryIntegration, components['schemas']['RepositoryIntegrationResponse']>

export const RepositoryIntegrationPaginatedResponseSchema = Paginated(
  RepositoryIntegrationSchema,
  StandardPageNavigationLinksSchema
).describe('RepositoryIntegrationPaginatedResponse')
export type RepositoryIntegrationPaginatedResponse = z.infer<typeof RepositoryIntegrationPaginatedResponseSchema>
true satisfies AreEquivalent<
  RepositoryIntegrationPaginatedResponse,
  components['schemas']['RepositoryIntegrationPaginatedResponse']
>

export const BitbucketWorkspaceSchema = z
  .object({
    _links: z.object({
      repositories: HALLinkSchema,
    }),
    name: z.string(),
    slug: z.string(),
  })
  .describe('BitbucketWorkspace')
export type BitbucketWorkspace = z.infer<typeof BitbucketWorkspaceSchema>
true satisfies AreEquivalent<BitbucketWorkspace, components['schemas']['BitbucketWorkspaceResponse']>

export const BitbucketWorkspacePaginatedResponseSchema = CursorPaginated(BitbucketWorkspaceSchema).describe(
  'BitbucketWorkspacePaginatedResponse'
)
export type BitbucketWorkspacePaginatedResponse = z.infer<typeof BitbucketWorkspacePaginatedResponseSchema>
true satisfies AreEquivalent<
  BitbucketWorkspacePaginatedResponse,
  components['schemas']['BitbucketWorkspacePaginatedResponse']
>

export const IntegrationsBitbucketRepositoryResponseSchema = z
  .object({
    name: z.string(),
    pixee_id: z.string().optional(),
    slug: z.string(),
    workspace: z.string(),
  })
  .describe('IntegrationsBitbucketRepositoryResponse')
export type IntegrationsBitbucketRepositoryResponse = z.infer<typeof IntegrationsBitbucketRepositoryResponseSchema>
true satisfies AreEquivalent<
  IntegrationsBitbucketRepositoryResponse,
  components['schemas']['IntegrationsBitbucketRepositoryResponse']
>

export const BitbucketRepositoryPaginatedResponseSchema = CursorPaginated(
  IntegrationsBitbucketRepositoryResponseSchema
).describe('BitbucketRepositoryPaginatedResponse')
export type BitbucketRepositoryPaginatedResponse = z.infer<typeof BitbucketRepositoryPaginatedResponseSchema>
true satisfies AreEquivalent<
  BitbucketRepositoryPaginatedResponse,
  components['schemas']['BitbucketRepositoryPaginatedResponse']
>

export const VerificationResultResponseSchema = z
  .enum(['likely-exploitable', 'not-exploitable', 'partially-exploitable', 'analysis-unavailable'])
  .describe('VerificationResultResponse')
export type VerificationResultResponse = z.infer<typeof VerificationResultResponseSchema>
true satisfies AreEquivalent<VerificationResultResponse, components['schemas']['VerificationResultResponse']>

export const ResultsVerificationFileResponseSchema = z
  .object({
    content: z.string(),
    filename: z.string(),
    title: z.string(),
  })
  .describe('ResultsVerificationFileResponse')
export type ResultsVerificationFileResponse = z.infer<typeof ResultsVerificationFileResponseSchema>
true satisfies AreEquivalent<ResultsVerificationFileResponse, components['schemas']['ResultsVerificationFileResponse']>

export const ResultsVerificationHeadlineResponseSchema = z
  .object({
    description: z.string(),
    title: z.string(),
  })
  .describe('ResultsVerificationHeadlineResponse')
export type ResultsVerificationHeadlineResponse = z.infer<typeof ResultsVerificationHeadlineResponseSchema>
true satisfies AreEquivalent<
  ResultsVerificationHeadlineResponse,
  components['schemas']['ResultsVerificationHeadlineResponse']
>

export const ResultsVerificationPayloadResultsResponseSchema = z
  .object({
    columns: z.array(z.string()),
    rows: z.array(z.record(z.string(), z.string())),
  })
  .describe('ResultsVerificationPayloadResultsResponse')
export type ResultsVerificationPayloadResultsResponse = z.infer<typeof ResultsVerificationPayloadResultsResponseSchema>
true satisfies AreEquivalent<
  ResultsVerificationPayloadResultsResponse,
  components['schemas']['ResultsVerificationPayloadResultsResponse']
>

export const ResultsVerificationResponseSchema = z
  .object({
    body: z.string(),
    code_blocks: z.array(ResultsVerificationFileResponseSchema),
    headlines: z.array(ResultsVerificationHeadlineResponseSchema),
    payload_results: ResultsVerificationPayloadResultsResponseSchema.optional(),
    result: VerificationResultResponseSchema,
  })
  .describe('ResultsVerificationResponse')
export type ResultsVerificationResponse = z.infer<typeof ResultsVerificationResponseSchema>
true satisfies AreEquivalent<ResultsVerificationResponse, components['schemas']['ResultsVerificationResponse']>

export const OrganizationPreferencesLinksSchema = z
  .object({
    self: HALLinkSchema,
  })
  .describe('OrganizationPreferencesLinks')
export type OrganizationPreferencesLinks = z.infer<typeof OrganizationPreferencesLinksSchema>
true satisfies AreEquivalent<OrganizationPreferencesLinks, components['schemas']['OrganizationPreferencesLinks']>

export const OrganizationPreferencesResponseSchema = z
  .object({
    _links: OrganizationPreferencesLinksSchema,
    content: z.string(),
    org_id: z.string(),
    updated_at: z.string(),
    updated_by: z.string(),
  })
  .describe('OrganizationPreferencesResponse')
export type OrganizationPreferencesResponse = z.infer<typeof OrganizationPreferencesResponseSchema>
true satisfies AreEquivalent<OrganizationPreferencesResponse, components['schemas']['OrganizationPreferencesResponse']>

export const UpdateOrganizationPreferencesRequestSchema = z
  .object({
    content: z.string().max(250_000),
  })
  .describe('UpdateOrganizationPreferencesRequest')
export type UpdateOrganizationPreferencesRequest = z.infer<typeof UpdateOrganizationPreferencesRequestSchema>
true satisfies AreEquivalent<
  UpdateOrganizationPreferencesRequest,
  components['schemas']['UpdateOrganizationPreferencesRequest']
>

// #endregion

// #region Request schemas

const WorkflowExecutionPolicySchema = z.enum(['execute', 'prevent-execution']).describe('WorkflowExecutionPolicy')
type WorkflowExecutionPolicy = z.infer<typeof WorkflowExecutionPolicySchema>
true satisfies AreEquivalent<WorkflowExecutionPolicy, components['schemas']['WorkflowExecutionPolicy']>

const BaseSchema = z
  .object({
    branch: z.string(),
    sha: z.string().optional(),
  })
  .describe('Base')
type Base = z.infer<typeof BaseSchema>
true satisfies AreEquivalent<Base, components['schemas']['Base']>

export const CreateScanRequestMetadataSchema = z.object({
  base: BaseSchema.optional(),
  branch: z.string().min(1).optional(),
  gitlab_pipeline_id: z.number().int().optional(),
  integration_id: z.string().max(64).optional(),
  pr_number: z.number().int().optional(),
  sha: z
    .string()
    .regex(/^[a-fA-F0-9]{40}$/)
    .optional(),
  tool: ToolSchema,
  workflow_execution_policy: WorkflowExecutionPolicySchema,
})
type CreateScanRequestMetadata = z.infer<typeof CreateScanRequestMetadataSchema>
true satisfies AreEquivalent<CreateScanRequestMetadata, components['schemas']['CreateScanRequestMetadata']>

export const CreateSbomRequestMetadataSchema = z
  .object({
    branch: z.string().min(1).optional(),
    sha: z.string(),
    tool: SbomToolSchema,
  })
  .describe('CreateSbomRequestMetadata')
export type CreateSbomRequestMetadata = z.infer<typeof CreateSbomRequestMetadataSchema>
true satisfies AreEquivalent<CreateSbomRequestMetadata, components['schemas']['CreateSbomRequestMetadata']>

export const PatchesCreatePatchRequestSchema = z
  .object({
    findings: FindingsSelectionSchema.optional(),
    fix_result_ids: z.array(z.string()).min(1).optional(),
  })
  .describe('PatchesCreatePatchRequest')
export type PatchesCreatePatchRequest = z.infer<typeof PatchesCreatePatchRequestSchema>
true satisfies AreEquivalent<PatchesCreatePatchRequest, components['schemas']['PatchesCreatePatchRequest']>

export const CreatePatchPayloadSchema = z.object({
  patch_id: z
    .string()
    .min(1)
    .regex(/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/),
})
export type CreatePatchPayload = z.infer<typeof CreatePatchPayloadSchema>
true satisfies AreEquivalent<CreatePatchPayload, components['schemas']['CreatePatchPayload']>

export const CreateWorkflowRequestSchema = z
  .object({
    action: WorkflowActionSchema,
    event: z.discriminatedUnion('type', [
      z.object({
        branch: z.string().optional(),
        cadence: ScheduleWorkflowCadenceSchema,
        start: z.iso.datetime().optional(),
        type: z.literal('schedule'),
      }),
      z.object({
        branch: z.string().optional(),
        type: z.literal('new-scan'),
      }),
      z.object({
        source_branch: z.string().optional(),
        target_branch: z.string().optional(),
        type: z.literal('pull-request-scan'),
      }),
    ]),
    name: z.string().min(1).optional(),
    tool: ToolSchema,
  })
  .describe('CreateWorkflowRequest')
export type CreateWorkflowRequest = z.infer<typeof CreateWorkflowRequestSchema>
// @ts-expect-error Spec uses oneOf union without an explicit type discriminator; Zod uses a discriminatedUnion with one.
true satisfies AreEquivalent<CreateWorkflowRequest, components['schemas']['CreateWorkflowRequest']>

export const UpdateScheduleWorkflowEventSchema = z
  .object({
    branch: z.string().optional(),
    cadence: ScheduleWorkflowCadenceSchema.optional(),
    start: z.string().nullish(),
    type: z.literal('schedule'),
  })
  .describe('UpdateScheduleWorkflowEvent')
export type UpdateScheduleWorkflowEvent = z.infer<typeof UpdateScheduleWorkflowEventSchema>
true satisfies AreEquivalent<UpdateScheduleWorkflowEvent, components['schemas']['UpdateScheduleWorkflowEvent']>

export const UpdateNewScanWorkflowEventSchema = z
  .object({
    branch: z.string().optional(),
    type: z.literal('new-scan'),
  })
  .describe('UpdateNewScanWorkflowEvent')
export type UpdateNewScanWorkflowEvent = z.infer<typeof UpdateNewScanWorkflowEventSchema>
true satisfies AreEquivalent<UpdateNewScanWorkflowEvent, components['schemas']['UpdateNewScanWorkflowEvent']>

export const UpdatePullRequestScanWorkflowEventSchema = z
  .object({
    source_branch: z.string().nullish(),
    target_branch: z.string().nullish(),
    type: z.literal('pull-request-scan'),
  })
  .describe('UpdatePullRequestScanWorkflowEvent')
export type UpdatePullRequestScanWorkflowEvent = z.infer<typeof UpdatePullRequestScanWorkflowEventSchema>
true satisfies AreEquivalent<
  UpdatePullRequestScanWorkflowEvent,
  components['schemas']['UpdatePullRequestScanWorkflowEvent']
>

export const UpdateNoneWorkflowActionSchema = z
  .object({
    type: z.literal('none'),
  })
  .describe('UpdateNoneWorkflowAction')
export type UpdateNoneWorkflowAction = z.infer<typeof UpdateNoneWorkflowActionSchema>
true satisfies AreEquivalent<UpdateNoneWorkflowAction, components['schemas']['UpdateNoneWorkflowAction']>

export const UpdateCreatePatchWorkflowActionSchema = z
  .object({
    findings: z
      .object({
        include_severity_labels: z.array(z.string()).nullable().optional(),
        maximum_severity_score: z.number().int().nullable().optional(),
        minimum_severity_score: z.number().int().nullable().optional(),
      })
      .nullable()
      .optional(),
    fixes: z
      .object({
        minimum_fix_confidence: ConfidenceSchema.nullable().optional(),
      })
      .nullable()
      .optional(),
    limit: z
      .union([z.object({ max: z.number().int(), type: z.literal('finding-count') }), z.literal('INSTANCE')])
      .nullable()
      .optional(),
    type: z.literal('create-patch'),
  })
  .describe('UpdateCreatePatchWorkflowAction')
export type UpdateCreatePatchWorkflowAction = z.infer<typeof UpdateCreatePatchWorkflowActionSchema>
// @ts-expect-error Nullable oneOf in OpenAPI produces collapsed intersection type for limit field.
true satisfies AreEquivalent<UpdateCreatePatchWorkflowAction, components['schemas']['UpdateCreatePatchWorkflowAction']>

export const UpdateWorkflowActionSchema = z
  .discriminatedUnion('type', [UpdateCreatePatchWorkflowActionSchema, UpdateNoneWorkflowActionSchema])
  .describe('UpdateWorkflowAction')
export type UpdateWorkflowAction = z.infer<typeof UpdateWorkflowActionSchema>
// @ts-expect-error Nullable oneOf in OpenAPI produces collapsed intersection type for limit field.
true satisfies AreEquivalent<UpdateWorkflowAction, components['schemas']['UpdateWorkflowAction']>

export const UpdateWorkflowEventSchema = z
  .discriminatedUnion('type', [
    UpdateScheduleWorkflowEventSchema,
    UpdateNewScanWorkflowEventSchema,
    UpdatePullRequestScanWorkflowEventSchema,
  ])
  .describe('UpdateWorkflowEvent')
export type UpdateWorkflowEvent = z.infer<typeof UpdateWorkflowEventSchema>
true satisfies AreEquivalent<UpdateWorkflowEvent, NonNullable<components['schemas']['UpdateWorkflowRequest']['event']>>

export const UpdateWorkflowRequestSchema = z
  .object({
    action: UpdateWorkflowActionSchema.optional(),
    enabled: z.boolean().optional(),
    event: UpdateWorkflowEventSchema.optional(),
    name: z.string().optional(),
  })
  .describe('UpdateWorkflowRequest')
export type UpdateWorkflowRequest = z.infer<typeof UpdateWorkflowRequestSchema>
// @ts-expect-error Nullable oneOf in OpenAPI produces collapsed intersection type for limit field in action variant.
true satisfies AreEquivalent<UpdateWorkflowRequest, components['schemas']['UpdateWorkflowRequest']>

export const AddGitRepositoryRequestSchema = z
  .object({
    credentials: z.object({
      password: z.string(),
      username: z.string(),
    }),
    name: z.string(),
    type: z.literal('git'),
    url: z.url(),
  })
  .describe('AddGitRepositoryRequest')
export type AddGitRepositoryRequest = z.infer<typeof AddGitRepositoryRequestSchema>
true satisfies AreEquivalent<AddGitRepositoryRequest, components['schemas']['CreateGitRepositoryRequest']>

export const AddAzureRepositoryRequestSchema = z
  .object({
    integration_id: z.string().optional(),
    name: z.string(),
    organization: z.string(),
    project: z.string(),
    type: z.literal('azure'),
  })
  .describe('AddAzureRepositoryRequest')
export type AddAzureRepositoryRequest = z.infer<typeof AddAzureRepositoryRequestSchema>
true satisfies AreEquivalent<AddAzureRepositoryRequest, components['schemas']['CreateAzureRepositoryRequest']>

export const AddGitLabRepositoryRequestSchema = z
  .object({
    integration_id: z.string().optional(),
    name: z.string(),
    namespace: z.string(),
    type: z.literal('gitlab'),
  })
  .describe('AddGitLabRepositoryRequest')
export type AddGitLabRepositoryRequest = z.infer<typeof AddGitLabRepositoryRequestSchema>
true satisfies AreEquivalent<AddGitLabRepositoryRequest, components['schemas']['CreateGitLabRepositoryRequest']>

export const AddBitbucketRepositoryRequestSchema = z
  .object({
    integration_id: z.string().optional(),
    slug: z.string(),
    type: z.literal('bitbucket'),
    workspace: z.string(),
  })
  .describe('AddBitbucketRepositoryRequest')
export type AddBitbucketRepositoryRequest = z.infer<typeof AddBitbucketRepositoryRequestSchema>
true satisfies AreEquivalent<AddBitbucketRepositoryRequest, components['schemas']['CreateBitbucketRepositoryRequest']>

export const AddRepositoryRequestSchema = z
  .discriminatedUnion('type', [
    AddGitRepositoryRequestSchema,
    AddAzureRepositoryRequestSchema,
    AddGitLabRepositoryRequestSchema,
    AddBitbucketRepositoryRequestSchema,
  ])
  .describe('AddRepositoryRequest')
export type AddRepositoryRequest = z.infer<typeof AddRepositoryRequestSchema>
true satisfies AreEquivalent<AddRepositoryRequest, components['schemas']['CreateRepositoryRequest']>

export const GitRemoteUnreachableProblemSchema = z
  .object({
    detail: z.string(),
    status: z.number().int(),
    title: z.string(),
  })
  .describe('GitRemoteUnreachableProblem')
export type GitRemoteUnreachableProblem = z.infer<typeof GitRemoteUnreachableProblemSchema>
true satisfies AreEquivalent<GitRemoteUnreachableProblem, components['schemas']['GitRemoteUnreachableProblem']>

export const CreateSonarRepositoryIntegrationRequestSchema = z
  .object({
    integration_id: z.string().optional(),
    project_key: z.string(),
    type: z.literal('sonar'),
  })
  .describe('CreateSonarRepositoryIntegrationRequest')
type CreateSonarRepositoryIntegrationRequest = z.infer<typeof CreateSonarRepositoryIntegrationRequestSchema>
true satisfies AreEquivalent<
  CreateSonarRepositoryIntegrationRequest,
  components['schemas']['CreateSonarRepositoryIntegrationRequest']
>

export const CreateVeracodeRepositoryIntegrationRequestSchema = z
  .object({
    application_guid: z.string(),
    integration_id: z.string().optional(),
    sandbox_guid: z.string().optional(),
    type: z.literal('veracode'),
  })
  .describe('CreateVeracodeRepositoryIntegrationRequest')
type CreateVeracodeRepositoryIntegrationRequest = z.infer<typeof CreateVeracodeRepositoryIntegrationRequestSchema>
true satisfies AreEquivalent<
  CreateVeracodeRepositoryIntegrationRequest,
  components['schemas']['CreateVeracodeRepositoryIntegrationRequest']
>

export const CreateCheckmarxRepositoryIntegrationRequestSchema = z
  .object({
    integration_id: z.string().optional(),
    project_id: z.string(),
    type: z.literal('checkmarx'),
  })
  .describe('CreateCheckmarxRepositoryIntegrationRequest')
type CreateCheckmarxRepositoryIntegrationRequest = z.infer<typeof CreateCheckmarxRepositoryIntegrationRequestSchema>
true satisfies AreEquivalent<
  CreateCheckmarxRepositoryIntegrationRequest,
  components['schemas']['CreateCheckmarxRepositoryIntegrationRequest']
>

export const CreateAppScanRepositoryIntegrationRequestSchema = z
  .object({
    application_id: z.string(),
    integration_id: z.string().optional(),
    type: z.literal('appscan'),
  })
  .describe('CreateAppScanRepositoryIntegrationRequest')
type CreateAppScanRepositoryIntegrationRequest = z.infer<typeof CreateAppScanRepositoryIntegrationRequestSchema>
true satisfies AreEquivalent<
  CreateAppScanRepositoryIntegrationRequest,
  components['schemas']['CreateAppScanRepositoryIntegrationRequest']
>

export const CreateArnicaRepositoryIntegrationRequestSchema = z
  .object({
    asset_key: z.string().min(1),
    integration_id: z.string().optional(),
    type: z.literal('arnica'),
  })
  .describe('CreateArnicaRepositoryIntegrationRequest')
type CreateArnicaRepositoryIntegrationRequest = z.infer<typeof CreateArnicaRepositoryIntegrationRequestSchema>
true satisfies AreEquivalent<
  CreateArnicaRepositoryIntegrationRequest,
  components['schemas']['CreateArnicaRepositoryIntegrationRequest']
>

export const CreateBlackDuckHubRepositoryIntegrationRequestSchema = z
  .object({
    integration_id: z.string().min(1),
    project_id: z.string().min(1),
    type: z.literal('black-duck-hub'),
    version_id: z.string().min(1),
  })
  .describe('CreateBlackDuckHubRepositoryIntegrationRequest')
type CreateBlackDuckHubRepositoryIntegrationRequest = z.infer<
  typeof CreateBlackDuckHubRepositoryIntegrationRequestSchema
>
true satisfies AreEquivalent<
  CreateBlackDuckHubRepositoryIntegrationRequest,
  components['schemas']['CreateBlackDuckHubRepositoryIntegrationRequest']
>

const CreateCheckmarxEnterpriseRepositoryIntegrationRequestSchema = z
  .object({
    integration_id: z.string().min(1),
    project_id: z.number().int().min(1),
    type: z.literal('checkmarx-enterprise'),
  })
  .describe('CreateCheckmarxEnterpriseRepositoryIntegrationRequest')
type CreateCheckmarxEnterpriseRepositoryIntegrationRequest = z.infer<
  typeof CreateCheckmarxEnterpriseRepositoryIntegrationRequestSchema
>
true satisfies AreEquivalent<
  CreateCheckmarxEnterpriseRepositoryIntegrationRequest,
  components['schemas']['CreateCheckmarxEnterpriseRepositoryIntegrationRequest']
>

export const CreateDatadogRepositoryIntegrationRequestSchema = z
  .object({
    integration_id: z.string().optional(),
    repository_id: z.string(),
    type: z.literal('datadog'),
  })
  .describe('CreateDatadogRepositoryIntegrationRequest')
type CreateDatadogRepositoryIntegrationRequest = z.infer<typeof CreateDatadogRepositoryIntegrationRequestSchema>
true satisfies AreEquivalent<
  CreateDatadogRepositoryIntegrationRequest,
  components['schemas']['CreateDatadogRepositoryIntegrationRequest']
>

export const CreatePolarisRepositoryIntegrationRequestSchema = z
  .object({
    create_scans_on_ci_run: z.boolean().optional(),
    integration_id: z.string().optional(),
    polaris_project_id: z.string().min(1),
    type: z.literal('polaris'),
  })
  .describe('CreatePolarisRepositoryIntegrationRequest')
type CreatePolarisRepositoryIntegrationRequest = z.infer<typeof CreatePolarisRepositoryIntegrationRequestSchema>
true satisfies AreEquivalent<
  CreatePolarisRepositoryIntegrationRequest,
  components['schemas']['CreatePolarisRepositoryIntegrationRequest']
>

export const CreateRepositoryIntegrationRequestSchema = z
  .discriminatedUnion('type', [
    CreateSonarRepositoryIntegrationRequestSchema,
    CreateVeracodeRepositoryIntegrationRequestSchema,
    CreateCheckmarxRepositoryIntegrationRequestSchema,
    CreateAppScanRepositoryIntegrationRequestSchema,
    CreateArnicaRepositoryIntegrationRequestSchema,
    CreateBlackDuckHubRepositoryIntegrationRequestSchema,
    CreateCheckmarxEnterpriseRepositoryIntegrationRequestSchema,
    CreateDatadogRepositoryIntegrationRequestSchema,
    CreatePolarisRepositoryIntegrationRequestSchema,
  ])
  .describe('CreateRepositoryIntegrationRequest')
export type CreateRepositoryIntegrationRequest = z.infer<typeof CreateRepositoryIntegrationRequestSchema>
true satisfies AreEquivalent<
  CreateRepositoryIntegrationRequest,
  components['schemas']['CreateRepositoryIntegrationRequest']
>

export const UpdatePolarisRepositoryIntegrationRequestSchema = z
  .object({
    create_scans_on_ci_run: z.boolean().optional(),
    type: z.literal('polaris'),
  })
  .describe('UpdatePolarisRepositoryIntegrationRequest')
type UpdatePolarisRepositoryIntegrationRequest = z.infer<typeof UpdatePolarisRepositoryIntegrationRequestSchema>
true satisfies AreEquivalent<
  UpdatePolarisRepositoryIntegrationRequest,
  components['schemas']['UpdatePolarisRepositoryIntegrationRequest']
>

export const UpdateRepositoryIntegrationRequestSchema = z
  .discriminatedUnion('type', [UpdatePolarisRepositoryIntegrationRequestSchema])
  .describe('UpdateRepositoryIntegrationRequest')
export type UpdateRepositoryIntegrationRequest = z.infer<typeof UpdateRepositoryIntegrationRequestSchema>
true satisfies AreEquivalent<
  UpdateRepositoryIntegrationRequest,
  components['schemas']['UpdateRepositoryIntegrationRequest']
>

// #endregion

// #region Feedback schemas (ISS-6528)

export const FeedbackRatingSchema = z.enum(['positive', 'negative']).describe('FeedbackRating')
export type FeedbackRating = z.infer<typeof FeedbackRatingSchema>
true satisfies AreEquivalent<FeedbackRating, components['schemas']['FeedbackRating']>

export const FeedbackScopeSchema = z.enum(['project', 'repository']).describe('FeedbackScope')
export type FeedbackScope = z.infer<typeof FeedbackScopeSchema>
true satisfies AreEquivalent<FeedbackScope, components['schemas']['FeedbackScope']>

export const FeedbackSubmissionResponseSchema = z
  .object({
    _links: FeedbackSubmissionLinksSchema.optional(),
    createdAt: InstantSchema.optional(),
    id: z.string().optional(),
    rating: FeedbackRatingSchema.optional(),
    scope: FeedbackScopeSchema,
    text: z.string().optional(),
    username: z.string().optional(),
  })
  .describe('FeedbackSubmissionResponse')
export type FeedbackSubmissionResponse = z.infer<typeof FeedbackSubmissionResponseSchema>
true satisfies AreEquivalent<FeedbackSubmissionResponse, components['schemas']['FeedbackSubmissionResponse']>

export const FeedbackSubmissionPaginatedResponseSchema = Paginated(
  FeedbackSubmissionResponseSchema,
  StandardPageNavigationLinksSchema
).describe('FeedbackSubmissionPaginatedResponse')
export type FeedbackSubmissionPaginatedResponse = z.infer<typeof FeedbackSubmissionPaginatedResponseSchema>
true satisfies AreEquivalent<
  FeedbackSubmissionPaginatedResponse,
  components['schemas']['FeedbackSubmissionPaginatedResponse']
>

export const CreateFeedbackSubmissionRequestSchema = z
  .object({
    project_id: z.string().optional(),
    rating: FeedbackRatingSchema,
    scope: FeedbackScopeSchema.optional(),
    text: z.string().min(0).max(1750).optional(),
  })
  .describe('CreateFeedbackSubmissionRequest')
export type CreateFeedbackSubmissionRequest = z.infer<typeof CreateFeedbackSubmissionRequestSchema>
true satisfies AreEquivalent<CreateFeedbackSubmissionRequest, components['schemas']['CreateFeedbackSubmissionRequest']>

export const FeedbackSubmissionsDeleteDisabledProblemResponseSchema = z
  .object({
    detail: z.string(),
    status: z.number().int(),
    title: z.string(),
  })
  .describe('FeedbackSubmissionsDeleteDisabledProblemResponse')
export type FeedbackSubmissionsDeleteDisabledProblemResponse = z.infer<
  typeof FeedbackSubmissionsDeleteDisabledProblemResponseSchema
>
true satisfies AreEquivalent<
  FeedbackSubmissionsDeleteDisabledProblemResponse,
  components['schemas']['FeedbackSubmissionsDeleteDisabledProblemResponse']
>

export const CodeFetchFailedProblemSchema = z
  .object({
    detail: z.string(),
    status: z.number().int(),
    title: z.string(),
  })
  .describe('CodeFetchFailedProblem')
export type CodeFetchFailedProblem = z.infer<typeof CodeFetchFailedProblemSchema>
true satisfies AreEquivalent<CodeFetchFailedProblem, components['schemas']['CodeFetchFailedProblem']>

export const CodingGuidelinesLinksSchema = z
  .object({
    content: HALLinkSchema,
    events: HALLinkSchema,
    self: HALLinkSchema,
  })
  .describe('CodingGuidelinesLinks')
export type CodingGuidelinesLinks = z.infer<typeof CodingGuidelinesLinksSchema>
true satisfies AreEquivalent<CodingGuidelinesLinks, components['schemas']['CodingGuidelinesLinks']>

export const CodingGuidelinesResponseSchema = z
  .object({
    _links: CodingGuidelinesLinksSchema.optional(),
    content_type: z.string().optional(),
    display_name: z.string().optional(),
    filename: z.string().optional(),
    id: z.string().optional(),
    size: z.number().int().optional(),
    status: z.string().optional(),
    updated_at: InstantSchema.optional(),
    uploaded_at: InstantSchema.optional(),
    uploaded_by: z.string().optional(),
  })
  .describe('CodingGuidelinesResponse')
export type CodingGuidelinesResponse = z.infer<typeof CodingGuidelinesResponseSchema>
true satisfies AreEquivalent<CodingGuidelinesResponse, components['schemas']['CodingGuidelinesResponse']>

export const UpdateCodingGuidelinesRequestSchema = z
  .object({ display_name: z.string().regex(/\S/).max(100) })
  .describe('UpdateCodingGuidelinesRequest')
export type UpdateCodingGuidelinesRequest = z.infer<typeof UpdateCodingGuidelinesRequestSchema>
true satisfies AreEquivalent<UpdateCodingGuidelinesRequest, components['schemas']['UpdateCodingGuidelinesRequest']>

export const FeedbackSubmissionsDeleteResponseSchema = z.object({
  deleted_feedback_submissions: z.number().int(),
  deleted_judgments: z.number().int(),
  deleted_memories: z.number().int(),
})
export type FeedbackSubmissionsDeleteResponse = z.infer<typeof FeedbackSubmissionsDeleteResponseSchema>
true satisfies AreEquivalent<
  FeedbackSubmissionsDeleteResponse,
  components['schemas']['FeedbackSubmissionsDeleteResponse']
>

// #endregion

// #region Support Bundle schemas (ISS-7005, Pixee Enterprise Server)
//
// TODO(ISS-7598): SupportBundleResponse and SupportBundleAlreadyRunningProblemResponse are served by
// pixee-platform's REST API and should be defined in its OpenAPI spec, not hand-written here.
// TODO(ISS-7599): once ISS-7598 lands, re-sync these schemas against components['schemas']
// (satisfies z.ZodType + AreEquivalent).

const SupportBundleLinksSchema = z
  .object({
    events: HALLinkSchema,
    self: HALLinkSchema,
  })
  .describe('SupportBundleLinks')

export const SupportBundleResponseSchema = z
  .object({
    _links: SupportBundleLinksSchema,
    id: z.string(),
    phase: z.string(),
  })
  .describe('SupportBundleResponse')
export type SupportBundleResponse = z.infer<typeof SupportBundleResponseSchema>

/**
 * Tier 3 extension of the platform's problem-JSON for the 409 already-running response. The
 * platform copies `submitter` from the in-cluster service's Job annotation so we can render it in
 * the UI.
 */
export const SupportBundleAlreadyRunningProblemResponseSchema = z
  .object({
    detail: z.string(),
    id: z.string(),
    phase: z.string(),
    submitter: z.string(),
    title: z.string(),
    type: z.literal('pixee:problems/api/support-bundles/already-running'),
  })
  .describe('SupportBundleAlreadyRunningProblemResponse')
export type SupportBundleAlreadyRunningProblemResponse = z.infer<
  typeof SupportBundleAlreadyRunningProblemResponseSchema
>

export const CapabilityDisabledProblemResponseSchema = z
  .object({
    detail: z.string(),
    status: z.number().int(),
    title: z.string(),
  })
  .describe('CapabilityDisabledProblemResponse')
export type CapabilityDisabledProblemResponse = z.infer<typeof CapabilityDisabledProblemResponseSchema>
true satisfies AreEquivalent<
  CapabilityDisabledProblemResponse,
  components['schemas']['CapabilityDisabledProblemResponse']
>

// #endregion

// #region API Root schemas (ISS-7005 — drives capability discovery)
//
// TODO(ISS-7598): the API root response and its _links (including the optional support-bundles link)
// should be defined in the pixee-platform OpenAPI spec.
// TODO(ISS-7599): once ISS-7598 lands, re-sync ApiRootResponse/ApiRootLinks against
// components['schemas'] and drop the .loose() escape hatch.

/**
 * `_links` on the root API response. `support-bundles` is present only when the chart's
 * supportBundleApi.enabled is true (and the in-cluster Service is rendered).
 */
const ApiRootLinksSchema = z
  .object({
    self: HALLinkSchema,
    'support-bundles': HALLinkSchema.optional(),
  })
  .loose()

export const ApiRootResponseSchema = z
  .object({
    _links: ApiRootLinksSchema,
  })
  .describe('ApiRootResponse')
export type ApiRootResponse = z.infer<typeof ApiRootResponseSchema>

export const PatchFixApplicationStatusResponseSchema = z.enum(['pending', 'applied', 'dropped'])
export type PatchFixApplicationStatusResponse = z.infer<typeof PatchFixApplicationStatusResponseSchema>
true satisfies AreEquivalent<
  PatchFixApplicationStatusResponse,
  components['schemas']['PatchFixApplicationStatusResponse']
>

export const PatchesPatchFixResponseSchema = z.object({
  _links: PatchesPatchFixLinksSchema,
  application_status: PatchFixApplicationStatusResponseSchema,
  merge_notes: z.string().optional(),
})
export type PatchesPatchFixResponse = z.infer<typeof PatchesPatchFixResponseSchema>
true satisfies AreEquivalent<PatchesPatchFixResponse, components['schemas']['PatchesPatchFixResponse']>

export const PageEmbeddedPatchFixResponseSchema = z.object({
  items: z.array(PatchesPatchFixResponseSchema),
})
export type PageEmbeddedPatchFixResponse = z.infer<typeof PageEmbeddedPatchFixResponseSchema>
true satisfies AreEquivalent<PageEmbeddedPatchFixResponse, components['schemas']['PageEmbeddedPatchesPatchFixResponse']>

export const PatchFixPaginatedResponseSchema = Paginated(
  PatchesPatchFixResponseSchema,
  StandardPageNavigationLinksSchema
).describe('PatchFixPaginatedResponse')
export type PatchFixPaginatedResponse = z.infer<typeof PatchFixPaginatedResponseSchema>
true satisfies AreEquivalent<PatchFixPaginatedResponse, components['schemas']['PatchFixPaginatedResponse']>

/**
 * How many ids one bulk membership request carries: the spec's `maxItems` on `repository_ids`. A
 * project's own limit is its `repository_capacity`, which the server reports per project.
 */
export const MAX_REPOSITORIES_PER_REQUEST = 100

export const AddProjectMembershipsRequestSchema = z.object({
  repository_ids: z.array(z.string().min(1)).min(1).max(MAX_REPOSITORIES_PER_REQUEST),
})
export type AddProjectMembershipsRequest = z.infer<typeof AddProjectMembershipsRequestSchema>
true satisfies AreEquivalent<AddProjectMembershipsRequest, components['schemas']['AddProjectMembershipsRequest']>

export const ProjectSchema = z.object({
  _embedded: ProjectEmbeddedSchema.optional(),
  _links: ProjectLinksSchema,
  _templates: ProjectTemplatesSchema,
  created_at: z.string(),
  description: z.string().optional(),
  id: z.string(),
  name: z.string(),
  repository_capacity: z.number().int(),
  repository_count: z.number().int(),
})
export type Project = z.infer<typeof ProjectSchema>
true satisfies AreEquivalent<Project, components['schemas']['Project']>

export const ProjectMembershipEmbeddedSchema = z.object({
  project: ProjectSchema,
})
export type ProjectMembershipEmbedded = z.infer<typeof ProjectMembershipEmbeddedSchema>
true satisfies AreEquivalent<ProjectMembershipEmbedded, components['schemas']['ProjectMembershipEmbedded']>

export const ProjectMembershipLinksSchema = z.object({
  project: HALLinkSchema,
  repository: HALLinkSchema,
  self: HALLinkSchema,
})
export type ProjectMembershipLinks = z.infer<typeof ProjectMembershipLinksSchema>
true satisfies AreEquivalent<ProjectMembershipLinks, components['schemas']['ProjectMembershipLinks']>

export const ProjectMembershipSchema = z
  .object({
    _embedded: ProjectMembershipEmbeddedSchema.optional(),
    _links: ProjectMembershipLinksSchema,
    _templates: ProjectMembershipTemplatesSchema,
    created_at: InstantSchema,
  })
  .describe('ProjectMembership')
export type ProjectMembership = z.infer<typeof ProjectMembershipSchema>
true satisfies AreEquivalent<ProjectMembership, components['schemas']['ProjectMembership']>

export const RepositoryEmbeddedSchema = z
  .object({
    'project-membership': ProjectMembershipSchema.optional(),
  })
  .describe('RepositoryEmbedded')
export type RepositoryEmbedded = z.infer<typeof RepositoryEmbeddedSchema>
true satisfies AreEquivalent<RepositoryEmbedded, components['schemas']['RepositoryEmbedded']>

export const PageEmbeddedProjectSchema = z.object({
  items: z.array(ProjectSchema),
})
export type PageEmbeddedProject = z.infer<typeof PageEmbeddedProjectSchema>
true satisfies AreEquivalent<PageEmbeddedProject, components['schemas']['PageEmbeddedProject']>

export const ProjectPaginatedResponseSchema = z
  .object({
    _embedded: PageEmbeddedProjectSchema,
    _links: StandardPageNavigationLinksSchema,
    _templates: ProjectCollectionTemplatesSchema,
    page: PageResponseSchema,
    total: z.number().int(),
  })
  .describe('ProjectPaginatedResponse')
export type ProjectPaginatedResponse = z.infer<typeof ProjectPaginatedResponseSchema>
true satisfies AreEquivalent<ProjectPaginatedResponse, components['schemas']['ProjectPaginatedResponse']>

export const PageEmbeddedProjectMembershipSchema = z.object({
  items: z.array(ProjectMembershipSchema),
})
export type PageEmbeddedProjectMembership = z.infer<typeof PageEmbeddedProjectMembershipSchema>
true satisfies AreEquivalent<PageEmbeddedProjectMembership, components['schemas']['PageEmbeddedProjectMembership']>

export const ProjectAnalysisServiceUnavailableProblemSchema = z
  .object({
    detail: z.string(),
    status: z.number().int(),
    title: z.string(),
  })
  .describe('ProjectAnalysisServiceUnavailableProblem')
export type ProjectAnalysisServiceUnavailableProblem = z.infer<typeof ProjectAnalysisServiceUnavailableProblemSchema>
true satisfies AreEquivalent<
  ProjectAnalysisServiceUnavailableProblem,
  components['schemas']['ProjectAnalysisServiceUnavailableProblem']
>

export const ProjectMembershipPaginatedResponseSchema = Paginated(
  ProjectMembershipSchema,
  StandardPageNavigationLinksSchema
)
  .extend({ _templates: ProjectMembershipCollectionTemplatesSchema })
  .describe('ProjectMembershipPaginatedResponse')
export type ProjectMembershipPaginatedResponse = z.infer<typeof ProjectMembershipPaginatedResponseSchema>
true satisfies AreEquivalent<
  ProjectMembershipPaginatedResponse,
  components['schemas']['ProjectMembershipPaginatedResponse']
>

export const PutProjectMembershipRequestSchema = z.object({
  project_id: z.string().min(1),
})
export type PutProjectMembershipRequest = z.infer<typeof PutProjectMembershipRequestSchema>
true satisfies AreEquivalent<PutProjectMembershipRequest, components['schemas']['PutProjectMembershipRequest']>

export const RepositoriesEmbedParameterSchema = z
  .enum(['items.project-membership', 'items.project-membership.project'])
  .describe('RepositoriesEmbedParameter')
export type RepositoriesEmbedParameter = z.infer<typeof RepositoriesEmbedParameterSchema>
true satisfies AreEquivalent<RepositoriesEmbedParameter, components['schemas']['RepositoriesEmbedParameter']>

export const RepositoryAnalysisServiceUnavailableProblemSchema = z
  .object({
    detail: z.string(),
    status: z.number().int(),
    title: z.string(),
  })
  .describe('RepositoryAnalysisServiceUnavailableProblem')
export type RepositoryAnalysisServiceUnavailableProblem = z.infer<
  typeof RepositoryAnalysisServiceUnavailableProblemSchema
>
true satisfies AreEquivalent<
  RepositoryAnalysisServiceUnavailableProblem,
  components['schemas']['RepositoryAnalysisServiceUnavailableProblem']
>

export const UnknownProjectProblemSchema = z
  .object({
    detail: z.string(),
    project_id: z.string(),
    status: z.number().int(),
    title: z.string(),
  })
  .describe('UnknownProjectProblem')
export type UnknownProjectProblem = z.infer<typeof UnknownProjectProblemSchema>
true satisfies AreEquivalent<UnknownProjectProblem, components['schemas']['UnknownProjectProblem']>

export const UpdateProjectRequestSchema = z.object({
  description: z.string().nullable().optional(),
  name: z.string().optional(),
})
export type UpdateProjectRequest = z.infer<typeof UpdateProjectRequestSchema>
true satisfies AreEquivalent<UpdateProjectRequest, components['schemas']['UpdateProjectRequest']>

// #endregion

// #region AI scan schemas

export const AiDetectionLocationSchema = z.object({
  file: z.string(),
  line: z.number().int(),
  snippet: z.string().optional(),
  snippet_start_line: z.number().int().optional(),
})
export type AiDetectionLocation = z.infer<typeof AiDetectionLocationSchema>
true satisfies AreEquivalent<AiDetectionLocation, components['schemas']['AiDetectionLocation']>

export const AiDetectionDetailResponseSchema = z.object({
  confidence: z.number().optional(),
  description: z.string().optional(),
  location: AiDetectionLocationSchema.optional(),
  matched_sink: z.string().optional(),
})
export type AiDetectionDetailResponse = z.infer<typeof AiDetectionDetailResponseSchema>
true satisfies AreEquivalent<AiDetectionDetailResponse, components['schemas']['AiDetectionDetailResponse']>

export const NotFoundProblemResponseSchema = z
  .object({
    detail: z.string(),
    status: z.number().int(),
    title: z.string(),
  })
  .describe('NotFoundProblemResponse')
export type NotFoundProblemResponse = z.infer<typeof NotFoundProblemResponseSchema>
true satisfies AreEquivalent<NotFoundProblemResponse, components['schemas']['NotFoundProblemResponse']>

export const OpenGrepRulesLinksSchema = z
  .object({
    content: HALLinkSchema.optional(),
    events: HALLinkSchema.optional(),
    self: HALLinkSchema,
  })
  .describe('OpenGrepRulesLinks')
export type OpenGrepRulesLinks = z.infer<typeof OpenGrepRulesLinksSchema>
true satisfies AreEquivalent<OpenGrepRulesLinks, components['schemas']['OpenGrepRulesLinks']>

export const OpenGrepRulesNoThreatModelProblemSchema = z
  .object({
    detail: z.string(),
    status: z.number().int(),
    title: z.string(),
  })
  .describe('OpenGrepRulesNoThreatModelProblem')
export type OpenGrepRulesNoThreatModelProblem = z.infer<typeof OpenGrepRulesNoThreatModelProblemSchema>
true satisfies AreEquivalent<
  OpenGrepRulesNoThreatModelProblem,
  components['schemas']['OpenGrepRulesNoThreatModelProblem']
>

export const OpenGrepRulesStatusSchema = z
  .enum(['completed', 'failed', 'processing', 'unknown'])
  .describe('OpenGrepRulesStatus')
export type OpenGrepRulesStatus = z.infer<typeof OpenGrepRulesStatusSchema>
true satisfies AreEquivalent<OpenGrepRulesStatus, components['schemas']['OpenGrepRulesStatus']>

export const OpenGrepRulesResponseSchema = z
  .object({
    _links: OpenGrepRulesLinksSchema,
    failureReason: z.string().optional(),
    generatedAt: InstantSchema.optional(),
    phase: z.string(),
    requestedBy: z.string().optional(),
    status: OpenGrepRulesStatusSchema,
  })
  .describe('OpenGrepRulesResponse')
export type OpenGrepRulesResponse = z.infer<typeof OpenGrepRulesResponseSchema>
true satisfies AreEquivalent<OpenGrepRulesResponse, components['schemas']['OpenGrepRulesResponse']>

export const OpenGrepRulesUnavailableProblemSchema = z
  .object({
    detail: z.string(),
    status: z.number().int(),
    title: z.string(),
  })
  .describe('OpenGrepRulesUnavailableProblem')
export type OpenGrepRulesUnavailableProblem = z.infer<typeof OpenGrepRulesUnavailableProblemSchema>
true satisfies AreEquivalent<OpenGrepRulesUnavailableProblem, components['schemas']['OpenGrepRulesUnavailableProblem']>

// #endregion

// #region Pixee Beacon

/** How much of the token budget a Beacon run may spend. The platform maps each level to a budget. */
export const BeaconEffortSchema = z.enum(['low', 'medium', 'high']).describe('AiScanEffort')
export type BeaconEffort = z.infer<typeof BeaconEffortSchema>
true satisfies AreEquivalent<BeaconEffort, components['schemas']['AiScanEffort']>

export const BeaconScanRequestSchema = z.object({ effort: BeaconEffortSchema.optional() }).describe('AiScanRequest')
export type BeaconScanRequest = z.infer<typeof BeaconScanRequestSchema>
true satisfies AreEquivalent<BeaconScanRequest, components['schemas']['AiScanRequest']>

/**
 * Where a run is in the platform's lifecycle. A run waits `queued` for analysis-service capacity, is
 * `dispatching` while the platform hands it over, and is `in-progress` once analysis-service accepted it.
 */
export const AiScanRunStateSchema = z
  .enum(['queued', 'dispatching', 'in-progress', 'completed', 'failed', 'cancelled'])
  .describe('AiScanRunState')
export type AiScanRunState = z.infer<typeof AiScanRunStateSchema>
true satisfies AreEquivalent<AiScanRunState, components['schemas']['AiScanRunState']>

/** The analysis-service scan status a run is in; `cancelled` is terminal alongside completed and failed. */
export const BeaconPhaseSchema = z
  .enum(['queued', 'fetching', 'generating', 'triaging', 'completed', 'failed', 'cancelled'])
  .describe('AiScanPhase')
export type BeaconPhase = z.infer<typeof BeaconPhaseSchema>
true satisfies AreEquivalent<BeaconPhase, components['schemas']['AiScanPhase']>

export const BeaconStageSchema = z
  .enum(['threat_model', 'surface_map', 'investigate', 'verify'])
  .describe('AiScanStage')
export type BeaconStage = z.infer<typeof BeaconStageSchema>
true satisfies AreEquivalent<BeaconStage, components['schemas']['AiScanStage']>

/** How far a run has worked through one kind of work; `total` is absent until analysis-service knows it. */
export const BeaconCountSchema = z
  .object({ done: z.number().int(), total: z.number().int().optional() })
  .describe('AiScanCountResponse')
export type BeaconCount = z.infer<typeof BeaconCountSchema>
true satisfies AreEquivalent<BeaconCount, components['schemas']['AiScanCountResponse']>

/** What analysis-service last reported about a run, so a list can show "x of 6 lenses" without its stream. */
export const BeaconProgressSchema = z
  .object({
    examined_percent: z.number().optional(),
    findings_kept: z.number().int().optional(),
    investigations: BeaconCountSchema.optional(),
    lenses: BeaconCountSchema.optional(),
    phase: BeaconPhaseSchema,
    // A platform newer than this build may report a stage it does not know; read it as absent, as the
    // platform itself does for a stage analysis-service reports that the platform does not know.
    stage: BeaconStageSchema.optional().catch(undefined),
    token_budget: z.number().int().optional(),
    verification: BeaconCountSchema.optional(),
  })
  .describe('AiScanProgressResponse')
export type BeaconProgress = z.infer<typeof BeaconProgressSchema>
true satisfies AreEquivalent<BeaconProgress, components['schemas']['AiScanProgressResponse']>

export const BeaconRunLinksSchema = z
  .object({
    coverage: HALLinkSchema.optional(),
    events: HALLinkSchema,
    repository: HALLinkSchema,
    scan: HALLinkSchema.optional(),
    self: HALLinkSchema,
  })
  .describe('AiScanRunLinks')
export type BeaconRunLinks = z.infer<typeof BeaconRunLinksSchema>
true satisfies AreEquivalent<BeaconRunLinks, components['schemas']['AiScanRunLinks']>

export const BeaconRunEmbeddedSchema = z.object({ repository: RepositorySchema }).describe('AiScanRunEmbedded')
export type BeaconRunEmbedded = z.infer<typeof BeaconRunEmbeddedSchema>
true satisfies AreEquivalent<BeaconRunEmbedded, components['schemas']['AiScanRunEmbedded']>

const BeaconRunBaseShape = {
  _embedded: BeaconRunEmbeddedSchema.optional(),
  _links: BeaconRunLinksSchema,
  branch: z.string().optional(),
  effort: BeaconEffortSchema,
  id: z.string(),
  queued_at: InstantSchema,
  requested_by: z.string().optional(),
  state_changed_at: InstantSchema,
}

export const QueuedBeaconRunSchema = z
  .object({ ...BeaconRunBaseShape, retry_after: InstantSchema.optional(), state: z.literal('queued') })
  .describe('QueuedAiScanRun')
export type QueuedBeaconRun = z.infer<typeof QueuedBeaconRunSchema>
true satisfies AreEquivalent<QueuedBeaconRun, components['schemas']['QueuedAiScanRun']>

export const DispatchingBeaconRunSchema = z
  .object({ ...BeaconRunBaseShape, state: z.literal('dispatching') })
  .describe('DispatchingAiScanRun')
export type DispatchingBeaconRun = z.infer<typeof DispatchingBeaconRunSchema>
true satisfies AreEquivalent<DispatchingBeaconRun, components['schemas']['DispatchingAiScanRun']>

export const InProgressBeaconRunSchema = z
  .object({
    ...BeaconRunBaseShape,
    progress: BeaconProgressSchema.optional(),
    sha: z.string(),
    state: z.literal('in-progress'),
  })
  .describe('InProgressAiScanRun')
export type InProgressBeaconRun = z.infer<typeof InProgressBeaconRunSchema>
true satisfies AreEquivalent<InProgressBeaconRun, components['schemas']['InProgressAiScanRun']>

export const CompletedBeaconRunSchema = z
  .object({
    ...BeaconRunBaseShape,
    findings_count: z.number().int(),
    progress: BeaconProgressSchema.optional(),
    sha: z.string(),
    state: z.literal('completed'),
  })
  .describe('CompletedAiScanRun')
export type CompletedBeaconRun = z.infer<typeof CompletedBeaconRunSchema>
true satisfies AreEquivalent<CompletedBeaconRun, components['schemas']['CompletedAiScanRun']>

export const FailedBeaconRunSchema = z
  .object({
    ...BeaconRunBaseShape,
    failure_reason: z.string(),
    progress: BeaconProgressSchema.optional(),
    sha: z.string().optional(),
    state: z.literal('failed'),
  })
  .describe('FailedAiScanRun')
export type FailedBeaconRun = z.infer<typeof FailedBeaconRunSchema>
true satisfies AreEquivalent<FailedBeaconRun, components['schemas']['FailedAiScanRun']>

export const CancelledBeaconRunSchema = z
  .object({
    ...BeaconRunBaseShape,
    progress: BeaconProgressSchema.optional(),
    sha: z.string().optional(),
    state: z.literal('cancelled'),
  })
  .describe('CancelledAiScanRun')
export type CancelledBeaconRun = z.infer<typeof CancelledBeaconRunSchema>
true satisfies AreEquivalent<CancelledBeaconRun, components['schemas']['CancelledAiScanRun']>

/** One Beacon run, in the shape its `state` allows: its references are in `_links`, its counts in `progress`. */
export const BeaconRunSchema = z
  .discriminatedUnion('state', [
    QueuedBeaconRunSchema,
    DispatchingBeaconRunSchema,
    InProgressBeaconRunSchema,
    CompletedBeaconRunSchema,
    FailedBeaconRunSchema,
    CancelledBeaconRunSchema,
  ])
  .describe('AiScanRunResponse')
export type BeaconRun = z.infer<typeof BeaconRunSchema>
true satisfies AreEquivalent<BeaconRun, components['schemas']['AiScanRunResponse']>

export const BeaconRunListSchema = z.object({ items: z.array(BeaconRunSchema) }).describe('AiScanRunList')
export type BeaconRunList = z.infer<typeof BeaconRunListSchema>
true satisfies AreEquivalent<BeaconRunList, components['schemas']['AiScanRunList']>

/** A cancellation arrived after the run had completed or failed; a cancelled run is cancelled again with a 200. */
export const AiScanAlreadyFinishedProblemSchema = z
  .object({ detail: z.string(), status: z.number().int(), title: z.string() })
  .describe('AiScanAlreadyFinishedProblem')
export type AiScanAlreadyFinishedProblem = z.infer<typeof AiScanAlreadyFinishedProblemSchema>
true satisfies AreEquivalent<AiScanAlreadyFinishedProblem, components['schemas']['AiScanAlreadyFinishedProblem']>

/** A run's coverage was asked for before the run completed. */
export const AiScanNotReadyProblemSchema = z
  .object({ detail: z.string(), state: AiScanRunStateSchema, status: z.number().int(), title: z.string() })
  .describe('AiScanNotReadyProblem')
export type AiScanNotReadyProblem = z.infer<typeof AiScanNotReadyProblemSchema>
true satisfies AreEquivalent<AiScanNotReadyProblem, components['schemas']['AiScanNotReadyProblem']>

// The coverage endpoint relays analysis-service's `ScanSummary` untyped (`application/json: {}`), so the
// schemas below mirror analysis-service-api.yaml instead of the platform spec; see unmapped-schemas.md.

export const CoverageStatusSchema = z
  .enum(['examined', 'partly', 'not_examined', 'not_applicable'])
  .describe('CoverageStatus')
export type CoverageStatus = z.infer<typeof CoverageStatusSchema>

const TokensByLensSchema = z.record(z.string(), z.number())

const CoverageEffortShape = {
  /** Set when the figures are an operation's share of the investigation that examined it. */
  approximate: z.boolean().optional(),
  files_read: z.number(),
  tokens_by_lens: TokensByLensSchema,
  tokens_spent: z.number(),
}

export const EntityCoverageSchema = z
  .object({
    findings: z.array(z.string()),
    id: z.string(),
    kind: z.string(),
    lenses_run: z.array(z.string()),
    name: z.string(),
    reason: z.string(),
    status: CoverageStatusSchema,
    ...CoverageEffortShape,
  })
  .describe('EntityCoverage')
export type EntityCoverage = z.infer<typeof EntityCoverageSchema>

export const OperationCoverageSchema = EntityCoverageSchema.extend({
  caller: z.string(),
  effect: z.string(),
  group: z.string(),
  location: z.string(),
}).describe('OperationCoverage')
export type OperationCoverage = z.infer<typeof OperationCoverageSchema>

export const OperationGroupCoverageSchema = z
  .object({
    findings: z.array(z.string()),
    id: z.string(),
    name: z.string(),
    operation_ids: z.array(z.string()),
    status: CoverageStatusSchema,
    ...CoverageEffortShape,
  })
  .describe('OperationGroupCoverage')
export type OperationGroupCoverage = z.infer<typeof OperationGroupCoverageSchema>

export const EntryPointCoverageSchema = EntityCoverageSchema.extend({
  groups: z.array(OperationGroupCoverageSchema),
  operations: z.array(OperationCoverageSchema),
}).describe('EntryPointCoverage')
export type EntryPointCoverage = z.infer<typeof EntryPointCoverageSchema>

export const AssetCoverageSchema = EntityCoverageSchema.extend({
  operation_ids: z.array(z.string()).optional(),
}).describe('AssetCoverage')
export type AssetCoverage = z.infer<typeof AssetCoverageSchema>

export const ControlCoverageSchema = EntityCoverageSchema.extend({
  applies_to: z.array(z.string()),
  auditable: z.boolean(),
}).describe('ControlCoverage')
export type ControlCoverage = z.infer<typeof ControlCoverageSchema>

export const LensBudgetSchema = z
  .object({
    allotted: z.number(),
    lens: z.string(),
    planned: z.number().int(),
    run: z.number().int(),
    skipped: z.number().int(),
    spent: z.number(),
  })
  .describe('LensBudget')
export type LensBudget = z.infer<typeof LensBudgetSchema>

export const CoverageAlarmCauseSchema = z
  .enum(['budget', 'cap', 'not_supported', 'not_scannable', 'error'])
  .describe('CoverageAlarmCause')
export type CoverageAlarmCause = z.infer<typeof CoverageAlarmCauseSchema>

export const CoverageAlarmRemedySchema = z.enum(['raise_effort', 'rescan', 'none']).describe('CoverageAlarmRemedy')
export type CoverageAlarmRemedy = z.infer<typeof CoverageAlarmRemedySchema>

// Summaries stored before alarms named their cause carry only `kind`, `sentence` and `severity`; a cause
// or remedy added upstream later reads as absent rather than failing the whole summary.
export const CoverageAlarmSchema = z
  .object({
    cause: CoverageAlarmCauseSchema.optional().catch(undefined),
    count: z.number().int().optional(),
    items: z.array(z.string()).optional(),
    kind: z.string(),
    remedy: CoverageAlarmRemedySchema.optional().catch(undefined),
    sentence: z.string(),
    severity: z.enum(['high', 'medium', 'low']),
  })
  .describe('CoverageAlarm')
export type CoverageAlarm = z.infer<typeof CoverageAlarmSchema>

export const CoverageVerdictLevelSchema = z.enum(['on', 'degraded', 'off']).describe('CoverageVerdictLevel')
export type CoverageVerdictLevel = z.infer<typeof CoverageVerdictLevelSchema>

export const CoverageVerdictSchema = z
  .object({ headline: z.string().optional(), level: CoverageVerdictLevelSchema, sentence: z.string() })
  .describe('CoverageVerdict')
export type CoverageVerdict = z.infer<typeof CoverageVerdictSchema>

export const CoverageProofSchema = z
  .object({
    alarms: z.array(CoverageAlarmSchema),
    candidates_deduped: z.number().int(),
    candidates_raw: z.number().int(),
    files_read: z.number().int(),
    files_read_sample: z.array(z.string()),
    investigations_planned: z.number().int(),
    investigations_run: z.number().int(),
    lenses: z.array(LensBudgetSchema),
    operations_mapped: z.number().int(),
    sinks_inventoried: z.number().int(),
    token_budget: z.number(),
    tokens_spent: z.number(),
    verdict: CoverageVerdictSchema,
  })
  .describe('ProofPanel')
export type CoverageProof = z.infer<typeof CoverageProofSchema>

export const CoverageReportSchema = z
  .object({
    assets: z.array(AssetCoverageSchema),
    controls: z.array(ControlCoverageSchema),
    entry_points: z.array(EntryPointCoverageSchema),
    examined_percent: z.number(),
    proof: CoverageProofSchema,
    repository: EntityCoverageSchema,
    sinks: z.array(EntityCoverageSchema),
  })
  .describe('CoverageReport')
export type CoverageReport = z.infer<typeof CoverageReportSchema>

export const BeaconEntityKindSchema = z
  .enum(['operation', 'group', 'entry_point', 'control', 'repository'])
  .describe('EntityKind')
export type BeaconEntityKind = z.infer<typeof BeaconEntityKindSchema>

export const FindingPlacementSchema = z
  .object({ entity_id: z.string(), entity_kind: BeaconEntityKindSchema, finding_uuid: z.string() })
  .describe('FindingPlacement')
export type FindingPlacement = z.infer<typeof FindingPlacementSchema>

/** Which stage dismissed a lead: the verifier refuted it, or triage judged it a false positive. */
export const DismissedBySchema = z.enum(['verifier', 'triage']).describe('VerdictJudge')
export type DismissedBy = z.infer<typeof DismissedBySchema>

/** A candidate Beacon dismissed, where it would have been placed had it been kept, and why. */
export const DismissedLeadSchema = z
  .object({
    cwe_ids: z.array(z.string()),
    /** The dismissal's full report, in markdown. */
    details: z.string(),
    dismissed_by: DismissedBySchema,
    entity_id: z.string(),
    entity_kind: BeaconEntityKindSchema,
    explanation: z.string(),
    file_path: z.string(),
    finding_uuid: z.string(),
    lens: z.string(),
    line_end: z.number().int(),
    line_start: z.number().int(),
    title: z.string(),
    weakness: z.string(),
  })
  .describe('DismissedLead')
export type DismissedLead = z.infer<typeof DismissedLeadSchema>

export const BeaconSummarySchema = z
  .object({
    completed_at: InstantSchema,
    counts: z.object({
      candidates_deduped: z.number().int(),
      candidates_raw: z.number().int(),
      entry_points: z.number().int(),
      /**
       * Leads Beacon dismissed. Equals `dismissed.length`, except in a summary stored before Beacon listed each
       * dismissed lead, where `dismissed` is empty.
       */
      false_positive: z.number().int(),
      findings_kept: z.number().int(),
      investigations: z.number().int(),
      operations: z.number().int(),
      true_positive: z.number().int(),
      uncertain: z.number().int(),
    }),
    coverage: CoverageReportSchema,
    /** Token quartiles over examined operations; the coverage map shades depth 1–4 by them. */
    depth_thresholds: z.tuple([z.number(), z.number(), z.number()]),
    dismissed: z.array(DismissedLeadSchema).default([]),
    effort: BeaconEffortSchema,
    finding_placements: z.array(FindingPlacementSchema),
    stages: z.array(z.object({ finished_at: InstantSchema, stage: BeaconStageSchema, started_at: InstantSchema })),
    started_at: InstantSchema,
    token_budget: z.number(),
    tokens_spent: z.number(),
  })
  .describe('ScanSummary')
export type BeaconSummary = z.infer<typeof BeaconSummarySchema>

// #endregion

export const FreeformUploadResponseSchema = z.object({
  id: z.string(),
  status: z.string(),
})
export type FreeformUploadResponse = z.infer<typeof FreeformUploadResponseSchema>
true satisfies AreEquivalent<FreeformUploadResponse, components['schemas']['FreeformUploadResponse']>

export const FreeformUploadSessionStateSchema = z
  .enum(['associating', 'completed', 'failed', 'parsing'])
  .describe('FreeformUploadSessionState')
export type FreeformUploadSessionState = z.infer<typeof FreeformUploadSessionStateSchema>
true satisfies AreEquivalent<FreeformUploadSessionState, components['schemas']['FreeformUploadSessionState']>

export const FreeformUploadFileStateSchema = z
  .enum(['parsed', 'pending', 'unextracted'])
  .describe('FreeformUploadFileState')
export type FreeformUploadFileState = z.infer<typeof FreeformUploadFileStateSchema>
true satisfies AreEquivalent<FreeformUploadFileState, components['schemas']['FreeformUploadFileState']>

export const FreeformUploadSessionLinksSchema = z
  .object({ events: HALLinkSchema, self: HALLinkSchema })
  .describe('FreeformUploadSessionLinks')
export type FreeformUploadSessionLinks = z.infer<typeof FreeformUploadSessionLinksSchema>
true satisfies AreEquivalent<FreeformUploadSessionLinks, components['schemas']['FreeformUploadSessionLinks']>

export const FreeformUploadSessionFileSchema = z
  .object({ filename: z.string(), parse_status: FreeformUploadFileStateSchema })
  .describe('FreeformUploadSessionFile')
export type FreeformUploadSessionFile = z.infer<typeof FreeformUploadSessionFileSchema>
true satisfies AreEquivalent<FreeformUploadSessionFile, components['schemas']['FreeformUploadSessionFile']>

export const FreeformUploadSessionFindingSchema = z
  .object({ finding_id: z.string(), path: z.string(), severity: z.string().optional(), title: z.string().optional() })
  .describe('FreeformUploadSessionFinding')
export type FreeformUploadSessionFinding = z.infer<typeof FreeformUploadSessionFindingSchema>
true satisfies AreEquivalent<FreeformUploadSessionFinding, components['schemas']['FreeformUploadSessionFinding']>

export const FreeformUploadSessionScanLinksSchema = z
  .object({ scan: HALLinkSchema, self: HALLinkSchema })
  .describe('FreeformUploadSessionScanLinks')
export type FreeformUploadSessionScanLinks = z.infer<typeof FreeformUploadSessionScanLinksSchema>
true satisfies AreEquivalent<FreeformUploadSessionScanLinks, components['schemas']['FreeformUploadSessionScanLinks']>

export const FreeformUploadSessionScanSchema = z
  .object({
    _links: FreeformUploadSessionScanLinksSchema,
    finding_count: z.number().int(),
    findings: z.array(FreeformUploadSessionFindingSchema),
    repo_full_name: z.string(),
    scan_id: z.string(),
  })
  .describe('FreeformUploadSessionScan')
export type FreeformUploadSessionScan = z.infer<typeof FreeformUploadSessionScanSchema>
true satisfies AreEquivalent<FreeformUploadSessionScan, components['schemas']['FreeformUploadSessionScan']>

export const FreeformUploadSessionUnassociatedSchema = z
  .object({ cwe: z.string().optional(), rationale: z.string(), reason: z.string(), title: z.string() })
  .describe('FreeformUploadSessionUnassociated')
export type FreeformUploadSessionUnassociated = z.infer<typeof FreeformUploadSessionUnassociatedSchema>
true satisfies AreEquivalent<
  FreeformUploadSessionUnassociated,
  components['schemas']['FreeformUploadSessionUnassociated']
>

/** One Findings Intake run: the files it read, the scans it created and the findings it could not place. */
export const FreeformUploadSessionResponseSchema = z
  .object({
    _links: FreeformUploadSessionLinksSchema,
    created_at: InstantSchema,
    created_by: z.string().optional(),
    failure_reason: z.string().optional(),
    files: z.array(FreeformUploadSessionFileSchema),
    id: z.string(),
    scans: z.array(FreeformUploadSessionScanSchema),
    status: FreeformUploadSessionStateSchema,
    unassociated: z.array(FreeformUploadSessionUnassociatedSchema),
    updated_at: InstantSchema,
  })
  .describe('FreeformUploadSessionResponse')
export type FreeformUploadSessionResponse = z.infer<typeof FreeformUploadSessionResponseSchema>
true satisfies AreEquivalent<FreeformUploadSessionResponse, components['schemas']['FreeformUploadSessionResponse']>

export const FreeformUploadSessionSummaryResponseSchema = z
  .object({
    _links: FreeformUploadSessionLinksSchema,
    created_at: InstantSchema,
    created_by: z.string().optional(),
    failure_reason: z.string().optional(),
    filenames: z.array(z.string()),
    finding_count: z.number().int(),
    id: z.string(),
    scan_count: z.number().int(),
    status: FreeformUploadSessionStateSchema,
    unassociated_count: z.number().int(),
    updated_at: InstantSchema,
  })
  .describe('FreeformUploadSessionSummaryResponse')
export type FreeformUploadSessionSummaryResponse = z.infer<typeof FreeformUploadSessionSummaryResponseSchema>
true satisfies AreEquivalent<
  FreeformUploadSessionSummaryResponse,
  components['schemas']['FreeformUploadSessionSummaryResponse']
>

export const FreeformUploadSessionPaginatedResponseSchema = Paginated(
  FreeformUploadSessionSummaryResponseSchema,
  StandardPageNavigationLinksSchema
).describe('FreeformUploadSessionPaginatedResponse')
export type FreeformUploadSessionPaginatedResponse = z.infer<typeof FreeformUploadSessionPaginatedResponseSchema>
true satisfies AreEquivalent<
  FreeformUploadSessionPaginatedResponse,
  components['schemas']['FreeformUploadSessionPaginatedResponse']
>

export const LabsResponseSchema = z.object({
  enabled: z.boolean(),
})
export type LabsResponse = z.infer<typeof LabsResponseSchema>
true satisfies AreEquivalent<LabsResponse, components['schemas']['LabsResponse']>

export const BranchEmbedParameterSchema = z.enum(['latest-scan'])
export type BranchEmbedParameter = z.infer<typeof BranchEmbedParameterSchema>
true satisfies AreEquivalent<BranchEmbedParameter, components['schemas']['BranchEmbedParameter']>

export const BranchResponseSchema = z.object({
  _embedded: BranchEmbeddedSchema.optional(),
  _links: BranchLinksSchema,
  is_default: z.boolean(),
  name: z.string(),
})
export type BranchResponse = z.infer<typeof BranchResponseSchema>
true satisfies AreEquivalent<BranchResponse, components['schemas']['BranchResponse']>

export const PageEmbeddedBranchResponseSchema = z.object({
  items: z.array(BranchResponseSchema),
})
export type PageEmbeddedBranchResponse = z.infer<typeof PageEmbeddedBranchResponseSchema>
true satisfies AreEquivalent<PageEmbeddedBranchResponse, components['schemas']['PageEmbeddedBranchResponse']>

export const BranchPaginatedResponseSchema = z
  .object({
    _embedded: PageEmbeddedBranchResponseSchema,
    _links: StandardPageNavigationLinksSchema,
    page: PageResponseSchema,
    total: z.number().int(),
  })
  .describe('BranchPaginatedResponse')
export type BranchPaginatedResponse = z.infer<typeof BranchPaginatedResponseSchema>
true satisfies AreEquivalent<BranchPaginatedResponse, components['schemas']['BranchPaginatedResponse']>
